import { v4 as uuidv4 } from 'uuid';
import {
  SEARCH_PAGE_SIZE,
  mcpCompositeRoute,
  mcpRequestLimit,
  assertMcpPreviewLimit,
  adaptMcpPeopleFilter,
  type McpSearchContext,
  type McpSearchRestoration,
} from './mcp-search-handoff';

import {
  programEmployerCandidates,
  programPersonImportRaw,
} from './startup-programs/startup-program-employers.utils';
import type {
  StartupProgramFilter,
  StartupProgramCoverage,
} from './startup-programs/startup-programs.types';
import {
  collectProgramPages,
  ProgramSearchError,
  startupProgramErrorMessage,
  hasStartupPrograms,
  startupProgramRequest,
} from './startup-programs/startup-programs.utils';
import {
  useConnectedSyncCrms,
  useCrmConnectionsReady,
} from '@/components/crm-sync-status/use-crm-sync-status.hook';
import type { ActiveFilter } from '@/components/search/search-shell.component';
import {
  expandCountrySelectionToDisplayNames,
  expandCountrySelectionToFullNames,
} from '@/constants/country-groups';
import { STOCK_LISTING_OPTIONS } from '@/constants/crustdata-stock-listings';
import { OPEN_TO_CARD_OPTIONS } from '@/constants/open-to-card-types';
import { expandIndustrySelection } from '@/constants/industry-groups';
import { naicsLabel } from '@/constants/naics';
import { useCrmFieldLabels } from '@/hooks/use-crm-chip-labels.hook';
import { useCreditGate } from '@/hooks/use-credit-gate.hook';
import {
  paidAccessNotice,
  requestPaidAccess,
} from '@/containers/settings/billing/paid-access';
import { requestErrorMessage } from '@/utils/request-error-message.utils';
import {
  errorDetailMessage,
  searchFailureCopy,
} from '@/utils/search-error-copy.util';
import type { SearchQuota } from '@/hooks/use-search-quota.hook';
import {
  isEnrichmentMessage,
  useEnrichmentWebSocket,
} from '@/hooks/use-unified-websocket.hook';
import { useOptionalWebSocketContext } from '@/context/websocket-context';
import { useAppDispatch } from '@/store';
import {
  addOperation,
  removeOperation,
  setStatus as setOperationStatus,
  updateProgress as updateOperationProgress,
} from '@/store/features/active-operations.feature';
import {
  useCompaniesFindStartMutation,
  useLazyGetCompaniesFindStatusQuery,
  usePeopleAtCompaniesImportFetchMutation,
  type CompaniesFindStatusResponse,
  type CompositePeopleSearchRequest,
} from '@/store/features/company-search.feature';
import {
  useGetCrmUsersQuery,
  useGetExclusionSetMutation,
  type CrmExclusionCondition,
  type CrmExclusionCrm,
  type CrmExclusionReference,
  type CrmExclusionReason,
  type CrmExclusionSetResponse,
} from '@/store/features/crm.feature';
import {
  useEnrichProspectManuallyMutation,
  useLazyGetEnrichmentTaskStatusQuery,
} from '@/store/features/enrichment.feature';
import {
  invalidateLeadListLeadsCache,
  leadListsApi,
  useGetLeadListsQuery,
} from '@/store/features/lead-lists.feature';
import type {
  CrustdataFilterGroup,
  IdentityExclusionKind,
  ImportPeopleResponse,
  PeopleIdentityExclusions,
  PersonImportOutcome,
  PersonRecord,
} from '@/store/features/prospects.feature';
import {
  useImportPeopleMutation,
  useLazyGetPeopleImportStatusQuery,
  useLazyGetPeopleSearchStatusQuery,
  usePeopleImportFetchMutation,
  usePeopleSearchMutation,
  type CompanyCoverage,
  type PeopleSearchRequest,
  type PeopleSearchResponse,
} from '@/store/features/prospects.feature';
import {
  INITIAL_FILTERS,
  type CrmFilterClause,
  type CrmFilterCrm,
  type SearchFilters,
} from '@/types/search-filters';
import {
  booleanQueryToCrustdataFilter,
  parseBooleanQueryOrCompileError,
} from '@/utils/boolean-query.util';
import { normalizeCalendarDate } from '@/utils/calendar-date.util';
import {
  FilterCompileError,
  filterCompileErrorMessage,
} from '@/utils/filter-compile-error.util';
import {
  INSTANTLY_STATUS_LABELS,
  buildInstantlyExclusionReference,
  hasDynamicPeopleExclusions,
  hasPeopleExclusions,
  crmFieldRefusal,
  requireCompleteCrmExclusions,
  validatePeopleExclusions,
} from './people-exclusion-validation';
import { normalizeDomainList } from '@/lib/normalize-domain';
import {
  asTypedDomain,
  foldPeopleCompanyScalar,
} from '@/utils/company-identity';
import { formatResetTime, rateLimitResetAt } from '@/lib/rate-limit';
import {
  buildCleanlistExclusionRequest,
  hasCleanlistExclusion,
  mergeCleanlistLinkedInUrls,
} from '@/utils/cleanlist-exclusions.util';
import {
  hasAnyCompanyFilter,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';
import {
  detectCompositeRoute,
  emptyCompanyFilters,
} from '@/utils/composite-search-router';
import {
  buildCrmExcludeConditions,
  emptyCrmIdentifiers,
  expandLinkedInExcludeVariants,
  getActiveCrmExclusions,
  mergeCrmIdentifiers,
} from '@/utils/crm-exclusions.util';
import { FIELD_PREFIX } from '@/utils/crm-section-clauses.util';
import {
  buildCrmFilterChips,
  type CrmFilterChipContext,
} from '@/utils/crm-filter-chips.util';
import { migrateSearchFilters } from '@/utils/legacy-search-migration';
import {
  addContainsFilter,
  addEnumFilter,
  addFuzzyFilter,
  addRangeFilter,
  addStructuredLocationFilter,
  expandJobTitleTerms,
  findBestMatchingEmployer,
  normalizeCompanyLinkedInUrls,
  normalizePersonLinkedInUrls,
  wrapFilters,
  PERSON_LOCATION_COLUMNS,
  type EmployerMatchCriteria,
} from '@/utils/people-search.utils';
import {
  trackPeopleImportCompleted,
  trackSearchCompleted,
  trackSearchCompositeRouted,
  trackSearchErrorShown,
  trackSearchExecuted,
} from '@/utils/search-analytics';
import {
  clearAllCachedResults,
  clearSearchState,
  getCachedResults,
  loadSearchState,
  patchSearchStateRow,
  persistSearch,
  restoredSearchPage,
  saveSearchPage,
} from '@/utils/search-cache';
import { withOrderedRanges } from '@/utils/search-filter-input.util';
import { validatePersonRecords } from '@/utils/search-schemas';
import { usePostHog } from 'posthog-js/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import {
  isOptedOutFailure,
  OPTED_OUT_MESSAGE,
  OPTED_OUT_REVEAL,
} from '@/utils/opted-out';
import { toastIfOptedOut } from '@/utils/opted-out-toast';
import { BRAND } from '@/lib/brand';

// ─── Types ───────────────────────────────────────────────────────────
// SearchFilters + INITIAL_FILTERS live in src/types/search-filters.ts
// so utilities (legacy-search-migration, composite-search-router) can
// import them without circling back through this hook's RTK Query
// graph. Re-exported here for backward compat with callsites that
// already pull them from this module.

export { INITIAL_FILTERS, type SearchFilters } from '@/types/search-filters';

// ─── CRM exclusion freshness / degradation surface ──────────────────
//
// Per-search summary of how the CRM exclusion sets resolved, so the
// sidebar can show freshness, incomplete results, errors, and a refresh
// affordance. An incomplete selected exclusion blocks search and import.
export interface CrmExclusionStatus {
  /** Most-recent ``fetched_at`` across the resolved conditions, or null
   *  if none reported one. Drives "based on your CRM · updated …". */
  fetchedAt: string | null;
  /** Any resolved set was capped server-side. */
  truncated: boolean;
  /** Any resolved set was a partial / graceful degradation. */
  partial: boolean;
  /** Any resolved set was past its freshness window. */
  stale: boolean;
  /** First graceful-degradation reason encountered, if any. */
  reason: CrmExclusionReason | null;
  /** Total CRM records the exclusion set spanned (sum across conditions). */
  recordCount: number;
  /** Whether a CRM exclusion fetch is currently in flight. */
  isFetching: boolean;
  /** Filter keys or clause IDs whose last resolution failed. */
  erroredKeys: string[];
}

const EMPTY_CRM_EXCLUSION_STATUS: CrmExclusionStatus = {
  fetchedAt: null,
  truncated: false,
  partial: false,
  stale: false,
  reason: null,
  recordCount: 0,
  isFetching: false,
  erroredKeys: [],
};

// ─── Cleanlist-dedup exclusion freshness surface (CP-8) ─────────────
//
// Per-search summary of how the Cleanlist exclusion-identifier resolve went,
// so the Exclude block can explain why an incomplete selected exclusion
// blocks the search and offer a smaller list selection.
export interface CleanlistExclusionStatus {
  /** True once the selected exclusion has resolved completely. */
  resolved: boolean;
  /** The identifier set was capped server-side (50k); some leads missing. */
  truncated: boolean;
  /** A resolve is currently in flight. */
  isFetching: boolean;
  error?: string;
}

const EMPTY_CLEANLIST_EXCLUSION_STATUS: CleanlistExclusionStatus = {
  resolved: false,
  truncated: false,
  isFetching: false,
};

// ─── Per-CRM filter sections (CP-5b) ────────────────────────────────
//
// The NEW per-CRM model: each connected CRM owns a clause list in
// ``filters.crmFilterSections``. Unlike the flat ``excludeCrm*`` fields
// (which resolve against the single active CRM), a section clause carries
// its OWN ``crm`` so a Salesforce filter and a HubSpot filter resolve
// independently and can be combined.
//
// This is ADDITIVE to ``getActiveCrmExclusions`` — both paths run; the flat
// fields stay one release for back-compat. EXCLUDE-polarity only this PR.

/** One section EXCLUDE clause flattened to (crm, condition) for resolution. */
export interface ActiveCrmSectionFilter {
  crm: CrmFilterCrm;
  /** Stable client id of the originating clause (for per-clause UI state). */
  id: string;
  condition: CrmExclusionCondition;
}

/** Defensive default — a saved/hydrated search may predate
 *  ``crmFilterSections``. Always read sections through this. */
const EMPTY_CRM_FILTER_SECTIONS: Record<CrmFilterCrm, CrmFilterClause[]> = {
  salesforce: [],
  hubspot: [],
};

/**
 * Flatten every EXCLUDE clause across ``filters.crmFilterSections`` into a
 * list of (crm, condition) pairs to resolve. Skips include clauses (not
 * supported this PR) and blank conditions. Defaults a missing sections object
 * to the empty per-CRM map (saved-search safety).
 */
export function getActiveCrmSectionFilters(
  filters: SearchFilters
): ActiveCrmSectionFilter[] {
  const sections = filters.crmFilterSections ?? EMPTY_CRM_FILTER_SECTIONS;
  const out: ActiveCrmSectionFilter[] = [];
  (Object.keys(EMPTY_CRM_FILTER_SECTIONS) as CrmFilterCrm[]).forEach((crm) => {
    const clauses = Array.isArray(sections[crm]) ? sections[crm] : [];
    for (const clause of clauses) {
      if (!clause || clause.polarity !== 'exclude') continue;
      // Keep malformed/blank clauses selected; the dispatch validator reports
      // them before any resolver or provider call instead of dropping them.
      const condition =
        typeof clause.condition === 'string' ? clause.condition.trim() : '';
      out.push({
        crm,
        id: clause.id,
        condition: condition as CrmExclusionCondition,
      });
    }
  });
  return out;
}

// ─── Filter → ActiveFilter conversion ───────────────────────────────

/** "Followers ≥ 10" / "Followers ≤ 5000" chips for a min/max pair. The search
 *  swaps an inverted pair (``withOrderedRanges`` in buildCrustdataFilters), so
 *  the chips show the range it actually used; each chip keeps the key of the
 *  field holding its value, so removing it clears that value. */
function rangeChips(
  filters: SearchFilters,
  base: 'connections' | 'followers',
  label: string
): ActiveFilter[] {
  const min = `${base}Min` as const;
  const max = `${base}Max` as const;
  const swapped = withOrderedRanges(filters, [base])[min] !== filters[min];
  const bounds = [
    [swapped ? max : min, '≥'],
    [swapped ? min : max, '≤'],
  ] as const;
  return bounds
    .filter(([key]) => filters[key])
    .map(([key, op]) => ({
      key,
      label: `${label} ${op}`,
      value: filters[key],
      displayValue: `${label} ${op} ${filters[key]}`,
    }));
}

/** The In Instantly mode chip with the count of Instantly leads the search
 *  couldn't check (no LinkedIn URL), once a search has reported it. */
export function withInstantlyUnchecked(
  active: ActiveFilter[],
  unchecked: number | null
): ActiveFilter[] {
  if (unchecked == null || unchecked <= 0) return active;
  return active.map((chip) =>
    chip.key === 'excludeInInstantly'
      ? {
          ...chip,
          displayValue: `${chip.displayValue ?? chip.value} · ${unchecked.toLocaleString()} Instantly ${unchecked === 1 ? 'lead' : 'leads'} not checked (no LinkedIn)`,
        }
      : chip
  );
}

export function getActivePeopleFilters(
  filters: SearchFilters,
  composite?: CompanySearchFilters,
  // Per-CRM owner-name maps so ``owner:<id>`` section chips render the
  // owner's name. Threaded in by the hook from the cached CRM-users query;
  // omitted/empty → ids fall back to the raw value (still removable).
  crmChipCtx?: CrmFilterChipContext
): ActiveFilter[] {
  const active: ActiveFilter[] = [];
  const add = (
    key: string,
    label: string,
    values: string[],
    variant?: 'exclude'
  ) => {
    for (const v of values) {
      active.push({ key, label, value: v, variant });
    }
  };

  if (filters.firstName)
    active.push({
      key: 'firstName',
      label: 'First Name',
      value: filters.firstName,
    });
  if (filters.lastName)
    active.push({
      key: 'lastName',
      label: 'Last Name',
      value: filters.lastName,
    });
  add('linkedinUrls', 'LinkedIn URL', filters.linkedinUrls);
  add('jobTitlesInclude', 'Job Title', filters.jobTitlesInclude);
  add('jobTitlesExclude', 'Job Title', filters.jobTitlesExclude, 'exclude');
  if (filters.jobTitleMatchType !== 'contains') {
    active.push({
      key: 'jobTitleMatchType',
      label: 'Match Type',
      value: filters.jobTitleMatchType === 'exact' ? 'Exact' : 'Similar',
    });
  }
  add('seniority', 'Seniority', filters.seniority);
  if (filters.yearsAtCompany) {
    active.push({
      key: 'yearsAtCompany',
      label: 'Years in Role',
      value: filters.yearsAtCompany,
    });
  }
  add('contactCountries', 'Location', filters.contactCountries);
  add(
    'contactCountriesExclude',
    'Location',
    filters.contactCountriesExclude,
    'exclude'
  );
  add('contactRegions', 'Location', filters.contactRegions);
  add(
    'contactRegionsExclude',
    'Location',
    filters.contactRegionsExclude,
    'exclude'
  );
  add('contactCities', 'Location', filters.contactCities);
  add(
    'contactCitiesExclude',
    'Location',
    filters.contactCitiesExclude,
    'exclude'
  );
  add('skills', 'Skill', filters.skills);
  if (filters.yearsOfExperience) {
    active.push({
      key: 'yearsOfExperience',
      label: 'Experience',
      value: filters.yearsOfExperience,
    });
  }
  active.push(...rangeChips(filters, 'connections', 'Connections'));
  if (filters.recentlyChangedJobs) {
    active.push({
      key: 'recentlyChangedJobs',
      label: 'Recently Changed Jobs',
      value: 'Yes',
    });
  }
  add('certificationKeywords', 'Certification', filters.certificationKeywords);
  add('degreeTypes', 'Degree', filters.degreeTypes);
  add('schoolNames', 'School', filters.schoolNames);
  add('fieldsOfStudy', 'Field of Study', filters.fieldsOfStudy);
  add('pastTitles', 'Past Title', filters.pastTitles);
  add('pastCompanies', 'Past Company', filters.pastCompanies);
  if (filters.companyNameFilter)
    active.push({
      key: 'companyNameFilter',
      label: 'Company',
      value: filters.companyNameFilter,
    });
  add('companyCountries', 'HQ Country', filters.companyCountries);
  add(
    'companyCountriesExclude',
    'HQ Country',
    filters.companyCountriesExclude,
    'exclude'
  );
  add('companyRegions', 'HQ State', filters.companyRegions);
  add(
    'companyRegionsExclude',
    'HQ State',
    filters.companyRegionsExclude,
    'exclude'
  );
  add('companyCities', 'HQ City', filters.companyCities);
  add(
    'companyCitiesExclude',
    'HQ City',
    filters.companyCitiesExclude,
    'exclude'
  );
  add('industriesInclude', 'Industry', filters.industriesInclude);
  add('industriesExclude', 'Industry', filters.industriesExclude, 'exclude');
  add('companySizes', 'Headcount', filters.companySizes);

  // ── Expanded filters (PRD 2026-05) — visible on the chip strip ──
  // Render URL chips with a short slug for readability, but keep the
  // full URL as ``value`` so ``removeFilter`` can match it against
  // the raw entry stored in SearchFilters. Without this split, the
  // chip's X click no-op'd because the array.filter(v != value)
  // comparison never matched. (Reviewer catch on commit 947bf75b.)
  const shortenUrl = (u: string) => {
    try {
      const p = new URL(u);
      return p.pathname.split('/').filter(Boolean).pop() ?? p.host;
    } catch {
      return u;
    }
  };
  for (const url of filters.currentCompanyLinkedInUrls) {
    active.push({
      key: 'currentCompanyLinkedInUrls',
      label: 'Company',
      value: url,
      displayValue: shortenUrl(url),
    });
  }
  for (const domain of filters.currentCompanyDomains) {
    active.push({
      key: 'currentCompanyDomains',
      label: 'Company',
      value: domain,
    });
  }
  for (const name of filters.currentCompanyNames) {
    active.push({
      key: 'currentCompanyNames',
      label: 'Company',
      value: name,
    });
  }
  for (const url of filters.pastCompanyLinkedInUrls) {
    active.push({
      key: 'pastCompanyLinkedInUrls',
      label: 'Past Company',
      value: url,
      displayValue: shortenUrl(url),
    });
  }
  for (const url of filters.neverWorkedAtCompanies) {
    active.push({
      key: 'neverWorkedAtCompanies',
      label: 'Never @',
      value: url,
      displayValue: shortenUrl(url),
      variant: 'exclude',
    });
  }
  add(
    'functionCategoriesInclude',
    'Function',
    filters.functionCategoriesInclude
  );
  add(
    'pastFunctionCategoriesInclude',
    'Past Function',
    filters.pastFunctionCategoriesInclude
  );
  add('pastSeniority', 'Past Seniority', filters.pastSeniority);
  add('neverHeldTitles', 'Never Title', filters.neverHeldTitles, 'exclude');
  add('languages', 'Language', filters.languages);
  // Open-to cards are stored as canonical wire values (e.g. HIRING_MANAGER);
  // render the friendly label from OPEN_TO_CARD_OPTIONS on the chip while
  // keeping the raw value so removeFilter still matches the stored entry.
  for (const card of filters.openToCards) {
    active.push({
      key: 'openToCards',
      label: 'Open To',
      value: card,
      displayValue: OPEN_TO_CARD_OPTIONS.find((o) => o.value === card)?.label,
    });
  }
  active.push(...rangeChips(filters, 'followers', 'Followers'));
  if (filters.headlineContains) {
    active.push({
      key: 'headlineContains',
      label: 'Headline',
      value: filters.headlineContains,
    });
  }
  if (filters.summaryContains) {
    active.push({
      key: 'summaryContains',
      label: 'Summary',
      value: filters.summaryContains,
    });
  }
  if (filters.titleBooleanQuery.trim()) {
    active.push({
      key: 'titleBooleanQuery',
      label: 'Title query',
      value: filters.titleBooleanQuery,
    });
  }
  if (filters.keywordsBooleanQuery.trim()) {
    active.push({
      key: 'keywordsBooleanQuery',
      label: 'Keyword query',
      value: filters.keywordsBooleanQuery,
    });
  }
  if (filters.twitterHandle) {
    active.push({
      key: 'twitterHandle',
      label: 'Twitter',
      value: `@${filters.twitterHandle}`,
    });
  }
  if (filters.profileUpdatedAfter) {
    active.push({
      key: 'profileUpdatedAfter',
      label: 'Profile Refreshed After',
      value: filters.profileUpdatedAfter,
    });
  }
  if (filters.joinedCurrentAfter) {
    active.push({
      key: 'joinedCurrentAfter',
      label: 'Joined After',
      value: filters.joinedCurrentAfter,
    });
  }
  if (filters.pastLeftCompanyAfter) {
    active.push({
      key: 'pastLeftCompanyAfter',
      label: 'Left After',
      value: filters.pastLeftCompanyAfter,
    });
  }
  if (
    filters.pastCompaniesRequireAll &&
    filters.pastCompanyLinkedInUrls.length > 1
  ) {
    active.push({
      key: 'pastCompaniesRequireAll',
      label: 'Past',
      value: 'Worked at ALL',
    });
  }
  if (filters.geoRadius) {
    active.push({
      key: 'geoRadius',
      label: 'Within',
      value: `${filters.geoRadius.radius}${filters.geoRadius.unit} of ${filters.geoRadius.center}`,
    });
  }
  for (const url of filters.excludeProfileUrls) {
    active.push({
      key: 'excludeProfileUrls',
      label: 'Exclude URL',
      value: url,
      displayValue: shortenUrl(url),
      variant: 'exclude',
    });
  }
  for (const domain of filters.excludeCompanyDomains) {
    active.push({
      key: 'excludeCompanyDomains',
      label: 'Exclude Domain',
      value: domain,
      variant: 'exclude',
    });
  }

  // ── CRM-based exclusions (FE-E) — each enabled toggle is one chip ──
  // The label is "CRM" and the value carries the human-readable
  // condition so the chip reads e.g. "CRM: Current customers". The X
  // resets the field via removeFilter (boolean → false / string → '').
  if (filters.excludeCrmCustomers) {
    active.push({
      key: 'excludeCrmCustomers',
      label: 'CRM',
      value: 'Current customers',
      variant: 'exclude',
    });
  }
  if (filters.excludeCrmActiveOpportunities) {
    active.push({
      key: 'excludeCrmActiveOpportunities',
      label: 'CRM',
      value: 'Active opportunities',
      variant: 'exclude',
    });
  }
  if (filters.excludeCrmOwnedByMe) {
    active.push({
      key: 'excludeCrmOwnedByMe',
      label: 'CRM',
      value: 'Owned by me',
      variant: 'exclude',
    });
  }
  if (filters.excludeCrmOwnedByOthers) {
    active.push({
      key: 'excludeCrmOwnedByOthers',
      label: 'CRM',
      value: 'Owned by others',
      variant: 'exclude',
    });
  }
  if (filters.excludeCrmOwner.trim()) {
    active.push({
      key: 'excludeCrmOwner',
      label: 'CRM owner',
      value: filters.excludeCrmOwner,
      variant: 'exclude',
    });
  }
  if (filters.excludeCrmAccountStatus.trim()) {
    active.push({
      key: 'excludeCrmAccountStatus',
      label: 'CRM status',
      value: filters.excludeCrmAccountStatus,
      variant: 'exclude',
    });
  }
  // ── Cleanlist-level dedup exclude (CP-8) — one removable chip ──
  // Removing it resets ``excludeInCleanlist`` to 'off' via removeFilter's
  // scalar path. The list ids stay (harmless when scope is off) so toggling
  // back to 'list' restores the prior selection.
  const cleanlistScope = filters.excludeInCleanlist ?? 'off';
  if (cleanlistScope !== 'off') {
    const listCount = (filters.excludeInCleanlistListIds ?? []).length;
    active.push({
      key: 'excludeInCleanlist',
      label: `Already in ${BRAND.name}`,
      value:
        cleanlistScope === 'any'
          ? 'Anywhere'
          : listCount === 1
            ? '1 list'
            : `${listCount} lists`,
      variant: 'exclude',
    });
  }
  // ── In Instantly exclude: one chip for the mode, one for the blocklist ──
  // Removing the mode chip resets ``excludeInInstantly`` to 'off'; the
  // statuses stay but are sent only with the statuses mode.
  // A pill shows only its displayValue, so each names Instantly.
  const instantlyMode = filters.excludeInInstantly ?? 'off';
  if (instantlyMode !== 'off') {
    const value =
      instantlyMode === 'any_campaign'
        ? 'Any campaign'
        : (filters.excludeInInstantlyStatuses ?? [])
            .map((status) => INSTANTLY_STATUS_LABELS[status] ?? status)
            .join(', ') || 'Lead statuses';
    active.push({
      key: 'excludeInInstantly',
      label: 'In Instantly',
      value,
      displayValue:
        instantlyMode === 'any_campaign'
          ? 'in Instantly'
          : `in Instantly (${value})`,
      variant: 'exclude',
    });
  }
  if (filters.excludeInInstantlyBlocklist) {
    active.push({
      key: 'excludeInInstantlyBlocklist',
      label: 'In Instantly',
      value: 'Blocklist',
      displayValue: 'on Instantly blocklist',
      variant: 'exclude',
    });
  }
  // ── Composite firmographic filters (Company firmographics section) ──
  // These live on a sibling CompanySearchFilters blob (people.compositeCompanyFilters)
  // and route the People search through /people-search/by-companies. They
  // were previously applied to the request but never surfaced as removable
  // chips — a "VPs at Series B SaaS companies" search ran with no visible
  // "Series B" chip and no way to drop it. Each field below emits a chip
  // namespaced with a ``cmp:`` prefix so handleRemoveFilter can route the X
  // click back to setCompositeCompanyFilters instead of the SearchFilters
  // remover (which only knows person keys and would silently no-op).
  if (composite) {
    if (hasStartupPrograms(composite.startupPrograms)) {
      active.push({
        key: 'cmp:startupPrograms',
        label: 'Startup programs',
        value: 'selected',
        displayValue: `Startup programs (${composite.startupPrograms?.selections?.length ?? 0})`,
      });
    }
    type CmpChip = {
      // Field on CompanySearchFilters; the chip key is `cmp:${field}`.
      field: keyof CompanySearchFilters;
      label: string;
      // How the field is shaped — drives chip emission + display.
      kind: 'array' | 'value' | 'currency' | 'bool';
      variant?: 'exclude';
      // Optional value → display-label mapper (array kinds only).
      mapValue?: (v: string) => string;
    };
    const fmtCurrency = (v: string) => {
      const n = Number(v);
      return Number.isNaN(n) ? `$${v}` : `$${n.toLocaleString()}`;
    };
    const stockLabel = (v: string) =>
      STOCK_LISTING_OPTIONS.find((o) => o.value === v)?.label ?? v;
    const cmpConfig: CmpChip[] = [
      // Industry / headcount / company-type tags + their excludes.
      { field: 'industriesInclude', label: 'Industry', kind: 'array' },
      {
        field: 'industriesExclude',
        label: 'Industry',
        kind: 'array',
        variant: 'exclude',
      },
      {
        field: 'naicsCodes',
        label: 'NAICS',
        kind: 'array',
        mapValue: naicsLabel,
      },
      { field: 'headcountRanges', label: 'Headcount', kind: 'array' },
      {
        field: 'headcountRangesExclude',
        label: 'Headcount',
        kind: 'array',
        variant: 'exclude',
      },
      { field: 'companyType', label: 'Company type', kind: 'array' },
      {
        field: 'companyTypeExclude',
        label: 'Company type',
        kind: 'array',
        variant: 'exclude',
      },
      // HQ location — country (ISO-3), state, city + their excludes, plus the
      // largest-headcount-country picker ("Most employees in").
      { field: 'hqCountriesIso3', label: 'HQ country', kind: 'array' },
      {
        field: 'hqCountriesIso3Exclude',
        label: 'HQ country',
        kind: 'array',
        variant: 'exclude',
      },
      {
        field: 'largestHeadcountCountriesIso3',
        label: 'Most employees in',
        kind: 'array',
      },
      { field: 'hqStates', label: 'HQ state', kind: 'array' },
      {
        field: 'hqStatesExclude',
        label: 'HQ state',
        kind: 'array',
        variant: 'exclude',
      },
      { field: 'hqCities', label: 'HQ city', kind: 'array' },
      {
        field: 'hqCitiesExclude',
        label: 'HQ city',
        kind: 'array',
        variant: 'exclude',
      },
      // LinkedIn / Crunchbase category tags.
      {
        field: 'linkedinCategoriesInclude',
        label: 'LinkedIn category',
        kind: 'array',
      },
      {
        field: 'crunchbaseCategoriesInclude',
        label: 'Crunchbase category',
        kind: 'array',
      },
      {
        field: 'crunchbaseCategoriesExclude',
        label: 'Crunchbase category',
        kind: 'array',
        variant: 'exclude',
      },
      // Funding stages — stored as canonical display labels ("Series B").
      { field: 'fundingRoundTypes', label: 'Funding', kind: 'array' },
      {
        field: 'fundingRoundTypesExclude',
        label: 'Funding',
        kind: 'array',
        variant: 'exclude',
      },
      { field: 'fundingAmountMin', label: 'Funding total ≥', kind: 'currency' },
      {
        field: 'lastFundingRoundAmountMin',
        label: 'Last round ≥',
        kind: 'currency',
      },
      {
        field: 'lastFundingRoundAmountMax',
        label: 'Last round ≤',
        kind: 'currency',
      },
      // Investors (free-text array on both sides).
      { field: 'investorsInclude', label: 'Investor', kind: 'array' },
      {
        field: 'investorsExclude',
        label: 'Investor',
        kind: 'array',
        variant: 'exclude',
      },
      { field: 'revenueMin', label: 'Revenue ≥', kind: 'currency' },
      { field: 'revenueMax', label: 'Revenue ≤', kind: 'currency' },
      { field: 'yearFoundedMin', label: 'Founded ≥', kind: 'value' },
      { field: 'yearFoundedMax', label: 'Founded ≤', kind: 'value' },
      // Headcount numeric min/max (employee_metrics.latest_count).
      { field: 'headcountLatestMin', label: 'Employees ≥', kind: 'value' },
      { field: 'headcountLatestMax', label: 'Employees ≤', kind: 'value' },
      { field: 'growthMin', label: 'Headcount growth ≥', kind: 'value' },
      { field: 'growthMax', label: 'Headcount growth ≤', kind: 'value' },
      { field: 'growth1mMin', label: 'Headcount growth (1m) ≥', kind: 'value' },
      { field: 'growth3mMin', label: 'Headcount growth (3m) ≥', kind: 'value' },
      {
        field: 'growth12mMin',
        label: 'Headcount growth (12m) ≥',
        kind: 'value',
      },
      // Follower metrics.
      { field: 'followersMin', label: 'Followers ≥', kind: 'value' },
      {
        field: 'followersGrowth6mMin',
        label: 'Follower growth (6m) ≥',
        kind: 'value',
      },
      {
        field: 'followersGrowth12mMin',
        label: 'Follower growth (12m) ≥',
        kind: 'value',
      },
      { field: 'lastFundingDateAfter', label: 'Funded after', kind: 'value' },
      { field: 'ipoDateAfter', label: 'IPO after', kind: 'value' },
      {
        field: 'stockListings',
        label: 'Listed',
        kind: 'array',
        mapValue: stockLabel,
      },
      { field: 'stockSymbols', label: 'Stock symbol', kind: 'array' },
      { field: 'domainsList', label: 'Domain', kind: 'array' },
      { field: 'companyNamesList', label: 'Company', kind: 'array' },
      { field: 'linkedinUrls', label: 'Company LinkedIn', kind: 'array' },
      { field: 'twitterHandle', label: 'Twitter', kind: 'value' },
      { field: 'descriptionKeyword', label: 'Description', kind: 'value' },
      { field: 'updatedAfter', label: 'Updated after', kind: 'value' },
      { field: 'acquiredOnly', label: 'Acquired', kind: 'bool' },
      { field: 'bootstrappedOnly', label: 'Bootstrapped', kind: 'bool' },
    ];
    for (const c of cmpConfig) {
      const raw = composite[c.field];
      const key = `cmp:${c.field}`;
      if (c.kind === 'array') {
        if (Array.isArray(raw)) {
          for (const v of raw) {
            const value = String(v);
            active.push({
              key,
              label: c.label,
              value,
              displayValue: c.mapValue ? c.mapValue(value) : undefined,
              variant: c.variant,
            });
          }
        }
      } else if (c.kind === 'bool') {
        if (raw === true) {
          active.push({
            key,
            label: c.label,
            value: 'Only',
            variant: c.variant,
          });
        }
      } else {
        // 'value' | 'currency' — single string field. Keep the raw value
        // for chip identity; currency renders a formatted displayValue.
        if (typeof raw === 'string' && raw.trim() !== '') {
          active.push({
            key,
            label: c.label,
            value: raw,
            displayValue: c.kind === 'currency' ? fmtCurrency(raw) : undefined,
            variant: c.variant,
          });
        }
      }
    }
  }

  // ── Per-CRM section chips (CP-6) — readable, one per section clause ──
  // Emitted in ADDITION to the legacy flat ``excludeCrm*`` chips above
  // (which stay for back-compat with saved searches predating the per-CRM
  // model). Each chip's key encodes its (crm, clauseId) so the X removes
  // exactly that clause; owner ids resolve to names via ``crmChipCtx``.
  active.push(...buildCrmFilterChips(filters, crmChipCtx));

  return active;
}

/**
 * Keys that should NOT be treated as user-set filters even when their
 * value differs from INITIAL_FILTERS. ``maxResults`` /
 * ``limitPerCompany`` are pagination controls; ``jobTitleMatchType``
 * always has a default ('contains'); ``companyFilterCompositeMode``
 * is a routing override evaluated separately. Anything else qualifies
 * as a real filter when it diverges from its INITIAL value.
 */
const NON_FILTER_SEARCH_KEYS: Set<keyof SearchFilters> = new Set([
  'jobTitleMatchType',
  'maxResults',
  'limitPerCompany',
  'companyFilterCompositeMode',
]);

/** Generic "did the user set anything?" predicate. Compares each key
 *  to its INITIAL_FILTERS counterpart so newly-added fields are picked
 *  up automatically — the previous hand-maintained list was missing
 *  ~14 fields (currentCompanyLinkedInUrls, currentCompanyDomains,
 *  geoRadius, languages, openToCards, functionCategoriesInclude,
 *  pastSeniority, neverWorkedAtCompanies, neverHeldTitles,
 *  pastFunctionCategoriesInclude, profileUpdatedAfter, headlineContains,
 *  summaryContains, twitterHandle, joinedCurrentAfter,
 *  pastLeftCompanyAfter, excludeProfileUrls, pastCompaniesRequireAll,
 *  followersMin/Max, currentCompanyLinkedInUrls, pastCompanyLinkedInUrls)
 *  and silently blocked legitimate searches with "Please add a filter."
 *  Round-1 + round-2 reliability audit P0. */
export function hasAnyFilterValues(filters: SearchFilters): boolean {
  for (const key of Object.keys(INITIAL_FILTERS) as Array<
    keyof SearchFilters
  >) {
    if (NON_FILTER_SEARCH_KEYS.has(key)) continue;
    // Statuses left over from the statuses mode narrow nothing once the
    // mode moves on; the request leaves them out too.
    if (
      key === 'excludeInInstantlyStatuses' &&
      filters.excludeInInstantly !== 'statuses'
    )
      continue;
    if (key === 'crmFilterSections') {
      // The default ``{ salesforce: [], hubspot: [] }`` is a non-null object but
      // carries NO clauses; isFieldSet would treat it as set and make a blank
      // search look filtered (bypassing the empty-search gate). Only count it
      // when a section actually has a clause.
      const sections = filters.crmFilterSections;
      if (
        sections &&
        Object.values(sections).some((clauses) => (clauses?.length ?? 0) > 0)
      ) {
        return true;
      }
      continue;
    }
    if (isFieldSet(filters[key], INITIAL_FILTERS[key])) return true;
  }
  return false;
}

/** Compare a current field value to its INITIAL_FILTERS default. Truthy
 *  means the user has set it. Handles arrays, strings, booleans,
 *  numbers, and nullable objects (geoRadius). */

function isFieldSet(current: unknown, initial: unknown): boolean {
  if (Array.isArray(current)) return current.length > 0;
  // Compare to the default, not just emptiness: a field with a NON-empty
  // default (e.g. excludeInCleanlist: 'off') would otherwise read as "set"
  // forever, making hasAnyFilterValues(INITIAL_FILTERS) always true — which
  // hid the People-tab SearchLanding and let filterless searches dispatch (F3).
  if (typeof current === 'string')
    return current.trim() !== '' && current !== initial;
  if (typeof current === 'boolean') return current !== initial;
  if (typeof current === 'number') return current !== initial;
  if (current == null) return false;
  if (typeof current === 'object') {
    // geoRadius is the only nullable-object field today; treat any
    // non-null object as set.
    return true;
  }
  return false;
}

// ─── Result post-processing ─────────────────────────────────────────

/**
 * Cap the rendered result set to at most N rows per unique company.
 * Crustdata doesn't ship a `limit_per_company` operator on the legacy
 * `/screener/persondb/search` endpoint, so this is a pure frontend
 * post-filter applied to the array returned by the API.
 *
 * Stable: preserves the original ordering inside each company group.
 * Returns the input unchanged when ``maxPerCompany`` parses to <= 0.
 */
export function capResultsPerCompany(
  results: PersonRecord[],
  maxPerCompany: string,
  criteria: EmployerMatchCriteria = {}
): PersonRecord[] {
  const max = parseInt(maxPerCompany, 10);
  if (!Number.isFinite(max) || max <= 0) return results;
  const counts = new Map<string, number>();
  const kept: PersonRecord[] = [];
  for (const r of results) {
    // Prefer LinkedIn URL when available (most stable id); fall back
    // to domain, then to lowercased name, then to a synthetic
    // unique key so rows with no company identifier pass through.
    // Use the same program discriminator as programEmployerCandidates (present
    // matched OR selected employer keys), so the cap keys on the server-chosen
    // employer for program rows and only falls back for ordinary rows.
    const candidates = programEmployerCandidates(r.raw);
    const matching = candidates?.length
      ? findBestMatchingEmployer(candidates, criteria)
      : undefined;
    const rawCompanyKey =
      candidates !== undefined
        ? String(matching?.company_linkedin_profile_url || `__unknown:${r.id}`)
        : (
            r.raw?.current_employers as
              | { company_linkedin_profile_url?: string }[]
              | undefined
          )?.[0]?.company_linkedin_profile_url ||
          r.company_domain ||
          r.company_name?.toLowerCase().trim() ||
          `__unknown:${r.id}`;
    const key = rawCompanyKey || `__unknown:${r.id}`;
    const n = counts.get(key) ?? 0;
    if (n < max) {
      counts.set(key, n + 1);
      kept.push(r);
    }
  }
  return kept;
}

// ─── Build Crustdata Filters ────────────────────────────────────────

// Exported so surfaces that need a match count from a SearchFilters blob
// (e.g. the playbooks audience sizer) build the exact same Crustdata payload
// the People Finder does, rather than re-deriving it.
/** Crustdata's text operator for an employer-name match mode. Mirrors
 *  ``companyNameOperator`` on the Companies tab. */
export function employerNameOperator(
  matchType: SearchFilters['companyNameMatchType']
): string {
  return matchType === 'exact' ? '=' : matchType === 'similar' ? '(.)' : '[.]';
}

export function buildCrustdataFilters(input: SearchFilters) {
  // A typed company name (or domain) is one more alternative in the current
  // company OR group below when that group has other values, never an AND
  // against it; alone it keeps its own leaf, below (foldPeopleCompanyScalar).
  const filters = withOrderedRanges(foldPeopleCompanyScalar(input), [
    'connections',
    'followers',
  ]);
  const conditions: Record<string, unknown>[] = [];

  // Company Attributes
  // Industry arrays may contain group tokens (e.g. "Software & IT")
  // from the grouped FilterPicker. Expand them to the underlying
  // LinkedIn industry strings before sending — Crustdata's
  // company_industries field only matches canonical industry names.
  addEnumFilter(
    conditions,
    'company_industries',
    expandIndustrySelection(filters.industriesInclude)
  );
  addEnumFilter(
    conditions,
    'company_industries',
    expandIndustrySelection(filters.industriesExclude),
    true
  );
  addEnumFilter(conditions, 'company_headcount_range', filters.companySizes);

  // Seniority
  addEnumFilter(conditions, 'seniority_level', filters.seniority);
  addEnumFilter(
    conditions,
    'function_category',
    filters.functionCategoriesInclude
  );

  // Job Title - expand abbreviations
  if (
    filters.jobTitlesInclude.length &&
    filters.jobTitleMatchType !== 'exact'
  ) {
    addFuzzyFilter(
      conditions,
      'title',
      expandJobTitleTerms(filters.jobTitlesInclude),
      filters.jobTitleMatchType
    );
  } else {
    addFuzzyFilter(
      conditions,
      'title',
      filters.jobTitlesInclude,
      filters.jobTitleMatchType
    );
  }
  addEnumFilter(conditions, 'title', filters.jobTitlesExclude, true);

  // Experience.
  //
  // Picker values look like ``"3-5"`` (ranged) or ``"10+"`` (open-ended
  // floor). The old ``split('-')`` on ``"10+"`` returned ``["10+"]`` and
  // then ``Number("10+")`` = NaN, so the "More than 10 years" option
  // quietly broke the filter. Strip the trailing ``+`` before parsing
  // and skip the upper-bound leg when no max exists.
  if (filters.yearsOfExperience) {
    const [minRaw, maxRaw] = filters.yearsOfExperience.split('-');
    const minNum = Number((minRaw || '').replace(/\+$/, ''));
    if (!Number.isNaN(minNum)) {
      addRangeFilter(conditions, 'years_of_experience_raw', 'gte', minNum);
    }
    if (maxRaw && maxRaw !== '+') {
      const maxNum = Number(maxRaw);
      if (!Number.isNaN(maxNum)) {
        addRangeFilter(conditions, 'years_of_experience_raw', 'lte', maxNum);
      }
    }
  }

  // Contact Location — city > state > country precedence on INCLUDES.
  // The most-specific include wins: a picked city already carries its own
  // state and country (the picker commits the full "City, State, Country"
  // triple), so also applying the broader pickers would only let a
  // contradictory pair — "United States" + a UK city — zero the search out.
  //
  // Includes compile to Crustdata's LABELLED columns (location_details.city /
  // .state / .country) rather than a substring test over the free-text
  // ``region`` blob. That is what makes country genuinely constrain a city:
  // the blob could only ask "does this address contain 'Manchester'", which
  // matched Manchester UK as readily as Manchester NH. Verified equivalent on
  // live data — Manchester+Nashua NH returns 92,448 structured vs 92,517 for
  // the old substring pair, a 0.07% delta.
  //
  // Excludes still ride the substring path: they are a separate,
  // lower-traffic surface and are out of scope for this change.
  const hasContactCityInc = filters.contactCities.length > 0;
  const hasContactStateInc = filters.contactRegions.length > 0;
  // Resolve the country picker once: it constrains any city/state value that
  // carries no country of its own, and is the whole filter when neither of
  // the narrower pickers is set. The picker holds tokens, not triples — iso3,
  // display names or group tokens ("EMEA") — so resolve to both display and
  // full spellings: ``location_details.country`` is overwhelmingly "United
  // States" but a few rows carry "United States of America" (156 at last
  // count), and sending both costs nothing.
  const contactCountriesInc = Array.from(
    new Set([
      ...expandCountrySelectionToDisplayNames(filters.contactCountries),
      ...expandCountrySelectionToFullNames(filters.contactCountries),
    ])
  );
  if (hasContactCityInc) {
    addStructuredLocationFilter(
      conditions,
      PERSON_LOCATION_COLUMNS,
      filters.contactCities,
      contactCountriesInc
    );
  } else if (hasContactStateInc) {
    addStructuredLocationFilter(
      conditions,
      PERSON_LOCATION_COLUMNS,
      filters.contactRegions,
      contactCountriesInc
    );
  } else {
    // Country-only searches keep the long-standing ``region [.]`` shape. It
    // was never the broken case — a bare country substring cannot pick up the
    // wrong country — and that shape is pinned by a cross-repo contract
    // (src/utils/wire-normalization.contract.test.ts and the backend's
    // tests/search/test_wire_normalization_contract.py, which replays the
    // exact same literal). Structured columns are used where they actually
    // buy something: the city/state branches, and constraining bare values.
    addFuzzyFilter(
      conditions,
      'region',
      expandCountrySelectionToDisplayNames(filters.contactCountries)
    );
  }
  addContainsFilter(
    conditions,
    'region_country_component',
    expandCountrySelectionToFullNames(filters.contactCountriesExclude),
    'not_contains'
  );
  addContainsFilter(
    conditions,
    'region_state_component',
    filters.contactRegionsExclude,
    'not_contains'
  );
  addContainsFilter(
    conditions,
    'region',
    filters.contactCitiesExclude,
    'not_contains'
  );

  // Company HQ Location — same city > state > country precedence.
  const hasCompanyCityInc = filters.companyCities.length > 0;
  const hasCompanyStateInc = filters.companyRegions.length > 0;
  // company_hq_location is also a LinkedIn-style location string -> display names.
  const companyCountriesInc =
    hasCompanyCityInc || hasCompanyStateInc
      ? []
      : expandCountrySelectionToDisplayNames(filters.companyCountries);
  const companyStatesInc = hasCompanyCityInc ? [] : filters.companyRegions;
  addFuzzyFilter(conditions, 'company_hq_location', companyCountriesInc);
  addContainsFilter(
    conditions,
    'company_hq_location_country',
    expandCountrySelectionToFullNames(filters.companyCountriesExclude),
    'not_contains'
  );
  addFuzzyFilter(conditions, 'company_hq_location', companyStatesInc);
  addContainsFilter(
    conditions,
    'company_hq_location_region',
    filters.companyRegionsExclude,
    'not_contains'
  );
  // Cities compile to the same native substring leaf on EVERY surface —
  // interactive search, the Add-to-list import pull, the playbook audience
  // sizer and the playbook save. The interactive paths used to pre-resolve
  // these through /autocomplete and emit an exact ``in`` list instead, which
  // made the preview, the imported set and the saved playbook disagree about
  // the same audience. The scoped city picker already commits the full
  // "City, State, Country" triple (see ``filterLocationSuggestions``), so the
  // substring test on that triple is as precise as the resolved list was —
  // without the ≤100-suggestion recall cap or the per-search round-trips.
  addFuzzyFilter(conditions, 'company_hq_location', filters.companyCities);
  addContainsFilter(
    conditions,
    'company_hq_location',
    filters.companyCitiesExclude,
    'not_contains'
  );

  // Profile
  addFuzzyFilter(conditions, 'skills', filters.skills);
  if (filters.connectionsMin) {
    const val = Number(filters.connectionsMin);
    if (!Number.isNaN(val))
      addRangeFilter(conditions, 'num_of_connections', 'gte', val);
  }
  if (filters.connectionsMax) {
    const val = Number(filters.connectionsMax);
    if (!Number.isNaN(val))
      addRangeFilter(conditions, 'num_of_connections', 'lte', val);
  }
  if (filters.followersMin) {
    const val = Number(filters.followersMin);
    if (!Number.isNaN(val))
      addRangeFilter(conditions, 'num_of_followers', 'gte', val);
  }
  if (filters.followersMax) {
    const val = Number(filters.followersMax);
    if (!Number.isNaN(val))
      addRangeFilter(conditions, 'num_of_followers', 'lte', val);
  }
  addEnumFilter(conditions, 'languages', filters.languages);
  addEnumFilter(conditions, 'open_to_cards', filters.openToCards);
  if (filters.profileUpdatedAfter) {
    conditions.push({
      column: 'profile_updated_after',
      type: 'range_filter',
      filter_type: 'gte',
      value: normalizeCalendarDate(
        filters.profileUpdatedAfter,
        'profileUpdatedAfter',
        'Profile updated after'
      ),
    });
  }
  if (filters.twitterHandle.trim()) {
    conditions.push({
      column: 'twitter_handle',
      type: '=',
      value: filters.twitterHandle.trim().replace(/^@+/, ''),
    });
  }

  // Education
  addEnumFilter(conditions, 'education_degree_name', filters.degreeTypes);
  addEnumFilter(conditions, 'education_institute_name', filters.schoolNames);
  addEnumFilter(conditions, 'education_field_of_study', filters.fieldsOfStudy);

  // Certifications
  addEnumFilter(
    conditions,
    'certifications_name',
    filters.certificationKeywords
  );

  // New filters - verified against docs/plans/crustdata-people-search-api.md

  // A typed company name with no other current-company value (the fold
  // above left it): staging's leaf, in staging's place, byte-identical. A
  // URL or bare domain never matches an employer NAME, so it goes out as an
  // exact domain match.
  const companyName = filters.companyNameFilter.trim();
  const companyNameDomain = companyName ? asTypedDomain(companyName) : null;
  if (companyNameDomain) {
    conditions.push({
      column: 'current_employers.company_website_domain',
      type: '=',
      value: companyNameDomain,
    });
  } else if (companyName) {
    conditions.push({
      column: 'current_employers.name',
      type: employerNameOperator(filters.companyNameMatchType),
      value: companyName,
    });
  }

  // First/last name - exact match (not fuzzy). A LinkedIn URL already
  // names the person, so the name filters are dropped when one is set: an
  // extra exact-name condition only loses the match (and a name plus a URL
  // batch is a request the provider rejects). A full name typed into First
  // name with Last name empty is split on its first and last words.
  const hasLinkedinUrls =
    normalizePersonLinkedInUrls(filters.linkedinUrls).length > 0;
  let firstName = filters.firstName.trim();
  let lastName = filters.lastName.trim();
  if (!lastName && /\s/.test(firstName)) {
    const words = firstName.split(/\s+/);
    firstName = words[0];
    lastName = words[words.length - 1];
  }
  if (firstName && !hasLinkedinUrls) {
    conditions.push({ column: 'first_name', type: '=', value: firstName });
  }
  if (lastName && !hasLinkedinUrls) {
    conditions.push({ column: 'last_name', type: '=', value: lastName });
  }

  // LinkedIn URLs - exact match, OR if multiple.
  //
  // Multi-URL groups use the v1 ``group_filter`` shape (matches
  // wrapFilters + addContainsFilter). The previous ``{op, conditions}``
  // shape was a v2-ish artifact that the v1 persondb parser silently
  // drops — bulk LinkedIn paste would return an unfiltered result set.
  if (filters.linkedinUrls.length > 0) {
    // All pasted LinkedIn URLs go into ONE Crustdata batch condition: a single
    // `in` list on flagship_profile_url (the VANITY url field users paste — NOT
    // the opaque linkedin_profile_url, which holds the /in/ACoAA… form),
    // normalized to Crustdata's stored shape so they byte-match. Was a single
    // `=` / N OR'd `=` conditions on the wrong column → 0 rows for vanity urls,
    // and the OR-of-`=` batch tripped the v1 persondb parser → 502.
    const normalizedLinkedinUrls = normalizePersonLinkedInUrls(
      filters.linkedinUrls
    );
    if (normalizedLinkedinUrls.length > 0) {
      conditions.push({
        column: 'flagship_profile_url',
        type: 'in',
        value: normalizedLinkedinUrls,
      });
    }
  }

  // Past employers. The require-all switch changes the selected companies
  // from one OR group into independent top-level predicates, which the outer
  // AND requires a profile to satisfy across its employment history.
  const pastCompanyUrls = normalizeCompanyLinkedInUrls(
    filters.pastCompanyLinkedInUrls
  );
  if (filters.pastCompaniesRequireAll) {
    for (const company of filters.pastCompanies) {
      addFuzzyFilter(conditions, 'past_company_name', [company]);
    }
    for (const url of pastCompanyUrls) {
      conditions.push({
        column: 'past_company_linkedin_profile_url',
        type: '=',
        value: url,
      });
    }
  } else {
    // Match Any: a past company named OR one given by its LinkedIn URL. One
    // paste writes both, and an AND of the two wanted someone who worked at
    // a named company and at a URL's company. Either alone is sent as before.
    const anyPast: Record<string, unknown>[] = [];
    if (filters.pastCompanies.length)
      addFuzzyFilter(anyPast, 'past_employers.name', filters.pastCompanies);
    addEnumFilter(
      anyPast,
      'past_company_linkedin_profile_url',
      pastCompanyUrls
    );
    if (filters.pastCompanies.length && pastCompanyUrls.length)
      conditions.push({ type: 'group_filter', op: 'or', filters: anyPast });
    else conditions.push(...anyPast);
  }
  if (filters.pastTitles.length) {
    addFuzzyFilter(conditions, 'past_employers.title', filters.pastTitles);
  }
  addEnumFilter(conditions, 'past_seniority_level', filters.pastSeniority);
  addEnumFilter(
    conditions,
    'past_function_category',
    filters.pastFunctionCategoriesInclude
  );

  addEnumFilter(
    conditions,
    'all_company_linkedin_profile_url',
    normalizeCompanyLinkedInUrls(filters.neverWorkedAtCompanies),
    true
  );
  addEnumFilter(conditions, 'all_title', filters.neverHeldTitles, true);

  if (filters.joinedCurrentAfter) {
    conditions.push({
      column: 'joined_current_after',
      type: 'range_filter',
      filter_type: 'gte',
      value: normalizeCalendarDate(
        filters.joinedCurrentAfter,
        'joinedCurrentAfter',
        'Started current role after'
      ),
    });
  }
  if (filters.pastLeftCompanyAfter) {
    conditions.push({
      column: 'past_left_company_after',
      type: 'range_filter',
      filter_type: 'gte',
      value: normalizeCalendarDate(
        filters.pastLeftCompanyAfter,
        'pastLeftCompanyAfter',
        'Left past company after'
      ),
    });
  }

  // Years at company (maps to current_employers.years_at_company_raw).
  // Same ``10+`` NaN pitfall as yearsOfExperience above — strip trailing
  // ``+`` before parsing and skip the upper-bound leg when open-ended.
  if (filters.yearsAtCompany) {
    const [minRaw, maxRaw] = filters.yearsAtCompany.split('-');
    const minNum = Number((minRaw || '').replace(/\+$/, ''));
    if (!Number.isNaN(minNum)) {
      addRangeFilter(
        conditions,
        'current_employers.years_at_company_raw',
        'gte',
        minNum
      );
    }
    if (maxRaw && maxRaw !== '+') {
      const maxNum = Number(maxRaw);
      if (!Number.isNaN(maxNum)) {
        addRangeFilter(
          conditions,
          'current_employers.years_at_company_raw',
          'lte',
          maxNum
        );
      }
    }
  }

  // Recently changed jobs (boolean field)
  if (filters.recentlyChangedJobs) {
    conditions.push({
      column: 'recently_changed_jobs',
      type: '=',
      value: true,
    });
  }

  // Headline / Summary contains — single-term shortcuts that don't go
  // through the boolean parser (kept for users who just want a plain
  // substring match without learning the AND/OR syntax).
  if (filters.headlineContains.trim()) {
    addFuzzyFilter(conditions, 'headline', [filters.headlineContains.trim()]);
  }
  if (filters.summaryContains.trim()) {
    addFuzzyFilter(conditions, 'summary', [filters.summaryContains.trim()]);
  }

  // Boolean queries — Sales-Nav style `(a AND b) OR c`, translated to
  // Crustdata's group_filter shape. A malformed expression THROWS: swallowing
  // it dropped just this predicate and left the rest, so a stray paren turned
  // "VP or Director at these accounts" into "everyone at these accounts".
  // Every dispatch boundary catches this and refuses the request.
  if (filters.titleBooleanQuery.trim()) {
    const ast = parseBooleanQueryOrCompileError(
      filters.titleBooleanQuery,
      'titleBooleanQuery',
      'Job title Boolean query'
    );
    if (ast) {
      const f = booleanQueryToCrustdataFilter(ast, ['current_employers.title']);
      if (f) conditions.push(f as Record<string, unknown>);
    }
  }
  if (filters.keywordsBooleanQuery.trim()) {
    const ast = parseBooleanQueryOrCompileError(
      filters.keywordsBooleanQuery,
      'keywordsBooleanQuery',
      'Keyword Boolean query'
    );
    if (ast) {
      // Apply across headline + summary, OR'd together so each term
      // hits either field.
      const f = booleanQueryToCrustdataFilter(ast, ['headline', 'summary']);
      if (f) conditions.push(f as Record<string, unknown>);
    }
  }

  // Current employer — URL + domain + name. Any path identifies a
  // specific company. All three fields target ``current_employers.*``;
  // when the user (or the bulk Find Contacts flow) supplies several,
  // OR them together so a row hits if any signal matches.
  // Without this OR, a row whose Crustdata response is missing
  // ``company_linkedin_profile_url`` (which happens often) would be
  // filtered out by an AND. Domain is the more reliable join.
  // Normalize at the wire boundary: PersonDB stores URLs without a
  // trailing slash, lowercased, on the ``www`` subdomain. A user-pasted
  // URL with any other shape returns zero rows from the byte-exact
  // equality filter. The bulk-paste write sites also normalize, but
  // doing it here too means old saved-search state with bad URLs starts
  // working without a data migration.
  const currentCompanyConds: Record<string, unknown>[] = [
    ...normalizeCompanyLinkedInUrls(filters.currentCompanyLinkedInUrls).map(
      (url) => ({
        column: 'current_employers.company_linkedin_profile_url',
        type: '=',
        value: url,
      })
    ),
    // Normalized at the WIRE boundary, not just at the write site: saved
    // searches, ?q= links and agent-authored filters carry raw pastes.
    ...normalizeDomainList(filters.currentCompanyDomains).map((domain) => ({
      column: 'current_employers.company_website_domain',
      type: '=',
      value: domain,
    })),
    // Employer names (typed or pasted; a typed scalar name is folded in at
    // the top), OR'd with the URL/domain conditions so
    // "employer matches ANY pasted name OR domain OR URL". Exact mode is what
    // stops a pasted "Ramp" also returning people at "Rampart".
    // A blank name compiles to nothing (it used to send a whitespace leaf).
    ...filters.currentCompanyNames
      .filter((name) => name.trim())
      .map((name) => ({
        column: 'current_employers.name',
        type: employerNameOperator(filters.companyNameMatchType),
        value: name,
      })),
  ];
  if (currentCompanyConds.length === 1) {
    conditions.push(currentCompanyConds[0]);
  } else if (currentCompanyConds.length > 1) {
    conditions.push({
      type: 'group_filter',
      op: 'or',
      filters: currentCompanyConds,
    });
  }

  // Geo-radius filter — Crustdata's ``geo_distance`` operator. The
  // value object's required keys are ``location`` (free-text city) or
  // ``lat_lng`` plus a numeric ``distance``; ``unit`` defaults to km
  // when omitted (verified live: bare ``distance: 25`` matches the
  // same population as ``distance: 25, unit: 'km'``). The previous
  // shape (``center`` / ``radius``) was silently rejected with a 502.
  if (
    filters.geoRadius &&
    filters.geoRadius.center.trim() &&
    filters.geoRadius.radius > 0
  ) {
    conditions.push({
      column: 'region',
      type: 'geo_distance',
      value: {
        location: filters.geoRadius.center,
        distance: filters.geoRadius.radius,
        unit: filters.geoRadius.unit,
      },
    });
  }

  return wrapFilters(conditions);
}

/**
 * Merge subtract-only (exclude) conditions into a person_filter group.
 *
 * ``buildCrustdataFilters`` (via ``wrapFilters``) returns ``{}`` when the
 * user set no positive person filters. A subtract-only query is invalid on
 * the plain ``/people-search/search`` endpoint — it has no positive bound —
 * so there we only extend an EXISTING AND group and never synthesize one.
 *
 * On the composite ``/people-search/by-companies`` endpoint the
 * ``company_filter`` DOES supply the bound, so an exclude-only person_filter
 * is valid: we synthesize the AND group rather than drop the excludes.
 * Dropping them is a privacy leak — excluded CRM customers / Cleanlist
 * dupes would otherwise slip into a firmographic-only result set and, worse,
 * into the Add-to-list import pull (up to 5,000 rows). See CP-8 / #502.
 *
 * Mutates ``apiFilters`` in place (matching the prior inline splice) and is a
 * no-op when there are no exclude conditions. Centralising this here removes
 * the duplicated splice that previously diverged between the two call sites.
 */
export function applyExcludeConditions(
  apiFilters: unknown,
  excludeConditions: Record<string, unknown>[],
  useComposite: boolean
): void {
  if (excludeConditions.length === 0 || !apiFilters) return;
  const group = apiFilters as {
    type?: string;
    op?: string;
    filters?: unknown[];
  };
  if (Array.isArray(group.filters)) {
    group.filters.push(...excludeConditions);
  } else if (useComposite) {
    group.type = 'group_filter';
    group.op = 'and';
    group.filters = [...excludeConditions];
  }
}

// ─── Paging ──────────────────────────────────────────────────────────
//
// The results table shows 25 rows a page, and each search call fetches one
// page (SEARCH_PAGE_SIZE) with a forward-only ``next_cursor``. Every page
// fetched is one search on metered plans. We buffer ("stash") every fetched
// page so going back makes NO network call and spends nothing; a new page is
// fetched only when the user pages past the buffer. Paging calls are also
// rate-limited (40/hour/org).
const PAGE_SIZE = SEARCH_PAGE_SIZE;

type IdentityExclusionAcknowledgement = {
  applied_identity_exclusion_token?: string;
  applied_identity_exclusions?: IdentityExclusionKind[] | null;
};

export const IDENTITY_EXCLUSION_ACKNOWLEDGEMENT_ERROR =
  'People search did not confirm that the selected exclusions were applied';

const IDENTITY_EXCLUSION_ERROR_MESSAGES: Record<string, string> = {
  identity_exclusions_disabled:
    'These exclusions are not available for this organization. Turn them off and try again.',
  identity_exclusion_session_missing:
    'We could not verify these exclusions right now. Try again.',
  cleanlist_exclusion_unavailable: `${BRAND.name} exclusions are temporarily unavailable. Try again or turn the filter off.`,
  cleanlist_exclusion_scope_changed: `The selected ${BRAND.name} lists changed. Review the list selection and try again.`,
  cleanlist_identity_candidates_unusable: `We could not safely match these results to ${BRAND.name}. Narrow the search or turn the ${BRAND.name} exclusion off.`,
  crm_identity_index_unavailable:
    'CRM exclusion data is temporarily unavailable. Try again.',
  invalid_crm_exclusion_ref:
    'A selected CRM exclusion is invalid. Remove it and try again.',
  crm_exclusion_scope_changed:
    'Your CRM connection changed. Try again to refresh its exclusion data.',
  crm_exclusion_generation_expired:
    'Your CRM exclusion data is out of date. Try again to refresh it.',
  crm_exclusion_generation_incompatible:
    'Your CRM exclusion settings changed. Try again to refresh them.',
  crm_owner_unresolved:
    'We could not match you to a CRM owner. Choose a specific owner or remove the owner filter.',
  crm_identity_candidates_unusable:
    'We could not safely match these results to your CRM. Narrow the search or remove the CRM exclusion.',
  instantly_unavailable:
    'Instantly could not be read right now. Try again or turn In Instantly off.',
  no_instantly_connection:
    'Instantly is not connected. Connect it in Integrations or turn In Instantly off.',
  too_many_records:
    'Your Instantly workspace has more than 15,000 leads, more than a search can check. Turn In Instantly off. An agent play trigger checks up to 18,000.',
  instantly_needs_agents:
    'In Instantly needs Agents on your plan. Turn it off to search.',
};

/** Convert stable identity-exclusion API codes into copy with a recovery step. */
export function identityExclusionErrorMessage(code: string): string | null {
  return IDENTITY_EXCLUSION_ERROR_MESSAGES[code] ?? null;
}

/** The recovery copy for a failed request whose ``detail`` is a known
 *  identity-exclusion code, else null. Paging and import fetches check this
 *  before their status handling, so a 403 ``instantly_needs_agents`` never
 *  reads as spent previews. */
function identityExclusionFailureMessage(err: unknown): string | null {
  const detail = (err as { data?: { detail?: unknown } } | null)?.data?.detail;
  return typeof detail === 'string'
    ? identityExclusionErrorMessage(detail)
    : null;
}

/** Refuse rows the server did not confirm it filtered. The token must echo,
 *  and every requested kind must be in ``applied_identity_exclusions``. A
 *  server that predates the kinds list sends none: it applied CRM and
 *  Cleanlist when it echoed the token, but it never applies Instantly, so an
 *  Instantly request needs the list. */
function requireIdentityExclusionsApplied(
  response: IdentityExclusionAcknowledgement,
  exclusions: PeopleIdentityExclusions | undefined
): void {
  if (!exclusions) return;
  if (response.applied_identity_exclusion_token !== exclusions.intent_token)
    throw new Error(IDENTITY_EXCLUSION_ACKNOWLEDGEMENT_ERROR);
  const requested: IdentityExclusionKind[] = [
    ...(exclusions.crm.length ? (['crm'] as const) : []),
    ...(exclusions.cleanlist ? (['cleanlist'] as const) : []),
    ...(exclusions.instantly ? (['instantly'] as const) : []),
  ];
  const applied = response.applied_identity_exclusions;
  if (applied == null) {
    if (exclusions.instantly)
      throw new Error(IDENTITY_EXCLUSION_ACKNOWLEDGEMENT_ERROR);
    return;
  }
  if (!Array.isArray(applied) || requested.some((k) => !applied.includes(k)))
    throw new Error(IDENTITY_EXCLUSION_ACKNOWLEDGEMENT_ERROR);
}

/** The filters as the exclusion cache discriminator. Inactive Instantly
 *  fields are left out: they send nothing, and a search cached before they
 *  existed must still hit once migration adds them as defaults. */
function exclusionsCacheValue(filters: SearchFilters): Partial<SearchFilters> {
  if (buildInstantlyExclusionReference(filters)) return filters;
  const rest: Partial<SearchFilters> = { ...filters };
  delete rest.excludeInInstantly;
  delete rest.excludeInInstantlyStatuses;
  delete rest.excludeInInstantlyBlocklist;
  return rest;
}

function identityExclusionsCacheValue(
  exclusions: PeopleIdentityExclusions
): Omit<PeopleIdentityExclusions, 'intent_token'> {
  return {
    crm: exclusions.crm,
    ...(exclusions.cleanlist ? { cleanlist: exclusions.cleanlist } : {}),
    ...(exclusions.instantly ? { instantly: exclusions.instantly } : {}),
  };
}

// ─── Hook ────────────────────────────────────────────────────────────

interface UsePeopleSearchOptions {
  linkedListId?: string;
  mcpHandoffBlocked?: boolean;
  suspendAutomaticSearch?: boolean;
  /** Current Clerk organization id. Used to scope sessionStorage cache
   *  + state keys so a tenant switch never surfaces a previous org's
   *  cached people results during the 5-minute TTL window. */
  orgId?: string | null;
  /** Shared search-quota instance, owned by the search container so the
   *  People and Companies tabs decrement the SAME optimistic counter.
   *  (Calling ``useSearchQuota()`` separately in each hook would give
   *  each its own optimistic state, so a Company search wouldn't update
   *  the People-tab badge until a /billing/plans refetch.) */
  searchQuota: SearchQuota;
}

export interface UsePeopleSearchReturn {
  restoreMcpSearch: (restoration: McpSearchRestoration | null) => void;
  clearMcpContext: () => void;
  filters: SearchFilters;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  /** Composite firmographic slice held alongside the PersonDB-native
   *  filters. Non-empty values cause handleSearch to route through
   *  /people-search/by-companies. */
  compositeCompanyFilters: CompanySearchFilters;
  setCompositeCompanyFilters: React.Dispatch<
    React.SetStateAction<CompanySearchFilters>
  >;
  results: PersonRecord[];
  /** Every row fetched so far across all blocks (uncapped). The results
   *  table resolves its cross-page row selection against this. */
  stash: PersonRecord[];
  total: number;
  /** Count semantics captured from the last successful search. */
  totalIsUpperBound: boolean;
  totalKnown: boolean;
  programCoverage?: StartupProgramCoverage;
  programWarning?: string;
  /** Set when the search asked each company separately (a per-company cap
   *  over several companies): companies matched, and which had nobody. */
  companyCoverage: CompanyCoverage | null;
  /** The search found nobody by exact name, so the rows are the closest
   *  names instead (backend match_mode "fuzzy_name"). */
  closestMatches: boolean;
  /** Instantly leads the last search couldn't check because they have no
   *  LinkedIn URL. Null before an In Instantly search reports it. */
  instantlyUnchecked: number | null;
  hasSearched: boolean;
  isSearching: boolean;
  /** True while a search that asks each company separately is running. A long
   *  company list takes several seconds, so the table says what it is doing. */
  searchingEachCompany: boolean;
  /** Bumped on every true new search (filter/sort change or clearAll), not
   *  on paging. The results table clears cross-page selection when it
   *  changes. */
  searchSessionId: number;
  /** 1-based page index the results table is currently displaying. */
  currentPage: number;
  /** True while the next block of results is being fetched. Drives the
   *  spinner on the "Next" control. */
  isPaging: boolean;
  /** Whether paging forward is possible (more buffered rows OR a cursor
   *  for the next block). When false AND on the last page, "Next" is
   *  disabled. */
  hasNextPage: boolean;
  /** Hourly paging limit hit: Next is locked until this time (epoch ms). */
  pagingLockedUntil: number | null;
  /** Advance one page. Serves from the stash when buffered; otherwise
   *  fetches the next block via the cursor (counts as 1 preview call). */
  goNextPage: () => void;
  /** Go back one page. Always served from the stash — never a call. */
  goPrevPage: () => void;
  /** Jump to a 1-based page WITHIN the fetched buffer. Clamped — never
   *  fetches (arbitrary-page fetch-forward is a deliberate non-feature:
   *  each block is a metered preview call). Returns the page landed on. */
  goToPage: (page: number) => number;
  /** Pages currently materialized in the buffer (capped stash / 25). */
  bufferedPageCount: number;
  /** True iff the last attempted search threw. Cleared when a new
   *  search dispatches or the user edits filters. Drives the
   *  results-panel error state + Retry CTA (round-1 reliability gap). */
  isError: boolean;
  /** Human-readable summary of the most recent error, when ``isError``
   *  is true. Defaults to "Search failed. Please try again." */
  errorMessage: string;
  /** Discriminator for the SearchErrorState variant. */
  errorKind: 'generic' | 'feature_gated';
  /** Re-run the last attempted search. Wired to the Retry button. */
  retrySearch: () => void;
  isImporting: boolean;
  /** Live progress of a chunked import (rows processed / total), or null when
   *  no import is running. Drives the "Importing N / M…" label. */
  importProgress: { done: number; total: number } | null;
  hasAnyFilter: boolean;
  activeFilters: ActiveFilter[];
  matchCriteria: EmployerMatchCriteria;
  /** The filters the current results were searched with (null before any
   *  search). Recent-search history records this, not the live sidebar. */
  searched: { filters: SearchFilters; composite: CompanySearchFilters } | null;
  linkedList: { id: string; name: string } | undefined;
  handleSearch: () => Promise<void>;
  /** Schedule a search to run after the next filter state update */
  scheduleSearch: () => void;
  /** The search has exclusions not resolved for these filters (rows
   *  restored from a cache, say): Show must stay clickable to resolve them
   *  before an import. */
  exclusionsUnresolved: boolean;
  /** Pre-load the people that should be imported when handleImportToList runs */
  setPendingImportPeople: (people: PersonRecord[]) => void;
  handleImportToList: (listId: string) => Promise<void>;
  /**
   * Import enrichment type threaded through the import flow.
   * Consumers that want enrichment should set this BEFORE calling
   * ``executeImport`` / ``handleImportToList``. The server-side
   * import endpoint scopes enrichment to the imported lead set —
   * calling the standalone bulkEnrichProspects endpoint afterward
   * with ``prospects: []`` enriches the entire list, which overshoots
   * badly on non-empty lists (CodeRabbit PR #286).
   */
  setPendingImportEnrichment: (
    enrichment: 'partial' | 'phone_only' | 'full' | 'none' | undefined
  ) => void;
  clearSort: () => void;
  sortColumn?: string;
  sortOrder: 'asc' | 'desc';
  handleSort: (column: string) => void;
  /**
   * Fires enrichment for a single row without requiring list selection —
   * resolves target list internally (linked list → protected Extension
   * Leads). The revealed email/phone appears inline via
   * ``revealedByPersonId`` when the backend finishes the task.
   */
  handleEnrichPerson: (person: PersonRecord, type: 'email' | 'phone') => void;
  enrichingPersonIds: Set<string>;
  enrichedPersonIds: Set<string>;
  /**
   * Map of personId → resolved contact values populated from the
   * enrichment websocket. Rendered inline in the results table so the
   * row updates without triggering a re-search.
   */
  revealedByPersonId: Map<string, { email?: string; phone?: string }>;
  /** Side-channel search used by Add-to-list drawer's count picker. */
  fetchPeople: (limit: number) => Promise<PersonRecord[] | null>;
  clearAll: () => void;
  removeFilter: (key: string, value: string) => void;
  importDialogOpen: boolean;
  setImportDialogOpen: (open: boolean) => void;
  confirmImportOpen: boolean;
  setConfirmImportOpen: (open: boolean) => void;
  pendingImportListId: string | null;
  executeImport: (
    listId: string,
    options?: {
      enrichListed?: boolean;
      listName?: string;
      /** The result screen shows the outcome: no success toast on top of
       *  it. Read when the import ends (the sheet may close meanwhile). */
      resultOnScreen?: () => boolean;
    }
  ) => Promise<ImportPeopleOutcome | null>;
  cancelPendingImport: () => void;
  /** Preview-quota state for free / AppSumo-starter users. Surfaced
   *  so the search container can render the onboarding card + sidebar
   *  badge. ``isLimited === false`` for paid + AppSumo-Pro tiers, in
   *  which case both UI surfaces render nothing. */
  searchQuota: {
    isLimited: boolean;
    remaining: number;
    max: number;
    isExhausted: boolean;
    isAppSumo: boolean;
  };
  /** CRM-exclusion freshness + degradation surface for the sidebar's
   *  CRM subsection (FE-E). Reflects the LAST search's exclusion-set
   *  resolution. */
  crmExclusionStatus: CrmExclusionStatus;
  /** Per-CRM freshness + degradation surface for the NEW per-CRM filter
   *  sections (CP-5b). Keyed by CRM so each section shows only its own
   *  status; a CRM absent from the map has no resolved status yet. */
  crmSectionStatus: Partial<Record<CrmFilterCrm, CrmExclusionStatus>>;
  /** Cleanlist-level dedup resolution surface (CP-8) — drives the subtle
   *  "showing first 50k — list is larger" note when the resolve was capped. */
  cleanlistExclusionStatus: CleanlistExclusionStatus;
  /** True when at least one connected sync CRM (Salesforce / HubSpot)
   *  exists — gates the CRM exclusion subsection. */
  hasConnectedCrm: boolean;
  /** Re-run the search forcing a fresh CRM exclusion-set fetch
   *  (force_refresh: true). Wired to the sidebar "refresh" affordance. */
  refreshCrmExclusions: () => void;
}

export interface ImportPeopleOutcome {
  imported_count: number;
  skipped_count: number;
  /** Leads this import added (absent when a batch is still running). */
  lead_ids?: string[];
  /** Requested people who were already on the list. */
  already_in_list_ids?: string[];
  /** A batch is still importing in the background. */
  pending?: boolean;
  /** What became of each requested person, from every finished batch;
   *  absent when the API predates it. */
  outcomes?: PersonImportOutcome[];
}

// ─── HTTP polling fallback ───────────────────────────────────────────
//
// The Temporal worker broadcasts terminal status via Redis pub/sub →
// WebSocket. That path works most of the time, but a handful of
// environments don't see the message: impersonated Clerk sessions
// where the broadcast user_id ≠ the connected socket's user_id,
// strict ad-blockers / corporate proxies that kill the WS, idle WS
// timeouts on cellular networks. Without a fallback the cell sits
// in the loading state forever.
//
// We poll the same Redis-backed task metadata that the WS message
// carries (``GET /enrichment/task/{task_id}/status``), then replay it
// through ``applyEnrichmentUpdate`` so the existing reveal / footer /
// toast logic fires identically whether the WS or this fallback won.

interface EnrichmentMatchSnapshot {
  personId: string;
  type: 'email' | 'phone';
  personName: string;
  operationId: string;
  listName: string;
}

interface PollTaskStatusArgs {
  taskId: string;
  match: EnrichmentMatchSnapshot;
  fetchTaskStatus: (
    arg: string,
    preferCacheValue?: boolean
  ) => {
    unwrap(): Promise<{
      status: string;
      result?: { email?: string; phone?: string };
      error?: string;
      failure_kind?: string | null;
    }>;
  };
  applyEnrichmentUpdate: (
    match: EnrichmentMatchSnapshot,
    msg: {
      task_id?: string;
      status?: string;
      result?: {
        email?: string;
        phone?: string;
        failure_kind?: string | null;
      };
      message?: string;
      failure_kind?: string | null;
    }
  ) => void;
  taskToPersonRef: React.MutableRefObject<Map<string, EnrichmentMatchSnapshot>>;
  /** Out of time with the task still unresolved. */
  onTimeout?: () => void;
  /** Aborted when the hook unmounts: the loop stops at its next wait. */
  signal?: AbortSignal;
}

// Polls every 4s for the first minute, then every 10s up to 5 min, then
// every 30s up to the 30 min ceiling, after which we stop polling (the WS
// handler can still take over if a late message arrives because
// ``taskToPersonRef`` stays populated until terminal status). The 5 min mark
// comes from prod ``enrichment_completed`` (14 days to 2026-10-15, reveals
// with at most 2 others by that user within ±5 min, so bulk runs drop out):
// email p95 104s / p99 161s, phone p99 181s, so 5 min covers ~99.8%. The
// rest wait on the same task at a slower pace, with the row still on
// "Revealing...", instead of handing the user a button to check again.
// HTTP polling is a fallback for the WebSocket — under normal
// conditions the WS handler will see the task_id terminal frame and
// remove it from ``taskToPersonRef``, which is the "another path
// already won" check inside the polling loop. With the previous
// values (first poll @ 2s, then every 4s) we'd fire one wasted HTTP
// call for every healthy WS-handled reveal — and at 10 concurrent
// reveals that's 10 polling loops × the WS-races-poll window.
//
// Round-2 performance audit: wait longer before the FIRST poll so the
// WS gets a fair chance to win. 8s is enough for the WS to deliver
// the terminal frame on a healthy session; if it doesn't, the polling
// loop still kicks in and resolves the row before the user notices.
const POLL_FIRST_DELAY_MS = 8000;
const POLL_INTERVAL_MS = 4000;
const POLL_SLOW_AFTER_MS = 60_000;
const POLL_SLOW_INTERVAL_MS = 10_000;
const POLL_KEEP_LOOKING_AFTER_MS = 300_000;
const POLL_KEEP_LOOKING_INTERVAL_MS = 30_000;
const POLL_MAX_DURATION_MS = 1_800_000;

function pollIntervalMs(elapsedMs: number): number {
  if (elapsedMs < POLL_SLOW_AFTER_MS) return POLL_INTERVAL_MS;
  if (elapsedMs < POLL_KEEP_LOOKING_AFTER_MS) return POLL_SLOW_INTERVAL_MS;
  return POLL_KEEP_LOOKING_INTERVAL_MS;
}

/** Resolves after ``ms``, or at once when ``signal`` aborts. */
function waitMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const onAbort = () => {
      clearTimeout(id);
      resolve();
    };
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function pollTaskStatusUntilDone({
  taskId,
  match,
  fetchTaskStatus,
  applyEnrichmentUpdate,
  taskToPersonRef,
  onTimeout,
  signal,
}: PollTaskStatusArgs): Promise<void> {
  const startedAt = Date.now();
  let delay = POLL_FIRST_DELAY_MS;
  while (Date.now() - startedAt < POLL_MAX_DURATION_MS) {
    await waitMs(delay, signal);
    if (signal?.aborted) return;
    delay = pollIntervalMs(Date.now() - startedAt);

    // WS already handled it — applyEnrichmentUpdate deletes the entry
    // on terminal status, so this is our cheap "another path won" check.
    if (!taskToPersonRef.current.has(taskId)) return;

    let status: string | undefined;
    let result: { email?: string; phone?: string } | undefined;
    let error: string | undefined;
    let failureClass: string | undefined;
    let failureKind: string | null | undefined;
    try {
      // preferCacheValue=false forces a fresh fetch; without it RTKQ
      // would return the previous status indefinitely from cache.
      const resp = await fetchTaskStatus(taskId, false).unwrap();
      status = resp.status;
      result = resp.result;
      error = resp.error;
      failureClass = (resp as { failure_class?: string }).failure_class;
      failureKind = resp.failure_kind;
    } catch {
      if (signal?.aborted) return;
      // 404 / network blip — keep polling, the worker may not have
      // written task metadata yet. The MAX_DURATION ceiling stops the
      // loop if the task genuinely never materializes.
      continue;
    }

    // Unmounted mid-request, or the WS settled it while we waited.
    if (signal?.aborted || !taskToPersonRef.current.has(taskId)) return;
    if (status === 'completed' || status === 'session_completed') {
      applyEnrichmentUpdate(match, {
        task_id: taskId,
        status: 'completed',
        result,
      });
      return;
    }
    if (status === 'failed') {
      applyEnrichmentUpdate(match, {
        task_id: taskId,
        status: 'failed',
        message: error,
        failure_kind: failureKind,
        // "no_data": every provider answered with nothing, a clean miss.
        result:
          failureClass === 'no_data' ? { failure_kind: 'miss' } : undefined,
      });
      return;
    }
    // 'processing' / 'queued' / 'unknown' → keep polling.
  }
  // Out of time with no verdict. Don't fake a failure: the worker may still
  // finish, and the task mapping stays so a late WS result lands. The caller
  // shows "Not found" so the row stops spinning (Replay: users re-clicking a
  // reveal spinning forever).
  if (taskToPersonRef.current.has(taskId)) onTimeout?.();
}

/** A failed async people search, as the inline call would have rejected. The
 *  job's 402/403 (search admission) arrives in a 200 poll, so base-query never
 *  saw it: open the paid-access prompt here, as it does for the inline call. */
export function failedPeopleSearchError(
  status: Pick<PeopleSearchResponse, 'error' | 'status_code'>,
  fallback: Error = new Error('Search failed. Please try again.')
): unknown {
  const detail = status.error;
  const notice = paidAccessNotice({ data: { detail } });
  if (notice) requestPaidAccess(notice);
  const message = typeof detail === 'string' ? detail : detail?.message;
  return typeof message === 'string'
    ? { status: status.status_code, data: { detail: message } }
    : fallback;
}

/** What the import toast says. "Added 0 (3 duplicates skipped)" reads as a
 *  near-miss, so an all-duplicate batch gets its own wording. */
export function importSummary(
  imported: number,
  skipped: number,
  listName: string
): string {
  if (imported === 0 && skipped > 0)
    return skipped === 1
      ? `That lead is already in "${listName}"`
      : `All ${skipped} are already in "${listName}"`;
  if (skipped > 0)
    return `Added ${imported} to "${listName}" (${skipped} duplicate${skipped === 1 ? '' : 's'} skipped)`;
  return `Added ${imported} to "${listName}"`;
}

const BULK_REVEAL_HINT_AFTER = 3;
const BULK_REVEAL_HINT_KEY = 'cleanlist:bulk-reveal-hint-shown';

function showBulkRevealHintOnce() {
  try {
    if (localStorage.getItem(BULK_REVEAL_HINT_KEY)) return;
    localStorage.setItem(BULK_REVEAL_HINT_KEY, '1');
  } catch {
    // Storage blocked: show it anyway, at most once per page load.
  }
  toast.info('Revealing one by one?', {
    description:
      'Tick the people you want (or the whole page), then Add to list with email or phone enrichment to reveal them all at once.',
    duration: 10000,
  });
}

export function usePeopleSearch({
  linkedListId,
  orgId,
  searchQuota,
  mcpHandoffBlocked = false,
  suspendAutomaticSearch = false,
}: UsePeopleSearchOptions): UsePeopleSearchReturn {
  const dispatch = useAppDispatch();
  const { openCreditGate } = useCreditGate();
  const posthog = usePostHog();
  const [programCoverage, setProgramCoverage] =
    useState<StartupProgramCoverage>();
  const [programWarning, setProgramWarning] = useState<string>();
  const [totalKnown, setTotalKnown] = useState(true);

  // Restore last search from sessionStorage on mount. ``orgId`` is part
  // of every cache key — re-keying when the user switches orgs gives us
  // a clean slate (previous org's results stay isolated in their own
  // namespace until TTL).
  const cachedState = useMemo(() => {
    const cached = loadSearchState('people', orgId);
    // Old persisted rows may have been fetched while an exclusion failed.
    // Restore the controls, but require a successful resolve before reusing rows.
    if (
      cached &&
      (hasDynamicPeopleExclusions(migrateSearchFilters(cached.filters)) ||
        hasStartupPrograms(cached.compositeCompanyFilters?.startupPrograms))
    ) {
      return {
        ...cached,
        results: [],
        total: 0,
        totalIsUpperBound: false,
        nextCursor: null,
        currentPage: 1,
      };
    }
    return cached;
  }, [orgId]);

  // Migration on load: pre-PRD cache blobs are missing the ~34 new
  // array/string fields we added. Without this, the first render
  // crashes when sidebar sections do ``filters.currentCompanyLinkedInUrls.length``
  // on undefined.
  const [filters, setFilters] = useState<SearchFilters>(() =>
    cachedState
      ? migrateSearchFilters(cachedState.filters as Record<string, unknown>)
      : INITIAL_FILTERS
  );
  const [mcpContext, setMcpContext] = useState<McpSearchContext | null>(null);
  const clearMcpContext = useCallback(() => {
    if (!mcpContext) return;
    setMcpContext(null);
    setSortColumn(undefined);
    setSortOrder('desc');
    searchPendingRef.current = false;
    searchGenerationRef.current++;
    lastRequestRef.current = null;
    nextCursorRef.current = null;
    setNextCursor(null);
  }, [mcpContext]);
  // Composite firmographic slice — held parallel to ``filters`` because
  // SearchFilters is PersonDB-native and these fields (revenue,
  // funding, growth %, IPO, etc.) live on CompanyDB. When any are set,
  // ``handleSearch`` routes through /people-search/by-companies. When
  // empty, the plain people-search endpoint is used. The slice
  // doubles as a regular CompanySearchFilters blob so we can reuse
  // ``buildCompanyFilters`` and ``detectCompositeRoute`` without
  // shape conversion.
  // Hydrate from cached state too so a page reload preserves the
  // firmographic slice that drove the previous search (round-2
  // reliability audit — this state used to vanish on refresh).
  const [compositeCompanyFilters, setCompositeCompanyFilters] =
    useState<CompanySearchFilters>(() => {
      const cachedComposite = cachedState?.compositeCompanyFilters;
      if (cachedComposite && typeof cachedComposite === 'object') {
        return {
          ...emptyCompanyFilters(),
          ...(cachedComposite as Partial<CompanySearchFilters>),
        };
      }
      return emptyCompanyFilters();
    });
  // Paging buffer. ``stash`` is every row fetched so far across blocks
  // (raw, uncapped); the displayed page is sliced from the capped view
  // below. Hydrated from the cached state so a reload restores the paged
  // position without re-billing Crustdata. ``nextCursor`` is the forward-
  // only cursor for the next block (null = no more rows).
  const [stash, setStash] = useState<PersonRecord[]>(() =>
    cachedState ? (cachedState.results as PersonRecord[]) : []
  );
  const stashRef = useRef(stash);
  stashRef.current = stash;
  const [nextCursor, setNextCursor] = useState<string | null>(
    () => cachedState?.nextCursor ?? null
  );
  // Mirror of ``nextCursor`` for the paging fetcher's closure, which is
  // memoized and would otherwise read a stale cursor.
  const nextCursorRef = useRef<string | null>(cachedState?.nextCursor ?? null);
  const [currentPage, setCurrentPage] = useState<number>(() =>
    restoredSearchPage('people', orgId, cachedState, PAGE_SIZE)
  );
  useEffect(() => {
    saveSearchPage('people', orgId, currentPage);
  }, [orgId, currentPage]);
  // True while a next-block fetch is in flight (drives the "Next" spinner).
  const [isPaging, setIsPaging] = useState(false);
  // Hit the hourly paging limit: Next stays locked until this time (epoch
  // ms), says when, and sends nothing meanwhile. Replay showed users
  // hammering Next against a toast that only said "Try again later".
  // Keyed by organization: the limit is per org, so another workspace's
  // deadline never blocks this one, while within one org it outlives new
  // searches (a new search doesn't reset the API's hourly budget).
  const [pagingLock, setPagingLock] = useState<{
    orgId: string | null | undefined;
    until: number;
  } | null>(null);
  const pagingLockedUntil =
    pagingLock && pagingLock.orgId === orgId ? pagingLock.until : null;
  useEffect(() => {
    if (pagingLock === null) return;
    const id = window.setTimeout(
      () => setPagingLock(null),
      Math.max(0, pagingLock.until - Date.now())
    );
    return () => window.clearTimeout(id);
  }, [pagingLock]);
  // Monotonic id bumped on every true new search (filter/sort change or
  // clearAll) — NOT on paging. The results table watches it to clear its
  // cross-page row selection, so selection survives paging but resets when
  // the search itself changes.
  const [searchSessionId, setSearchSessionId] = useState(0);
  const [total, setTotal] = useState(() => cachedState?.total ?? 0);
  const [totalIsUpperBound, setTotalIsUpperBound] = useState(
    () => cachedState?.totalIsUpperBound ?? false
  );
  const [hasSearched, setHasSearched] = useState(
    () => !!cachedState?.results?.length
  );

  const matchCriteria: EmployerMatchCriteria = useMemo(
    () => ({
      titles: filters.jobTitlesInclude,
      headcountRanges: filters.companySizes,
      locations: [...filters.contactCountries, ...filters.companyCountries],
    }),
    [
      filters.jobTitlesInclude,
      filters.companySizes,
      filters.contactCountries,
      filters.companyCountries,
    ]
  );

  // The cap that produced the CURRENT stash, captured when the search ran
  // rather than read from the live input. Typing a new number must not
  // re-cap rows fetched under the old one -- that is what made results
  // change before the search was even run. The server applies the cap
  // authoritatively (see limitPerCompanyParam); this client pass only keeps
  // the accumulated stash consistent across separately requested paging
  // blocks, whose per-request counts reset on the server.
  const [appliedLimitPerCompany, setAppliedLimitPerCompany] = useState(() => {
    // After a reload the stash comes back from session storage; restore the
    // cap it was searched with too, or the header falls back to the uncapped
    // total while the table stays capped.
    if (!cachedState) return '';
    const cap = Number.parseInt(filters.limitPerCompany, 10);
    return Number.isFinite(cap) && cap > 0 ? String(cap) : '';
  });
  const [companyCoverage, setCompanyCoverage] =
    useState<CompanyCoverage | null>(
      () => cachedState?.companyCoverage ?? null
    );
  const [closestMatches, setClosestMatches] = useState(
    () => cachedState?.closestMatches ?? false
  );
  // Instantly leads the last search couldn't check (no LinkedIn URL).
  const [instantlyUnchecked, setInstantlyUnchecked] = useState<number | null>(
    null
  );
  const [searchingEachCompany, setSearchingEachCompany] = useState(false);

  // The filters the CURRENT results were searched with. Everything the table
  // shows follows the last search, not the live sidebar: adding a title,
  // size or country chip used to re-map the visible rows (shown title,
  // matched employer, which rows survive the cap) immediately, so a filter
  // looked applied before anyone pressed Search.
  const [searched, setSearched] = useState<{
    filters: SearchFilters;
    composite: CompanySearchFilters;
  } | null>(() =>
    cachedState ? { filters, composite: compositeCompanyFilters } : null
  );
  const searchedMatchCriteria = useMemo<EmployerMatchCriteria | null>(
    () =>
      searched
        ? {
            titles: searched.filters.jobTitlesInclude,
            headcountRanges: searched.filters.companySizes,
            locations: [
              ...searched.filters.contactCountries,
              ...searched.filters.companyCountries,
            ],
          }
        : null,
    [searched]
  );
  // Import paths keep reading the live matchCriteria, as they always have.
  const appliedMatchCriteria = searchedMatchCriteria ?? matchCriteria;

  // Per-company cap applied across the WHOLE accumulated stash — mirrors
  // the pre-paging behavior that capped the single ~100-row preview.
  const cappedStash = useMemo(
    () =>
      capResultsPerCompany(stash, appliedLimitPerCompany, appliedMatchCriteria),
    [stash, appliedLimitPerCompany, appliedMatchCriteria]
  );
  // The (up to) 25-row window the table actually renders.
  const results = useMemo(
    () =>
      cappedStash.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [cappedStash, currentPage]
  );
  // Built request from the last dispatched search — reused verbatim for
  // block fetches so the cursor is threaded against byte-identical
  // filters/sort (the backend rejects a cursor paired with changed
  // filters). Cleared whenever filters/sort change.
  const lastRequestRef = useRef<{
    startupPrograms?: StartupProgramFilter;
    limitPerCompany?: number;
    apiFilters: ReturnType<typeof buildCrustdataFilters>;
    route: ReturnType<typeof detectCompositeRoute>;
    sortsParam: { column: string; order: 'asc' | 'desc' }[] | undefined;
    identityExclusions: PeopleIdentityExclusions | undefined;
    limit: number;
  } | null>(null);
  // Last-search error envelope. Cleared on every new dispatch + on
  // filter edits — kept here in the hook (not Redux) since it's local
  // to one search lifecycle.
  // ``kind`` discriminator lets the results panel pick the right
  // SearchErrorState variant:
  //   - 'generic'        → red triangle + Try again button (network /
  //                        upstream / validation failures)
  //   - 'feature_gated'  → blue sparkles + Upgrade your plan → /plans
  //                        (preview quota exhausted; backend rejected
  //                        with feature_gated; or pre-flight exhausted
  //                        check fired below).
  const [errorState, setErrorState] = useState<{
    isError: boolean;
    message: string;
    kind: 'generic' | 'feature_gated';
  }>({ isError: false, message: '', kind: 'generic' });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [pendingImportListId, setPendingImportListId] = useState<string | null>(
    null
  );
  const importInProgressRef = useRef(false);
  // People selected when the user clicks "Add to list". Set by the view
  // before invoking handleImportToList; consumed by executeImport.
  const pendingImportPeopleRef = useRef<PersonRecord[]>([]);
  const setPendingImportPeople = useCallback((people: PersonRecord[]) => {
    pendingImportPeopleRef.current = people;
  }, []);
  // Import-time enrichment (partial / phone_only / full). Set BEFORE
  // executeImport so /people-search/import carries it through and the
  // backend scopes enrichment to just the freshly-imported leads.
  // Using the separate bulkEnrichProspects endpoint with prospects=[]
  // triggers enrichment on the whole list, which re-enriches existing
  // leads and overshoots credits badly (CodeRabbit PR #286).
  const pendingImportEnrichmentRef = useRef<
    'partial' | 'phone_only' | 'full' | 'none' | undefined
  >(undefined);
  // Generation counter for in-flight search dispatches. Incremented on
  // every handleSearch entry; if a slower response lands while a newer
  // dispatch is already in flight we drop the stale write (cache +
  // state) and skip setResults. Mirrors AbortController semantics for
  // an RTK Query mutation pipeline that can't be cleanly cancelled.
  // Round-2 reliability audit S2.
  const searchGenerationRef = useRef(0);
  const mcpBlockedRef = useRef(mcpHandoffBlocked);
  useLayoutEffect(() => {
    mcpBlockedRef.current = mcpHandoffBlocked;
  }, [mcpHandoffBlocked]);
  const currentOrganizationRef = useRef(orgId);
  // Keep the org ref current AFTER commit rather than assigning during render:
  // an in-flight search compares this ref against its captured ``orgId`` to drop
  // stale writes on a tenant switch, and every reader (handleSearch,
  // fetchNextBlock, fetchPeople, the auto-search effect) runs post-commit.
  useEffect(() => {
    currentOrganizationRef.current = orgId;
  }, [orgId]);
  // The filters whose exclusions are resolved for the rows on screen. Rows
  // restored from this session with only static exclusions (company
  // domains, profile URLs, title excludes) were fetched with them compiled
  // into the query, so they are resolved with no network. Restored rows
  // with dynamic exclusions are dropped above and need a search.
  const resolvedExclusionFiltersRef = useRef<SearchFilters | null>(
    cachedState?.results?.length && !hasDynamicPeopleExclusions(filters)
      ? filters
      : null
  );
  const setPendingImportEnrichment = useCallback(
    (enrichment: 'partial' | 'phone_only' | 'full' | 'none' | undefined) => {
      pendingImportEnrichmentRef.current = enrichment;
    },
    []
  );
  const searchPendingRef = useRef(false);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Lead lists (used for linked-list lookup + import toast list name, and the
  // CP-8 Cleanlist-dedup list picker). ``isSuccess``/``isError`` drive the B1
  // readiness gate below so a hydrated search waits for lists to settle before
  // resolving the Cleanlist exclusion.
  const {
    data: allLists,
    isSuccess: leadListsLoaded,
    isError: leadListsErrored,
  } = useGetLeadListsQuery();

  // Preview-quota counter for free / AppSumo-starter users. Returns
  // ``isLimited: false`` for paid / AppSumo-Pro tiers, so the
  // increment + UI surfaces become no-ops for fully-entitled users.
  // Increment fires below in handleSearch only on a NETWORK success
  // (not cache hits) so the counter aligns with eventual server-side
  // accounting. ``incrementSearchQuota`` is pulled out as a stable
  // reference so handleSearch's useCallback deps stay clean — listing
  // the whole ``searchQuota`` object would cause handleSearch to re-
  // bind every render (the object identity churns even though the
  // function inside doesn't).
  const incrementSearchQuota = searchQuota.increment;
  const linkedList = useMemo(
    () =>
      linkedListId ? allLists?.find((l) => l.id === linkedListId) : undefined,
    [linkedListId, allLists]
  );

  const [triggerSearch] = usePeopleSearchMutation();
  // Separate trigger instances for block ("fetch more") paging so their
  // in-flight state doesn't flip the main ``isSearching`` flag (which
  // would flash the search button spinner / skeleton rows on every
  // Next click). Paging surfaces its own ``isPaging`` instead.
  const [triggerSearchPaging] = usePeopleSearchMutation();
  // Import-only "fetch more" mirrors of the two search endpoints. The
  // Add-to-list drawer pulls its expanded set through these so the
  // import path stays separated from the on-screen search preview.
  const [triggerImportFetch] = usePeopleImportFetchMutation();
  const [triggerCompositeImportFetch] =
    usePeopleAtCompaniesImportFetchMutation();
  // Each import chunk runs as a background job the client polls (via
  // /import/status) instead of blocking on one long POST. This keeps large /
  // enriched imports off the request's critical path so they can't hit the
  // gateway timeout (surfaced as a CORS error).
  const [triggerImport, { isLoading: isImporting }] = useImportPeopleMutation();
  const [triggerImportStatus] = useLazyGetPeopleImportStatusQuery();
  // The composite "people at these companies" search runs as a background job
  // the client polls (start → poll) instead of one blocking POST — one job
  // returns one cursor block, so paging semantics are unchanged.
  const [companiesFindStart] = useCompaniesFindStartMutation();
  const [companiesFindStatus] = useLazyGetCompaniesFindStatusQuery();
  const pollCompaniesFind = useCallback(
    async (
      payload: CompositePeopleSearchRequest
    ): Promise<CompaniesFindStatusResponse> => {
      const started = await companiesFindStart(payload).unwrap();
      const INTERVAL_MS = 800;
      const MAX_ATTEMPTS = 120; // ~1.5 min ceiling before we surface a retry
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const status = await companiesFindStatus(started.task_id).unwrap();
        if (status.status === 'completed') return status;
        if (status.status === 'failed')
          throw failedPeopleSearchError(
            status,
            payload.startup_programs && status.error_message
              ? new ProgramSearchError(status.error_message)
              : undefined
          );
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }
      throw new Error('Search timed out. Please try again.');
    },
    [companiesFindStart, companiesFindStatus]
  );
  // The plain people search runs as a background job the client polls (start →
  // poll) instead of a blocking POST — one job per cursor block, so paging is
  // unchanged.
  const [peopleSearchStatus] = useLazyGetPeopleSearchStatusQuery();
  const pollPeopleSearch = useCallback(
    async (
      startTrigger: typeof triggerSearch,
      payload: PeopleSearchRequest
    ): Promise<PeopleSearchResponse> => {
      // Same endpoint as the sync path (async_search:true → returns a task_id);
      // startTrigger is triggerSearch (fresh) or triggerSearchPaging (block) so
      // the existing isSearching-vs-isPaging loading split is preserved.
      const started = await startTrigger({
        ...payload,
        async_search: true,
      }).unwrap();
      if (!started.task_id) return started; // the API answered inline
      const INTERVAL_MS = 800;
      const MAX_ATTEMPTS = 120; // ~1.5 min ceiling before we surface a retry
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const status = await peopleSearchStatus(started.task_id).unwrap();
        if (status.status === 'completed') return status;
        if (status.status === 'failed') throw failedPeopleSearchError(status);
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }
      throw new Error('Search timed out. Please try again.');
    },
    [peopleSearchStatus]
  );

  // ── CRM exclusion sets (FE-E) ──
  // The active CRM is the first connected sync CRM (Salesforce ranks
  // before HubSpot in useConnectedSyncCrms). ``hasConnectedCrm`` gates the
  // sidebar subsection; ``triggerExclusionSet`` resolves each enabled
  // condition's identifier set before the search fires.
  const connectedCrms = useConnectedSyncCrms();
  const activeCrm = mcpContext
    ? mcpContext.crm
    : (connectedCrms[0]?.type ?? null);
  const hasConnectedCrm = connectedCrms.length > 0;
  // Owner-id → name maps per CRM, so ``owner:<id>`` section chips render the
  // owner's name (CP-6). The CRM-users query is the same cached entry the
  // owner picker reads (one fetch, shared). Skip when that CRM isn't
  // connected. There are only ever two sync CRMs, so two fixed hooks keep
  // the Rules-of-Hooks contract (no conditional hook calls).
  const sfConnected = connectedCrms.some((c) => c.type === 'salesforce');
  const hsConnected = connectedCrms.some((c) => c.type === 'hubspot');
  const activeOrganizationId = orgId ?? '';
  const { currentData: sfCrmUsers } = useGetCrmUsersQuery(
    { crmType: 'salesforce', organizationId: activeOrganizationId },
    { skip: !sfConnected || !activeOrganizationId }
  );
  const { currentData: hsCrmUsers } = useGetCrmUsersQuery(
    { crmType: 'hubspot', organizationId: activeOrganizationId },
    { skip: !hsConnected || !activeOrganizationId }
  );
  // The CRM's own name for each field a ``field:`` clause reads (CLE-550).
  const crmFieldLabels = useCrmFieldLabels(
    filters.crmFilterSections,
    activeOrganizationId
  );
  const crmChipCtx = useMemo<CrmFilterChipContext>(() => {
    const toMap = (users: typeof sfCrmUsers): Record<string, string> => {
      const m: Record<string, string> = {};
      for (const u of users?.users ?? []) {
        // Prefer name; fall back to email so an unnamed owner still reads
        // better than the opaque CRM id.
        m[u.crm_user_id] = u.name || u.email || u.crm_user_id;
      }
      return m;
    };
    return {
      ownerNames: { salesforce: toMap(sfCrmUsers), hubspot: toMap(hsCrmUsers) },
      fieldLabels: crmFieldLabels,
    };
  }, [sfCrmUsers, hsCrmUsers, crmFieldLabels]);
  // True once /crm/options has settled, so activeCrm is known. Used to hold a
  // hydrated/scheduled search until the CRM connection set is resolved when a
  // CRM exclusion is enabled — otherwise the first search fires with
  // activeCrm=null and silently drops the exclusion (B1).
  const crmReady = useCrmConnectionsReady();
  const [triggerExclusionSet] = useGetExclusionSetMutation();
  // Per-search CRM-exclusion resolution summary (freshness / degradation).
  const [crmExclusionStatus, setCrmExclusionStatus] =
    useState<CrmExclusionStatus>(EMPTY_CRM_EXCLUSION_STATUS);
  // Per-CRM resolution summary for the NEW filter sections (CP-5b). Kept
  // separate from the flat status above so the sidebar identifies which
  // selected CRM filter failed.
  const [crmSectionStatus, setCrmSectionStatus] = useState<
    Partial<Record<CrmFilterCrm, CrmExclusionStatus>>
  >({});
  // One-shot flag: the next handleSearch forces a fresh exclusion-set
  // fetch (force_refresh: true). Set by refreshCrmExclusions, consumed +
  // reset inside handleSearch.
  const forceRefreshCrmRef = useRef(false);

  // ── Cleanlist-level dedup exclusion (CP-8) ──
  // Sends the org's existing-lead scope (scope 'any' = all org leads; 'list'
  // = the picked list ids) as an identity reference the server resolves. A
  // 'list' scope with no lists blocks dispatch.
  const [cleanlistExclusionStatus, setCleanlistExclusionStatus] =
    useState<CleanlistExclusionStatus>(EMPTY_CLEANLIST_EXCLUSION_STATUS);
  // B1 readiness for the Cleanlist resolve: a URL-hydrated / scheduled search
  // must wait until the org's lead-lists query has SETTLED before resolving,
  // so a 'list'-scope dedup whose ids reference not-yet-loaded lists (and the
  // 'any' scope alike) actually resolves on the FIRST search instead of being
  // silently dropped. Mirrors ``crmReady``. Once the lists request settles,
  // the identifier resolver determines whether the search can proceed.
  const leadListsReady = leadListsLoaded || leadListsErrored;

  const [isSearching, setIsSearching] = useState(false);
  // Either slice qualifies — a pure firmographic query (composite only)
  // is still a real filter. ``hasAnyCompanyFilter`` accepts the
  // CompanySearchFilters shape that ``compositeCompanyFilters`` uses.
  const hasAnyFilter = useMemo(
    () =>
      hasAnyFilterValues(filters) ||
      hasAnyCompanyFilter(compositeCompanyFilters),
    [filters, compositeCompanyFilters]
  );
  const activeFilters = useMemo(
    () =>
      withInstantlyUnchecked(
        getActivePeopleFilters(filters, compositeCompanyFilters, crmChipCtx),
        instantlyUnchecked
      ),
    [filters, compositeCompanyFilters, crmChipCtx, instantlyUnchecked]
  );
  const hasAnyCrmExclusionEnabled = useMemo(
    () =>
      getActiveCrmExclusions(filters).length > 0 ||
      getActiveCrmSectionFilters(filters).length > 0,
    [filters]
  );

  // A selected condition must resolve completely before search or import.
  const resolveCrmExclusions = useCallback(
    async (
      forceRefresh: boolean
    ): Promise<{
      conditions: Record<string, unknown>[];
      references: CrmExclusionReference[];
      status: CrmExclusionStatus;
    }> => {
      const active = getActiveCrmExclusions(filters);
      if (!active.length) {
        return {
          conditions: [],
          references: [],
          status: EMPTY_CRM_EXCLUSION_STATUS,
        };
      }
      if (!activeCrm || !activeOrganizationId) {
        throw new FilterCompileError(
          'excludeCrmCustomers',
          'CRM exclusions',
          'Connect your CRM before using this filter'
        );
      }

      const settled = await Promise.allSettled(
        active.map((ex) =>
          triggerExclusionSet({
            organizationId: activeOrganizationId,
            crm: activeCrm,
            condition: ex.condition,
            ...(forceRefresh ? { force_refresh: true } : {}),
            supports_indexed: true,
          })
            .unwrap()
            .then((res) => ({ ex, res }))
        )
      );

      const payloads: CrmExclusionSetResponse['identifiers'][] = [];
      const references: CrmExclusionReference[] = [];
      const erroredKeys: string[] = [];
      let fetchedAt: string | null = null;
      let truncated = false;
      let partial = false;
      let stale = false;
      let reason: CrmExclusionReason | null = null;
      let recordCount = 0;

      for (let i = 0; i < settled.length; i++) {
        const outcome = settled[i];
        if (outcome.status === 'fulfilled') {
          const { res } = outcome.value;
          if (res.mode === 'indexed') {
            if (!res.reference) {
              erroredKeys.push(active[i].key as string);
              continue;
            }
            references.push(res.reference);
          } else {
            payloads.push(res.identifiers ?? emptyCrmIdentifiers());
            // Missing identifiers only degrade the INLINE payload — an indexed
            // response carries a ``reference`` instead and is complete without
            // them, so folding this into the generic flag would mark every
            // indexed exclusion partial.
            if (!res.identifiers) partial = true;
          }
          truncated = truncated || res.truncated;
          partial = partial || res.partial;
          stale = stale || res.stale;
          recordCount += res.record_count ?? 0;
          if (res.reason && !reason) reason = res.reason;
          if (res.fetched_at && (!fetchedAt || res.fetched_at > fetchedAt)) {
            fetchedAt = res.fetched_at;
          }
        } else {
          erroredKeys.push(active[i].key as string);
        }
      }

      const merged = mergeCrmIdentifiers(payloads);
      return {
        conditions: buildCrmExcludeConditions(merged),
        references,
        status: {
          fetchedAt,
          truncated,
          partial,
          stale,
          reason,
          recordCount,
          isFetching: false,
          erroredKeys,
        },
      };
    },
    [filters, activeCrm, activeOrganizationId, triggerExclusionSet]
  );

  // Each section resolves against its own CRM; all selected clauses must succeed.
  const resolveCrmSectionFilters = useCallback(
    async (
      forceRefresh: boolean
    ): Promise<{
      conditions: Record<string, unknown>[];
      references: CrmExclusionReference[];
      statusByCrm: Partial<Record<CrmFilterCrm, CrmExclusionStatus>>;
      /** The server's words for a refused ``field:`` clause, if one was. */
      refusal: string | null;
    }> => {
      const active = getActiveCrmSectionFilters(filters);
      if (!active.length || !activeOrganizationId) {
        return {
          conditions: [],
          references: [],
          statusByCrm: {},
          refusal: null,
        };
      }

      const settled = await Promise.allSettled(
        active.map((clause) =>
          triggerExclusionSet({
            organizationId: activeOrganizationId,
            crm: clause.crm as CrmExclusionCrm,
            condition: clause.condition,
            ...(forceRefresh ? { force_refresh: true } : {}),
            supports_indexed: true,
          })
            .unwrap()
            .then((res) => ({ clause, res }))
        )
      );

      // Identifiers union across ALL CRMs (anyone matching ANY enabled
      // clause is suppressed). Status is accumulated separately per CRM.
      const allPayloads: CrmExclusionSetResponse['identifiers'][] = [];
      const references: CrmExclusionReference[] = [];
      let refusal: string | null = null;
      const perCrm = new Map<
        CrmFilterCrm,
        {
          fetchedAt: string | null;
          truncated: boolean;
          partial: boolean;
          stale: boolean;
          reason: CrmExclusionReason | null;
          recordCount: number;
          erroredKeys: string[];
        }
      >();
      const bucketFor = (crm: CrmFilterCrm) => {
        let b = perCrm.get(crm);
        if (!b) {
          b = {
            fetchedAt: null,
            truncated: false,
            partial: false,
            stale: false,
            reason: null,
            recordCount: 0,
            erroredKeys: [],
          };
          perCrm.set(crm, b);
        }
        return b;
      };

      for (let i = 0; i < settled.length; i++) {
        const outcome = settled[i];
        const clause = active[i];
        const b = bucketFor(clause.crm);
        if (outcome.status === 'fulfilled') {
          const { res } = outcome.value;
          if (res.mode === 'indexed') {
            if (!res.reference) {
              b.erroredKeys.push(clause.id);
              continue;
            }
            references.push(res.reference);
          } else {
            allPayloads.push(res.identifiers ?? emptyCrmIdentifiers());
            // Inline-only: an indexed response is complete via its reference.
            if (!res.identifiers) b.partial = true;
          }
          b.truncated = b.truncated || res.truncated;
          b.partial = b.partial || res.partial;
          b.stale = b.stale || res.stale;
          b.recordCount += res.record_count ?? 0;
          if (res.reason && !b.reason) b.reason = res.reason;
          if (
            res.fetched_at &&
            (!b.fetchedAt || res.fetched_at > b.fetchedAt)
          ) {
            b.fetchedAt = res.fetched_at;
          }
        } else {
          b.erroredKeys.push(clause.id);
          if (clause.condition.startsWith(FIELD_PREFIX))
            refusal ??= crmFieldRefusal(outcome.reason);
        }
      }

      const statusByCrm: Partial<Record<CrmFilterCrm, CrmExclusionStatus>> = {};
      for (const [crm, b] of perCrm) {
        statusByCrm[crm] = {
          fetchedAt: b.fetchedAt,
          truncated: b.truncated,
          partial: b.partial,
          stale: b.stale,
          reason: b.reason,
          recordCount: b.recordCount,
          isFetching: false,
          erroredKeys: b.erroredKeys,
        };
      }

      const merged = mergeCrmIdentifiers(allPayloads);
      return {
        conditions: buildCrmExcludeConditions(merged),
        references,
        statusByCrm,
        refusal,
      };
    },
    [filters, activeOrganizationId, triggerExclusionSet]
  );

  // Share resolution across preview and expanded import fetches. A stale
  // request returns null before it can write status, cache, or provider results.
  const resolveSelectedExclusions = useCallback(
    async (forceRefresh: boolean, generation: number) => {
      const isCurrent = () =>
        generation === searchGenerationRef.current &&
        currentOrganizationRef.current === orgId;
      if (!isCurrent()) return null;
      validatePeopleExclusions(filters);
      if (hasDynamicPeopleExclusions(filters) && !activeOrganizationId) {
        throw new FilterCompileError(
          'crmFilterSections',
          'Exclusions',
          'Select an organization before searching'
        );
      }
      const flat = getActiveCrmExclusions(filters);
      const sections = getActiveCrmSectionFilters(filters);
      if ((flat.length || sections.length) && !crmReady) {
        throw new FilterCompileError(
          'crmFilterSections',
          'CRM exclusions',
          'Wait for your CRM connections to load'
        );
      }
      const conditions: Record<string, unknown>[] = [];
      const crmReferences: CrmExclusionReference[] = [];
      if (flat.length) {
        setCrmExclusionStatus({
          ...EMPTY_CRM_EXCLUSION_STATUS,
          isFetching: true,
        });
        const resolved = await resolveCrmExclusions(forceRefresh);
        if (!isCurrent()) return null;
        setCrmExclusionStatus(resolved.status);
        requireCompleteCrmExclusions(resolved.status);
        conditions.push(...resolved.conditions);
        crmReferences.push(...resolved.references);
      } else {
        setCrmExclusionStatus(EMPTY_CRM_EXCLUSION_STATUS);
      }
      if (sections.length) {
        setCrmSectionStatus(
          Object.fromEntries(
            sections.map((c) => [
              c.crm,
              { ...EMPTY_CRM_EXCLUSION_STATUS, isFetching: true },
            ])
          )
        );
        const resolved = await resolveCrmSectionFilters(forceRefresh);
        if (!isCurrent()) return null;
        setCrmSectionStatus(resolved.statusByCrm);
        // A field clause the CRM can't run: the server says why (no such
        // field, an operator its type doesn't take, a value that isn't one).
        if (resolved.refusal)
          throw new FilterCompileError(
            'crmFilterSections',
            'CRM exclusions',
            resolved.refusal
          );
        Object.values(resolved.statusByCrm).forEach(
          requireCompleteCrmExclusions
        );
        conditions.push(...resolved.conditions);
        crmReferences.push(...resolved.references);
      } else {
        setCrmSectionStatus({});
      }
      let cleanlistReference: PeopleIdentityExclusions['cleanlist'];
      if (hasCleanlistExclusion(filters)) {
        setCleanlistExclusionStatus({
          ...EMPTY_CLEANLIST_EXCLUSION_STATUS,
          isFetching: true,
        });
        try {
          const body = buildCleanlistExclusionRequest(filters);
          if (!body) throw new Error('No lists selected');
          cleanlistReference = body;
          setCleanlistExclusionStatus({
            resolved: true,
            truncated: false,
            isFetching: false,
          });
        } catch {
          if (!isCurrent()) return null;
          const message = `The selected ${BRAND.name} exclusions could not be fully loaded. Retry or turn the filter off`;
          setCleanlistExclusionStatus((prev) => ({
            ...prev,
            resolved: false,
            isFetching: false,
            error: message,
          }));
          throw new FilterCompileError(
            'excludeInCleanlist',
            `${BRAND.name} exclusions`,
            message
          );
        }
      } else {
        setCleanlistExclusionStatus(EMPTY_CLEANLIST_EXCLUSION_STATUS);
      }
      const urls = expandLinkedInExcludeVariants(
        mergeCleanlistLinkedInUrls(filters.excludeProfileUrls, [])
      );
      if (urls.length)
        conditions.push({
          column: 'flagship_profile_url',
          type: 'not_in',
          value: urls,
        });
      const domains = normalizeDomainList(filters.excludeCompanyDomains);
      if (domains.length)
        conditions.push({
          column: 'current_employers.company_website_domain',
          type: 'not_in',
          value: domains,
        });
      // Instantly is read on the server, so the reference alone carries it.
      const instantlyReference = buildInstantlyExclusionReference(filters);
      const identityExclusions =
        crmReferences.length || cleanlistReference || instantlyReference
          ? {
              intent_token: uuidv4(),
              crm: crmReferences,
              ...(cleanlistReference ? { cleanlist: cleanlistReference } : {}),
              ...(instantlyReference ? { instantly: instantlyReference } : {}),
            }
          : undefined;
      return { conditions, identityExclusions };
    },
    [
      filters,
      orgId,
      activeOrganizationId,
      crmReady,
      resolveCrmExclusions,
      resolveCrmSectionFilters,
    ]
  );

  // Hydrate the reveal map from server-provided enrichment values for a
  // batch of rows. The backend backfills ``email`` / ``phone`` for any
  // result the *current user* previously enriched, so a row the user
  // already paid credits on renders inline instead of an "Enrich" button.
  // Shared by the initial search and every paged-in block.
  const hydrateRevealsFromResults = useCallback((rows: PersonRecord[]) => {
    setRevealedByPersonId((prev) => {
      let next: Map<string, { email?: string; phone?: string }> | null = null;
      for (const r of rows) {
        const pid = r.id;
        if (!pid) continue;
        const hasEmail = typeof r.email === 'string' && r.email.length > 0;
        const hasPhone = typeof r.phone === 'string' && r.phone.length > 0;
        if (!hasEmail && !hasPhone) continue;
        const existing = prev.get(pid);
        // Don't clobber values the user just revealed in this session
        // with a possibly-older server snapshot.
        const merged = {
          email: existing?.email || (hasEmail ? r.email : undefined),
          phone: existing?.phone || (hasPhone ? r.phone : undefined),
        };
        if (
          existing?.email === merged.email &&
          existing?.phone === merged.phone
        ) {
          continue;
        }
        if (next === null) next = new Map(prev);
        next.set(pid, merged);
      }
      return next ?? prev;
    });
  }, []);

  const clearAll = useCallback(() => {
    searchGenerationRef.current++;
    searchPendingRef.current = false;
    forceRefreshCrmRef.current = false;
    resolvedExclusionFiltersRef.current = null;
    setFilters(INITIAL_FILTERS);
    setCompositeCompanyFilters(emptyCompanyFilters());
    setStash([]);
    setAppliedLimitPerCompany('');
    setSearched(null);
    setNextCursor(null);
    nextCursorRef.current = null;
    setCurrentPage(1);
    lastRequestRef.current = null;
    setTotal(0);
    setTotalIsUpperBound(false);
    setTotalKnown(true);
    setProgramCoverage(undefined);
    setProgramWarning(undefined);
    setCompanyCoverage(null);
    setInstantlyUnchecked(null);
    setClosestMatches(false);
    setHasSearched(false);
    setIsSearching(false);
    setIsPaging(false);
    pendingImportPeopleRef.current = [];
    setCrmExclusionStatus(EMPTY_CRM_EXCLUSION_STATUS);
    setCrmSectionStatus({});
    setCleanlistExclusionStatus(EMPTY_CLEANLIST_EXCLUSION_STATUS);
    setErrorState({ isError: false, message: '', kind: 'generic' });
    setSearchSessionId((n) => n + 1);
    clearSearchState('people', orgId);
  }, [orgId]);

  const restoreMcpSearch = useCallback(
    (restoration: McpSearchRestoration | null) => {
      clearAll();
      setSortColumn(restoration?.context.sort ?? undefined);
      setSortOrder('desc');
      pendingImportEnrichmentRef.current = undefined;
      setPendingImportListId(null);
      setImportDialogOpen(false);
      setConfirmImportOpen(false);
      setMcpContext(restoration?.context ?? null);
      if (restoration) {
        setFilters(restoration.people);
        setAppliedLimitPerCompany('');
        setSearched(null);
        setCompanyCoverage(null);
        setInstantlyUnchecked(null);
        setClosestMatches(false);
        setCompositeCompanyFilters(restoration.composite);
      }
    },
    [clearAll]
  );

  useEffect(() => {
    if (!mcpHandoffBlocked) return;
    searchGenerationRef.current++;
    searchPendingRef.current = false;
    lastRequestRef.current = null;
    nextCursorRef.current = null;
    resolvedExclusionFiltersRef.current = null;
    setStash([]);
    setNextCursor(null);
    setCurrentPage(1);
    setTotal(0);
    setHasSearched(false);
    pendingImportPeopleRef.current = [];
    pendingImportEnrichmentRef.current = undefined;
    setPendingImportListId(null);
    setIsSearching(false);
    setIsPaging(false);
    setSearchSessionId((n) => n + 1);
    setImportDialogOpen(false);
    setConfirmImportOpen(false);
  }, [mcpHandoffBlocked]);

  const previousOrganizationRef = useRef(orgId);
  useEffect(() => {
    if (previousOrganizationRef.current === orgId) return;
    clearSearchState('people', previousOrganizationRef.current);
    previousOrganizationRef.current = orgId;
    clearAll();
    setMcpContext(null);
  }, [orgId, clearAll]);

  useEffect(
    () => () => {
      searchGenerationRef.current++;
    },
    []
  );

  const previousFiltersRef = useRef({
    filters,
    compositeCompanyFilters,
    sortColumn,
    sortOrder,
  });
  useEffect(() => {
    const previous = previousFiltersRef.current;
    if (
      previous.filters === filters &&
      previous.compositeCompanyFilters === compositeCompanyFilters &&
      previous.sortColumn === sortColumn &&
      previous.sortOrder === sortOrder
    )
      return;
    previousFiltersRef.current = {
      filters,
      compositeCompanyFilters,
      sortColumn,
      sortOrder,
    };
    searchGenerationRef.current++;
    resolvedExclusionFiltersRef.current = null;
    setIsSearching(false);
    setIsPaging(false);
    setCrmExclusionStatus(EMPTY_CRM_EXCLUSION_STATUS);
    setCrmSectionStatus({});
    setCleanlistExclusionStatus(EMPTY_CLEANLIST_EXCLUSION_STATUS);
    setErrorState({ isError: false, message: '', kind: 'generic' });
    nextCursorRef.current = null;
    setNextCursor(null);
    lastRequestRef.current = null;
    pendingImportPeopleRef.current = [];
    setSearchSessionId((n) => n + 1);
    clearSearchState('people', orgId);
    if (
      previous.filters.firstName !== filters.firstName ||
      previous.filters.lastName !== filters.lastName ||
      hasStartupPrograms(previous.compositeCompanyFilters.startupPrograms) ||
      hasStartupPrograms(compositeCompanyFilters.startupPrograms)
    ) {
      setStash([]);
      setTotal(0);
      setTotalIsUpperBound(false);
      setTotalKnown(true);
      setProgramCoverage(undefined);
      setProgramWarning(undefined);
      setCompanyCoverage(null);
      setInstantlyUnchecked(null);
      setClosestMatches(false);
      setHasSearched(false);
      setCurrentPage(1);
    }
  }, [filters, compositeCompanyFilters, sortColumn, sortOrder, orgId]);

  const handleSearch = useCallback(async () => {
    if (mcpHandoffBlocked || mcpBlockedRef.current) return;
    if (mcpContext && filters.limitPerCompany.trim()) {
      toast.error(
        'MCP imports cannot apply the portal-specific per-company cap.'
      );
      return;
    }
    if (!hasAnyFilter) {
      toast.error('Please add at least one filter before searching');
      return;
    }

    // Reset the error envelope on every new dispatch — if the search
    // succeeds we never want the previous failure banner sticking.
    setErrorState({ isError: false, message: '', kind: 'generic' });

    // Pre-flight quota check — if the user's out of search allowance,
    // short-circuit before hitting the API and let the results panel
    // render the upgrade variant of SearchErrorState. The panel itself
    // is the user-facing prompt (large icon, copy, upgrade CTA) — no
    // toast layered on top, since two prompts for the same condition is
    // noisy. Hook returns ``isExhausted: false`` for paid Stripe tiers
    // (and stacked Sumolings), so this branch only fires for the
    // metered regimes: free-tier previews (5) and AppSumo tiers (their
    // two-bucket allowance — copy differs by ``isAppSumo``).
    if (searchQuota.isExhausted && !searchQuota.canConvertTrial) {
      setErrorState({
        isError: true,
        message: searchQuota.isAppSumo
          ? "You've used all your searches. Your monthly searches reset soon, or upgrade for unlimited search."
          : `You've used your ${searchQuota.max} available searches. View plans to keep searching.`,
        kind: 'feature_gated',
      });
      return;
    }

    // Compile FIRST — before the generation bump, before the one-shot
    // force-refresh flag is consumed, and before any resolver or provider
    // call. Filter state that cannot compile (including a malformed query
    // arriving from a saved search or ?q= link) must cost zero round-trips,
    // must not burn the refresh flag, and must not abandon an in-flight
    // search's writes.
    let startupPrograms: StartupProgramFilter | undefined;
    let apiFilters: ReturnType<typeof buildCrustdataFilters>;
    let route: ReturnType<typeof detectCompositeRoute>;
    try {
      validatePeopleExclusions(filters);
      startupPrograms = startupProgramRequest(
        compositeCompanyFilters.startupPrograms
      );
      apiFilters = adaptMcpPeopleFilter(
        buildCrustdataFilters(filters),
        filters,
        mcpContext
      );
      route = mcpCompositeRoute(filters, compositeCompanyFilters, mcpContext);
      assertMcpPreviewLimit(mcpContext, filters, route.useComposite);
      if (mcpContext && route.useComposite && sortColumn)
        throw new Error(
          'The MCP composite search did not apply the requested sort.'
        );
    } catch (err) {
      setErrorState({
        isError: true,
        message: filterCompileErrorMessage(
          err,
          'Could not build this search from the current filters.'
        ),
        kind: 'generic',
      });
      return;
    }

    // Bump generation BEFORE awaiting anything. Any in-flight dispatch
    // from a prior call will see its own gen != currentGen on response
    // and abandon its writes.
    const currentGen = ++searchGenerationRef.current;
    resolvedExclusionFiltersRef.current = null;
    setIsSearching(true);

    // Consume the one-shot force-refresh flag. When set (sidebar refresh
    // affordance), we both force a fresh exclusion-set fetch AND bypass
    // the local result cache so the refreshed CRM set actually re-runs.
    const forceRefreshCrm = forceRefreshCrmRef.current;
    forceRefreshCrmRef.current = false;

    try {
      const resolvedExclusions = await resolveSelectedExclusions(
        forceRefreshCrm,
        currentGen
      );
      if (
        mcpHandoffBlocked ||
        mcpBlockedRef.current ||
        resolvedExclusions === null ||
        currentGen !== searchGenerationRef.current ||
        currentOrganizationRef.current !== orgId
      )
        return;

      // ``apiFilters``/``route`` come from the pre-flight above. Routing is
      // needed BEFORE the exclude splice (so an exclude-only composite search
      // keeps its excludes) and before the cache check (block paging reuses
      // the stashed cursor-bearing request verbatim).
      //
      // Splice the CRM/Cleanlist exclude conditions into the person_filter
      // AND group so they subtract alongside the user's other filters. The
      // flat (FE-E), section (CP-5b), Cleanlist-dedup + manual-URL (CP-8)
      // conditions all land in the same group. On the composite endpoint an
      // exclude-only search is valid (company_filter bounds it), so the
      // helper synthesizes the group instead of silently dropping them.
      applyExcludeConditions(
        apiFilters,
        resolvedExclusions.conditions,
        route.useComposite
      );
      // Every filter the user set was dropped as unsearchable (a saved
      // search or agent filter holding a LinkedIn "URL" that isn't one, a
      // domain with no dot): the API would refuse with "At least one filter
      // is required" while the chips still show. Say what's wrong instead.
      if (
        !route.useComposite &&
        !startupPrograms &&
        Object.keys(apiFilters).length === 0
      ) {
        toast.error(
          'None of these filters can be searched. Check the LinkedIn URLs and domains you added.'
        );
        return;
      }
      const sortsParam = sortColumn
        ? [{ column: sortColumn, order: sortOrder }]
        : undefined;
      // Remember the exact built request so "fetch more" paging threads
      // the cursor against byte-identical filters/sort.
      // The per-company cap goes to the SERVER on every search, not only
      // startup-program ones. Capping client-side saw just the loaded
      // sample, so a page dominated by a few large employers could show
      // almost no companies at all, while the uncapped provider total made
      // the header disagree with the table. MCP keeps rejecting it: its
      // page semantics differ (see mcp-search-handoff).
      const parsedLimitPerCompany = Number.parseInt(
        filters.limitPerCompany,
        10
      );
      const limitPerCompanyParam =
        !mcpContext &&
        Number.isFinite(parsedLimitPerCompany) &&
        parsedLimitPerCompany > 0
          ? parsedLimitPerCompany
          : undefined;
      // The server asks each company separately when a cap spans several
      // companies (a composite search always spans its resolved set).
      setSearchingEachCompany(
        Boolean(limitPerCompanyParam) &&
          !startupPrograms &&
          (route.useComposite ||
            filters.currentCompanyDomains.length +
              filters.currentCompanyLinkedInUrls.length +
              filters.currentCompanyNames.length >=
              2)
      );
      lastRequestRef.current = {
        limitPerCompany: limitPerCompanyParam,
        apiFilters,
        route,
        sortsParam,
        startupPrograms,
        identityExclusions: resolvedExclusions.identityExclusions,
        limit: mcpRequestLimit(mcpContext, filters, route.useComposite),
      };
      // The provider's ``total_count`` is calculated before authoritative
      // exclusion post-filters. Capture that count contract with this request;
      // edited filters must not relabel rows from the last successful search.
      // Only resolver-backed (dynamic) exclusions run as post-filters — static
      // excludes (profile URLs, company domains, title excludes) are compiled
      // into the provider ``not_in`` query, so total_count already reflects them
      // and they do not make the count an upper bound.
      const requestTotalIsUpperBound = hasDynamicPeopleExclusions(filters);

      // Include sort state in the cache discriminator so changing sort
      // doesn't hit a stale-ordered result set. CodeRabbit PR #286:
      // cache key was filters-only, so flipping asc↔desc returned the
      // previous ordering.
      const cacheKey = {
        ...(apiFilters as Record<string, unknown>),
        ...(mcpContext ? { __mcpHandoff: mcpContext } : {}),
        // A resolved-empty filter is distinct from no selected filter.
        ...(hasPeopleExclusions(filters)
          ? { __exclusions: exclusionsCacheValue(filters) }
          : {}),
        ...(resolvedExclusions.identityExclusions
          ? {
              __identityExclusions: identityExclusionsCacheValue(
                resolvedExclusions.identityExclusions
              ),
            }
          : {}),
        // The server applies the cap, so two searches that differ only in cap
        // return different rows and must never share a cache entry.
        ...(limitPerCompanyParam
          ? { __limitPerCompany: limitPerCompanyParam }
          : {}),
        // Composite firmographic slice (funding stage, revenue, growth, …)
        // changes the ACTUAL query — composite /by-companies vs plain
        // PersonDB /search — even when the person filters are byte-identical.
        // Excluding it meant refining a search by a company firmographic
        // (e.g. adding "Series B" via the Copilot, or in the Company
        // Firmographics sidebar) returned the prior non-firmographic result
        // set from cache and never routed composite at all.
        ...(hasAnyCompanyFilter(compositeCompanyFilters)
          ? {
              __composite: compositeCompanyFilters as unknown as Record<
                string,
                unknown
              >,
            }
          : {}),
        ...(sortColumn
          ? { __sort: { column: sortColumn, order: sortOrder } }
          : {}),
      };

      // Check cache for identical search (scoped by orgId so a tenant
      // switch never surfaces the previous org's cached pages). A CRM
      // exclusion refresh skips the cache so the freshly-fetched set
      // actually re-runs even when the resolved identifiers are unchanged.
      const cached =
        mcpContext ||
        forceRefreshCrm ||
        startupPrograms ||
        resolvedExclusions.identityExclusions?.cleanlist ||
        // The Instantly workspace changes as campaigns add people, and the
        // unchecked count comes only with a fresh response.
        resolvedExclusions.identityExclusions?.instantly
          ? null
          : getCachedResults(cacheKey, filters.maxResults, orgId);
      if (cached) {
        // Cache hits are synchronous-ish — but still guard the write
        // in case a faster path races us.
        if (
          currentGen !== searchGenerationRef.current ||
          currentOrganizationRef.current !== orgId
        )
          return;
        // Restore the FULL accumulated stash (every block fetched last
        // time) plus its cursor, but reset to page 1 — an explicit
        // re-search always lands the user on the first page. Capping +
        // slicing happen in the derived ``results``.
        const cachedCursor = cached.nextCursor ?? null;
        setStash(cached.results as PersonRecord[]);
        setAppliedLimitPerCompany(
          limitPerCompanyParam ? String(limitPerCompanyParam) : ''
        );
        setSearched({ filters, composite: compositeCompanyFilters });
        setNextCursor(cachedCursor);
        nextCursorRef.current = cachedCursor;
        setCurrentPage(1);
        setTotal(cached.total);
        setTotalIsUpperBound(cached.totalIsUpperBound ?? false);
        setTotalKnown(true);
        setCompanyCoverage(cached.companyCoverage ?? null);
        setInstantlyUnchecked(null);
        setClosestMatches(cached.closestMatches ?? false);
        setHasSearched(true);
        resolvedExclusionFiltersRef.current = filters;
        pendingImportPeopleRef.current = [];
        // Refresh last-search state off the main thread — serializing the
        // cached result array here would re-freeze the UI on every repeat
        // search. (Rewrites the cache entry too; harmless and off-thread.)
        persistSearch({
          type: 'people',
          cacheKeyFilters: cacheKey,
          stateFilters: filters as unknown as Record<string, unknown>,
          limit: filters.maxResults,
          results: cached.results,
          total: cached.total,
          totalIsUpperBound: cached.totalIsUpperBound ?? false,
          nextCursor: cachedCursor,
          currentPage: 1,
          companyCoverage: cached.companyCoverage ?? null,
          closestMatches: cached.closestMatches ?? false,
          orgId,
          compositeCompanyFilters: compositeCompanyFilters as unknown as Record<
            string,
            unknown
          >,
        });
        return;
      }

      if (route.useComposite) {
        trackSearchCompositeRouted(posthog, {
          tab: 'people',
          reason: route.reason,
          companyFilterKeys: route.companyFilter
            ? Object.keys(route.companyFilter)
            : [],
        });
      }
      trackSearchExecuted(posthog, {
        tab: 'people',
        // Rough complexity proxy — number of populated filter slots.
        // ``hasAnyFilter`` is a boolean; the audit-bound field count is
        // good enough for funnel cohorts.
        filterCount: Object.values(filters).filter((v) => {
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === 'string') return v.trim() !== '';
          if (typeof v === 'boolean') return v;
          return false;
        }).length,
        composite: route.useComposite,
        limit: filters.maxResults,
      });

      type SearchResponse = {
        startup_program_coverage?: StartupProgramCoverage;
        program_warning?: string;
        results?: PersonRecord[];
        total_count?: number | null;
        next_cursor?: string | null;
        applied_identity_exclusion_token?: string;
        applied_identity_exclusions?: IdentityExclusionKind[] | null;
        instantly_unchecked?: number | null;
        company_coverage?: CompanyCoverage | null;
        match_mode?: 'fuzzy_name' | null;
      };
      let response: SearchResponse;
      if (route.useComposite) {
        // Block 1 — no cursor. limit is the block size; the backend caps
        // at BLOCK_SIZE regardless and returns ``next_cursor`` for paging.
        const compositePayload: CompositePeopleSearchRequest = {
          startup_programs: startupPrograms,
          ...(limitPerCompanyParam
            ? { limit_per_company: limitPerCompanyParam }
            : {}),
          company_filter: route.companyFilter ?? undefined,
          person_filter: apiFilters as Record<string, unknown>,
          limit: mcpRequestLimit(mcpContext, filters, true),
          sorts: sortsParam,
          identity_exclusions: resolvedExclusions.identityExclusions,
        };
        // Start a non-blocking job and poll it; the polled result mirrors the
        // composite response shape.
        const raw = await pollCompaniesFind(compositePayload);
        // Composite returns ``results`` as an unstructured object
        // array (Crustdata raw shape, same as plain search). Cast to
        // PersonRecord since the backend normalize step produces the
        // same wire shape on both code paths.
        response = {
          startup_program_coverage: raw.startup_program_coverage,
          program_warning: raw.program_warning || raw.composite_warning,
          results: raw.results as unknown as PersonRecord[],
          total_count: raw.total_count ?? null,
          next_cursor: raw.next_cursor ?? null,
          applied_identity_exclusion_token:
            raw.applied_identity_exclusion_token,
          applied_identity_exclusions: raw.applied_identity_exclusions,
          instantly_unchecked: raw.instantly_unchecked,
          company_coverage: raw.company_coverage ?? null,
        };
      } else {
        const searchPayload: PeopleSearchRequest = {
          filters: apiFilters as unknown as CrustdataFilterGroup,
          limit: mcpRequestLimit(mcpContext, filters),
          ...(limitPerCompanyParam
            ? { limit_per_company: limitPerCompanyParam }
            : {}),
          // Crustdata v1 persondb expects ``column`` (not ``field``) for
          // sort entries — see docs/plans/crustdata-people-search-api.md.
          // Previous ``field`` key silently dropped the sort upstream.
          ...(sortsParam ? { sorts: sortsParam } : {}),
          identity_exclusions: resolvedExclusions.identityExclusions,
        };
        // Start a job and poll it; the polled result mirrors the inline
        // response shape.
        response = await pollPeopleSearch(triggerSearch, searchPayload);
      }

      // Stale response — a newer dispatch already bumped the
      // generation counter. Drop these results on the floor; the
      // newer dispatch owns the UI now.
      if (
        mcpHandoffBlocked ||
        mcpBlockedRef.current ||
        currentGen !== searchGenerationRef.current ||
        currentOrganizationRef.current !== orgId
      )
        return;

      requireIdentityExclusionsApplied(
        response,
        resolvedExclusions.identityExclusions
      );

      // Permissive Zod check at the wire boundary — DEV warns when a
      // field drifted from the PersonRecord shape (e.g. Crustdata
      // changed ``email`` to a nested object). Production never throws.
      const validatedResults = validatePersonRecords(
        response.results || []
      ) as PersonRecord[];
      const searchResults = validatedResults;
      const searchTotal = response.total_count ?? searchResults.length ?? 0;

      // Dev-only diagnostic: log the shape of the first result so we
      // can tell whether hidden columns show blanks because (a) the
      // field is missing from Crustdata's response, or (b) the
      // accessor path is wrong on our side. Guarded behind
      // ``import.meta.env.DEV`` so production bundles don't spam user
      // consoles with raw payload structure (CodeRabbit PR #286).
      if (import.meta.env.DEV && searchResults.length > 0) {
        const first = searchResults[0] as unknown as {
          raw?: Record<string, unknown>;
        };
        const rawKeys = first.raw ? Object.keys(first.raw) : [];
        const rawSample = {
          num_of_connections: first.raw?.num_of_connections,
          years_of_experience_raw: first.raw?.years_of_experience_raw,
          recently_changed_jobs: first.raw?.recently_changed_jobs,
          skills_count: Array.isArray(first.raw?.skills)
            ? (first.raw?.skills as unknown[]).length
            : 'not-array',
          education_count: Array.isArray(first.raw?.education_background)
            ? (first.raw?.education_background as unknown[]).length
            : 'not-array',
          past_employers_count: Array.isArray(first.raw?.past_employers)
            ? (first.raw?.past_employers as unknown[]).length
            : 'not-array',
          certifications_count: Array.isArray(first.raw?.certifications)
            ? (first.raw?.certifications as unknown[]).length
            : 'not-array',
        };
        console.info('[people-search] first result raw.keys', rawKeys);
        console.info('[people-search] first result raw.sample', rawSample);
      }

      // Fresh search → block 1 becomes the whole stash, page 1, and the
      // cursor for block 2. (Capping is applied in the derived ``results``,
      // so the stash itself holds the raw rows for faithful re-capping as
      // more blocks accumulate.)
      // An explicit maxResults under a page is terminal: preserve it
      // without advancing the 25-row pager into an empty window.
      const freshCursor =
        mcpContext &&
        mcpRequestLimit(mcpContext, filters, route.useComposite) < PAGE_SIZE
          ? null
          : (response.next_cursor ?? null);
      setStash(searchResults);
      setAppliedLimitPerCompany(
        limitPerCompanyParam ? String(limitPerCompanyParam) : ''
      );
      setSearched({ filters, composite: compositeCompanyFilters });
      setNextCursor(freshCursor);
      nextCursorRef.current = freshCursor;
      setCurrentPage(1);
      setTotal(searchTotal);
      setTotalIsUpperBound(
        requestTotalIsUpperBound &&
          (!startupPrograms || response.total_count != null)
      );
      setTotalKnown(!startupPrograms || response.total_count != null);
      setProgramCoverage(response.startup_program_coverage);
      setProgramWarning(response.program_warning);
      setCompanyCoverage(response.company_coverage ?? null);
      setInstantlyUnchecked(response.instantly_unchecked ?? null);
      setClosestMatches(response.match_mode === 'fuzzy_name');
      setHasSearched(true);
      resolvedExclusionFiltersRef.current = filters;
      pendingImportPeopleRef.current = [];

      // The pair of search_executed above: only a response that painted, with
      // the count search_executed cannot have. Not a cache hit, not a stale or
      // failed dispatch.
      trackSearchCompleted(posthog, {
        tab: 'people',
        total: searchTotal,
        composite: route.useComposite,
        totalKnown: response.total_count != null,
        // The provider's total comes before exclusions and before a
        // per-company cap (the page shows the capped rows): a bound.
        totalIsUpperBound:
          response.total_count != null &&
          (requestTotalIsUpperBound ||
            (Boolean(limitPerCompanyParam) && !startupPrograms)),
      });

      // Burn one preview-quota credit ONLY on a network success.
      // Cache-hit re-searches (above) don't consume a credit so the
      // user can re-open or tweak views of the same filter set without
      // burning previews. Hook is a no-op for unlimited tiers.
      // A fully unresolved program selection makes no PersonDB call and is not metered.
      if (
        !startupPrograms ||
        response.startup_program_coverage?.resolved_company_count !== 0
      )
        incrementSearchQuota();

      // Hydrate the reveal map from server-provided enrichment values so
      // re-searching for somebody you already paid credits on renders the
      // value inline instead of a misleading "Enrich" button.
      hydrateRevealsFromResults(searchResults);

      // Persist the result cache + last-search state OFF the main thread
      // (Web Worker serialize, fire-and-forget). Results have already been
      // painted via setResults above; this must never block the UI. The
      // cache key matches the one used for cache lookup so read/write stay
      // aligned (sort-aware); orgId scopes the storage namespace per the
      // round-2 reliability audit (cross-tenant leak).
      if (!mcpContext && !startupPrograms)
        persistSearch({
          type: 'people',
          cacheKeyFilters: cacheKey,
          stateFilters: filters as unknown as Record<string, unknown>,
          limit: filters.maxResults,
          results: searchResults,
          total: searchTotal,
          totalIsUpperBound: requestTotalIsUpperBound,
          nextCursor: freshCursor,
          currentPage: 1,
          companyCoverage: response.company_coverage ?? null,
          closestMatches: response.match_mode === 'fuzzy_name',
          orgId,
          compositeCompanyFilters: compositeCompanyFilters as unknown as Record<
            string,
            unknown
          >,
        });
    } catch (err) {
      if (
        mcpHandoffBlocked ||
        mcpBlockedRef.current ||
        currentGen !== searchGenerationRef.current ||
        currentOrganizationRef.current !== orgId
      )
        return;
      let message =
        err instanceof Error &&
        (err.message === IDENTITY_EXCLUSION_ACKNOWLEDGEMENT_ERROR ||
          err instanceof ProgramSearchError)
          ? err.message
          : filterCompileErrorMessage(err, 'Search failed. Please try again.');
      let isFeatureGated = false;
      if (
        typeof err === 'object' &&
        err !== null &&
        'data' in (err as Record<string, unknown>)
      ) {
        const data = (err as Record<string, unknown>).data;
        const detail =
          typeof data === 'object' && data !== null
            ? (data as Record<string, unknown>).detail
            : data;
        if (typeof detail === 'string') {
          message = identityExclusionErrorMessage(detail) ?? detail;
        } else if (typeof detail === 'object' && detail !== null) {
          const detailRecord = detail as Record<string, unknown>;
          const errorCode = detailRecord.error;
          if (errorCode === 'feature_gated') {
            isFeatureGated = true;
            const requiredTier = detailRecord.required_tier || 'Starter';
            message = `People Finder requires the ${String(requiredTier).charAt(0).toUpperCase() + String(requiredTier).slice(1)} plan.`;
          } else if (typeof errorCode === 'string') {
            message = identityExclusionErrorMessage(errorCode) ?? message;
          } else {
            // ``{ code, message }``, e.g. invalid_filter for a filter value
            // the search can't use.
            message = errorDetailMessage(err) ?? message;
          }
        }
      }
      if (!isFeatureGated)
        message = searchFailureCopy(message, err, 'People Finder');
      // For feature_gated rejections we let the SearchErrorState
      // upgrade variant be the one prompt — no toast layered on top.
      // Generic failures still get a toast so the user gets a heads-
      // up even if they're not looking at the results panel.
      if (!isFeatureGated) {
        toast.error(message);
      }
      // Surface the error in the results panel so a stale render
      // doesn't pretend nothing went wrong. The retry CTA reads this
      // state via the hook return. Only paint the error if this is
      // still the latest dispatch — otherwise a stale failure would
      // overwrite a newer in-flight success.
      if (currentGen === searchGenerationRef.current) {
        setErrorState({
          isError: true,
          message,
          kind: isFeatureGated ? 'feature_gated' : 'generic',
        });
        trackSearchErrorShown(posthog, {
          tab: 'people',
          message,
          status:
            typeof err === 'object' && err && 'status' in err
              ? (err as { status?: number }).status
              : undefined,
        });
      }
    } finally {
      if (
        currentGen === searchGenerationRef.current &&
        currentOrganizationRef.current === orgId
      ) {
        setIsSearching(false);
        setCrmExclusionStatus((prev) => ({ ...prev, isFetching: false }));
        setCrmSectionStatus((prev) =>
          Object.fromEntries(
            Object.entries(prev).map(([crm, status]) => [
              crm,
              { ...status, isFetching: false },
            ])
          )
        );
      }
    }
  }, [
    triggerSearch,
    pollCompaniesFind,
    pollPeopleSearch,
    filters,
    compositeCompanyFilters,
    hasAnyFilter,
    resolveSelectedExclusions,
    hydrateRevealsFromResults,
    sortColumn,
    sortOrder,
    orgId,
    posthog,
    // Stable function ref (useCallback in use-search-quota.hook), so
    // adding it doesn't churn handleSearch every render.
    incrementSearchQuota,
    // Pre-flight quota check needs the live exhaustion flag + the
    // AppSumo flag (picks the exhausted-state copy).
    searchQuota.isExhausted,
    searchQuota.canConvertTrial,
    searchQuota.max,
    searchQuota.isAppSumo,
    mcpContext,
    mcpHandoffBlocked,
  ]);

  // The main error panel retries the whole failed attempt. When a CRM
  // exclusion was part of that attempt, also request fresh CRM exclusion data
  // so Retry does not immediately repeat a stale or expired generation error.
  const retrySearch = useCallback(() => {
    if (hasAnyCrmExclusionEnabled) forceRefreshCrmRef.current = true;
    void handleSearch();
  }, [hasAnyCrmExclusionEnabled, handleSearch]);

  // ── Block paging ──────────────────────────────────────────────────
  //
  // Fetch the NEXT block of results using the forward-only cursor +
  // the exact request that produced the current stash. Appends the block
  // to the stash and advances the cursor. Idempotent while in flight (the
  // in-flight promise guards against a double Next click). Returns the added
  // row count, or null on failure. Uses the dedicated paging triggers so the main
  // ``isSearching`` flag isn't flipped.
  const pagingFetchRef = useRef<Promise<number | null> | null>(null);
  // Session id captured at fetch start so a late-resolving paging response
  // from an OLD search context can't repopulate stash / cursor after the
  // user has edited filters or kicked off a new search. Read inside the
  // closure rather than at the outer scope so each call gets the live
  // value. (``react-hooks/exhaustive-deps`` doesn't see ref reads.)
  const searchSessionIdRef = useRef(searchSessionId);
  searchSessionIdRef.current = searchSessionId;
  const fetchNextBlock = useCallback((): Promise<number | null> => {
    if (mcpHandoffBlocked || mcpBlockedRef.current)
      return Promise.resolve(null);
    if (pagingFetchRef.current) return pagingFetchRef.current;
    const req = lastRequestRef.current;
    const cursor = nextCursorRef.current;
    if (!req || !cursor) return Promise.resolve(null);
    const sessionAtStart = searchSessionIdRef.current;
    const generationAtStart = searchGenerationRef.current;

    const run = (async (): Promise<number | null> => {
      setIsPaging(true);
      try {
        const startupPrograms = startupProgramRequest(req.startupPrograms);
        let newRows: PersonRecord[];
        let pageTotal: number | null | undefined;
        let pageCoverage: StartupProgramCoverage | undefined;
        let pageWarning: string | undefined;
        let newCursor: string | null;
        if (req.route.useComposite) {
          const compositePagePayload: CompositePeopleSearchRequest = {
            startup_programs: startupPrograms,
            ...(req.limitPerCompany
              ? { limit_per_company: req.limitPerCompany }
              : {}),
            company_filter: req.route.companyFilter ?? undefined,
            person_filter: req.apiFilters as Record<string, unknown>,
            limit: req.limit,
            cursor,
            sorts: req.sortsParam,
            identity_exclusions: req.identityExclusions,
          };
          // Each page is its own async job; the polled response echoes
          // applied_identity_exclusion_token so the guard below still holds.
          const raw = await pollCompaniesFind(compositePagePayload);
          requireIdentityExclusionsApplied(raw, req.identityExclusions);
          pageTotal = raw.total_count;
          pageCoverage = raw.startup_program_coverage;
          pageWarning = raw.program_warning || raw.composite_warning;
          newRows = validatePersonRecords(
            (raw.results as unknown as PersonRecord[]) || []
          ) as PersonRecord[];
          newCursor = raw.next_cursor ?? null;
        } else {
          const searchPagePayload: PeopleSearchRequest = {
            filters: req.apiFilters as unknown as CrustdataFilterGroup,
            limit: req.limit,
            cursor,
            ...(req.limitPerCompany
              ? { limit_per_company: req.limitPerCompany }
              : {}),
            ...(req.sortsParam ? { sorts: req.sortsParam } : {}),
            identity_exclusions: req.identityExclusions,
          };
          const raw = await pollPeopleSearch(
            triggerSearchPaging,
            searchPagePayload
          );
          requireIdentityExclusionsApplied(raw, req.identityExclusions);
          newRows = validatePersonRecords(raw.results || []) as PersonRecord[];
          newCursor = raw.next_cursor ?? null;
        }
        // Drop the result if the search context has moved on (filter/sort
        // edit, fresh search, or first/last-name clear). Without this guard
        // a slow paging response can re-populate the cursor and stash for a
        // query the user no longer cares about.
        if (
          mcpHandoffBlocked ||
          mcpBlockedRef.current ||
          searchGenerationRef.current !== generationAtStart ||
          searchSessionIdRef.current !== sessionAtStart ||
          lastRequestRef.current !== req ||
          nextCursorRef.current !== cursor
        ) {
          return null;
        }
        hydrateRevealsFromResults(newRows);
        stashRef.current = [...stashRef.current, ...newRows];
        setStash(stashRef.current);
        if (startupPrograms) {
          setTotalKnown(pageTotal != null);
          setTotal((prev) => pageTotal ?? prev + newRows.length);
          setProgramCoverage(pageCoverage);
          setProgramWarning(pageWarning);
        }
        setNextCursor(newCursor);
        nextCursorRef.current = newCursor;
        // Mirror the server's debit so the badge stays accurate and the
        // pre-gate in goNextPage fires before the next call would 403: every
        // page fetched is one search on every metered plan (no-op for
        // unlimited tiers).
        incrementSearchQuota();
        return newRows.length;
      } catch (err) {
        if (
          mcpHandoffBlocked ||
          mcpBlockedRef.current ||
          lastRequestRef.current !== req ||
          searchSessionIdRef.current !== sessionAtStart
        )
          return null;
        // Don't advance the page on a failed block fetch — the user stays
        // put with their current results intact. Surface the gate that
        // tripped:
        //  - 403: preview/feature gate (free tier out of previews).
        //  - 429: hourly preview rate limit (40/hour/org).
        const status =
          typeof err === 'object' && err && 'status' in err
            ? (err as { status?: number }).status
            : undefined;
        const exclusionMessage = identityExclusionFailureMessage(err);
        if (exclusionMessage) {
          toast.error(exclusionMessage);
        } else if (req.startupPrograms && status === 409) {
          // Expired or changed snapshots need a fresh search, not the same cursor.
          setNextCursor(null);
          nextCursorRef.current = null;
          setErrorState({
            isError: true,
            message:
              'These search results have expired or changed. Run the search again to continue.',
            kind: 'generic',
          });
        } else if (status === 403) {
          toast.error("You've used your free previews — upgrade to see more.");
        } else if (status === 429) {
          const resetAt = rateLimitResetAt(err);
          setPagingLock(resetAt === null ? null : { orgId, until: resetAt });
          toast.error(
            resetAt
              ? `Hourly preview limit reached. More pages at ${formatResetTime(resetAt)}.`
              : 'Hourly preview limit reached. Try again later.',
            {
              description:
                'Add these results to a list to pull more without waiting.',
            }
          );
        } else {
          toast.error('Failed to load more results.');
        }
        return null;
      } finally {
        setIsPaging(false);
        pagingFetchRef.current = null;
      }
    })();

    pagingFetchRef.current = run;
    return run;
  }, [
    orgId,
    triggerSearchPaging,
    pollCompaniesFind,
    pollPeopleSearch,
    hydrateRevealsFromResults,
    incrementSearchQuota,
    mcpHandoffBlocked,
  ]);

  const goNextPage = useCallback(async () => {
    if (mcpHandoffBlocked || mcpBlockedRef.current) return;
    const generationAtStart = searchGenerationRef.current;
    if (isPaging) return;
    // Index of the first row the NEXT page would show, against the capped
    // (displayed) stash.
    const nextPageStart = currentPage * PAGE_SIZE;
    const nextPageEnd = nextPageStart + PAGE_SIZE;
    // Imported blocks need not align with the 25-row display window. Fill
    // the target window before showing it so a later block cannot add rows
    // to an already-passed page (for example, a 30-row request).
    const needsMcpPageFill =
      mcpContext && nextCursorRef.current && cappedStash.length < nextPageEnd;
    if (
      cappedStash.length > nextPageStart &&
      (!needsMcpPageFill || searchQuota.isExhausted)
    ) {
      // Already buffered — just advance, no network call (no quota cost).
      setCurrentPage((p) => p + 1);
      return;
    }
    // Past the buffer — need the next block (if any).
    if (!nextCursorRef.current) return;
    if (pagingLockedUntil !== null && Date.now() < pagingLockedUntil) {
      toast.error(
        `Hourly preview limit reached. More pages at ${formatResetTime(pagingLockedUntil)}.`
      );
      return;
    }
    // Fetching the next page is a metered call on free, AppSumo, trial and
    // Starter plans (each page deducts one search, same as the first).
    // Pre-gate when the allowance is spent — mirror handleSearch so the
    // user gets the upgrade nudge instead of a raw backend 403, and we
    // never fire the gated call. Entitled tiers have isExhausted=false, so
    // this is a no-op and paging is unlimited.
    if (searchQuota.isExhausted && !searchQuota.canConvertTrial) {
      toast.error(
        searchQuota.isAppSumo
          ? "You've used all your searches — upgrade for unlimited."
          : "You've used your free previews — upgrade to load more."
      );
      return;
    }
    const addedRows = await fetchNextBlock();
    const bufferedRows = cappedStash.length + (addedRows ?? 0);
    // Do NOT advance on a failed fetch (e.g. 429).
    if (
      addedRows !== null &&
      !mcpHandoffBlocked &&
      !mcpBlockedRef.current &&
      searchGenerationRef.current === generationAtStart &&
      (!mcpContext ||
        (cappedStash.length >= nextPageStart &&
          bufferedRows > nextPageStart &&
          (bufferedRows >= nextPageEnd || !nextCursorRef.current)))
    )
      // Two Next callbacks can await the same paging promise. An MCP page
      // advances only once from the window that started that shared fetch;
      // otherwise advance one page, clamped to the capped buffered rows.
      setCurrentPage((p) =>
        mcpContext && p !== currentPage
          ? p
          : Math.min(
              p + 1,
              Math.max(
                1,
                Math.ceil(
                  capResultsPerCompany(
                    stashRef.current,
                    appliedLimitPerCompany,
                    appliedMatchCriteria
                  ).length / PAGE_SIZE
                )
              )
            )
      );
  }, [
    isPaging,
    currentPage,
    cappedStash.length,
    appliedLimitPerCompany,
    appliedMatchCriteria,
    fetchNextBlock,
    searchQuota.isExhausted,
    searchQuota.canConvertTrial,
    searchQuota.max,
    searchQuota.isAppSumo,
    mcpHandoffBlocked,
    mcpContext,
    pagingLockedUntil,
  ]);

  // Mirrors goNextPage's "already buffered" test, for the pager.
  const pagerNextStart = currentPage * PAGE_SIZE;
  const nextPageNeedsFetch = !(
    cappedStash.length > pagerNextStart &&
    (!(
      mcpContext &&
      nextCursor &&
      cappedStash.length < pagerNextStart + PAGE_SIZE
    ) ||
      searchQuota.isExhausted)
  );

  const goPrevPage = useCallback(() => {
    if (mcpHandoffBlocked || mcpBlockedRef.current) return;
    // Always served from the stash — never a network call.
    setCurrentPage((p) => Math.max(1, p - 1));
  }, [mcpHandoffBlocked]);

  // Buffered-range jump for the numbered pager. Clamps to what's fetched:
  // jumping past the buffer would need a forward-only cursor walk of
  // metered block fetches — an explicit non-feature (see the no-prefetch
  // note below). Callers toast when the clamp bites.
  const bufferedPageCount = Math.max(
    1,
    Math.ceil(cappedStash.length / PAGE_SIZE)
  );
  const goToPage = useCallback(
    (page: number) => {
      if (mcpHandoffBlocked || mcpBlockedRef.current) return currentPage;
      const clamped = Math.min(
        Math.max(1, Math.floor(page)),
        Math.max(1, Math.ceil(cappedStash.length / PAGE_SIZE))
      );
      setCurrentPage(clamped);
      return clamped;
    },
    [cappedStash.length, currentPage, mcpHandoffBlocked]
  );

  // NOTE: there is intentionally NO background prefetch. The next block is
  // fetched lazily, only on the actual "Next" click that crosses into
  // unbuffered territory (see goNextPage). Prefetching on landing fired a
  // /search call before the user asked to advance, which on free/limited
  // accounts tripped the preview gate "on page 4" without any user action.

  // Schedule a search to run after the next filter state update (avoids stale closure)
  // Content of the filters a held scheduled search is waiting with.
  const pendingSearchKeyRef = useRef<string | null>(null);
  // The filters on screen when a search was scheduled. A link schedules
  // right after setting its filters, before they render; under StrictMode
  // the effect below replays in between and saw "nothing searchable", which
  // dropped the link's search (dev only, where StrictMode replays effects).
  const latestFiltersRef = useRef(filters);
  latestFiltersRef.current = filters;
  const scheduledFromRef = useRef<SearchFilters | null>(null);
  const scheduleSearch = useCallback(() => {
    searchPendingRef.current = true;
    pendingSearchKeyRef.current = null;
    scheduledFromRef.current = latestFiltersRef.current;
  }, []);

  useEffect(() => {
    if (suspendAutomaticSearch) {
      searchPendingRef.current = false;
      pendingSearchKeyRef.current = null;
      return;
    }
    if (!searchPendingRef.current) return;
    // A scheduled search belongs to the filters it was scheduled for. It can
    // be held below until CRM / lead-list data is ready, and the user may keep
    // editing meanwhile; firing it then would run a search nobody asked for on
    // their next keystroke. So drop it once the filters it was held with
    // change. Compared by content, so re-rendering identical filters does not
    // cancel a hydrated search.
    const filtersKey = JSON.stringify(filters);
    if (
      pendingSearchKeyRef.current !== null &&
      pendingSearchKeyRef.current !== filtersKey
    ) {
      searchPendingRef.current = false;
      pendingSearchKeyRef.current = null;
      return;
    }
    // Nothing searchable, so handleSearch would refuse anyway. Drop the
    // request instead of leaving it armed for the next edit ("Find contacts"
    // on a nameless company, a link carrying only the per-company cap).
    if (!hasAnyFilter) {
      // Scheduled before its filters rendered: wait for them, once.
      if (scheduledFromRef.current === filters) {
        scheduledFromRef.current = null;
        return;
      }
      searchPendingRef.current = false;
      pendingSearchKeyRef.current = null;
      return;
    }
    scheduledFromRef.current = null;
    // B1: hold a scheduled / URL-hydrated search until /crm/options has settled
    // when a CRM exclusion is enabled, so the exclusion actually resolves on the
    // FIRST search (activeCrm is null until options load — a hydrated search
    // otherwise ran unfiltered). When crmReady flips true this effect re-runs
    // and fires. On an options error, the exclusion resolver reports failure
    // instead of leaving the search pending. Covers the flat excludeCrm* path and
    // the per-CRM filter sections (CP-5b) — section clauses likewise need the
    // connection set resolved before their first resolve.
    if (
      (getActiveCrmExclusions(filters).length > 0 ||
        getActiveCrmSectionFilters(filters).length > 0) &&
      !crmReady
    ) {
      pendingSearchKeyRef.current = filtersKey;
      return;
    }
    // B1 (CP-8): likewise hold the search until the org's lead-lists query has
    // settled when a Cleanlist dedup exclusion is enabled — otherwise a
    // hydrated/scheduled search fires before the list ids can be validated and
    // the Cleanlist exclusion is silently dropped. Degrades open on lists
    // error (leadListsReady true) so a shared search never hangs.
    if (hasCleanlistExclusion(filters) && !leadListsReady) {
      pendingSearchKeyRef.current = filtersKey;
      return;
    }
    searchPendingRef.current = false;
    pendingSearchKeyRef.current = null;
    handleSearch();
  }, [
    filters,
    hasAnyFilter,
    handleSearch,
    crmReady,
    leadListsReady,
    suspendAutomaticSearch,
  ]);

  const [importProgress, setImportProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const executeImport = useCallback(
    async (
      listId: string,
      options?: {
        enrichListed?: boolean;
        listName?: string;
        /** The result screen shows the outcome: no success toast on top of
         *  it. Read when the import ends (the sheet may close meanwhile). */
        resultOnScreen?: () => boolean;
      }
    ): Promise<ImportPeopleOutcome | null> => {
      if (importInProgressRef.current) return null;
      try {
        validatePeopleExclusions(filters);
        if (
          (hasPeopleExclusions(filters) || !hasSearched) &&
          resolvedExclusionFiltersRef.current !== filters
        ) {
          // The Show button stays clickable in this state and its run
          // resolves them (exclusionsUnresolved).
          toast.error('Click Show to refresh this search, then add to list.');
          return null;
        }
      } catch (err) {
        toast.error(
          filterCompileErrorMessage(
            err,
            'The selected filters are not resolved.'
          )
        );
        return null;
      }
      importInProgressRef.current = true;
      setImportDialogOpen(false);
      setConfirmImportOpen(false);

      // Prefer the pending selection set by the view on "Add to list";
      // fall back to the full results set for backward-compatible callers.
      const peopleToImport =
        pendingImportPeopleRef.current.length > 0
          ? pendingImportPeopleRef.current
          : results;

      if (peopleToImport.length === 0) {
        toast.error('No people to import');
        importInProgressRef.current = false;
        return null;
      }

      try {
        startupProgramRequest(compositeCompanyFilters.startupPrograms);
        const enrichmentType = pendingImportEnrichmentRef.current;

        // De-duplicate the fetched rows by LinkedIn identity BEFORE chunking.
        // The old sequential importer got cross-chunk dedup for free — each
        // chunk re-read the list, so a person reappearing in a later chunk was
        // seen as "already in list" and neither billed nor re-added. Running
        // chunks in parallel (below) removes that guarantee: two in-flight
        // chunks both read the list before either commits, so a row present in
        // both would be billed twice even though ``add_leads`` (ON CONFLICT DO
        // NOTHING) lands it once. Collapsing duplicates here keeps the parallel
        // path billing-correct and trims wasted payload. Key priority mirrors
        // the backend resolver (``flagship_profile_url`` → … → ``linkedin_url``);
        // rows with no URL are kept untouched so the server still reports them
        // as errors exactly as before.
        const linkedInKey = (raw: Record<string, unknown>): string => {
          for (const f of [
            'flagship_profile_url',
            'linkedin_flagship_url',
            'linkedin_profile_url',
            'linkedin_url',
          ]) {
            const v = raw[f];
            if (typeof v === 'string' && v) return v;
          }
          return '';
        };
        const seenKeys = new Set<string>();
        const rawPeople = peopleToImport
          .map((p) => programPersonImportRaw(p.raw, matchCriteria))
          .filter((raw) => {
            const key = linkedInKey(raw);
            if (!key) return true; // no identity to dedupe on — keep as-is
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          });
        const total = rawPeople.length;

        // Chunk the import so a large batch (e.g. 2,500) isn't one multi-MB
        // POST that stalls the "Importing…" spinner for tens of seconds and
        // risks a gateway timeout. Smaller requests complete reliably and let
        // us stream progress. Credits are metered per imported row server-side,
        // so splitting the batch never changes the total charge; server-side
        // dedup still skips rows already on the list on every chunk.
        const CHUNK_SIZE = 500;
        const chunks: Record<string, unknown>[][] = [];
        for (let start = 0; start < total; start += CHUNK_SIZE) {
          chunks.push(rawPeople.slice(start, start + CHUNK_SIZE));
        }

        // Run chunks concurrently instead of one-after-another. The old
        // sequential loop waited a full round-trip per 500 rows (5 serial POSTs
        // for a 2,500 import); a bounded pool collapses that to roughly one
        // round-trip's wall-clock while capping how many heavy bulk transactions
        // hit Postgres at once — a 5,000 import (10 chunks) never fires more
        // than ``MAX_CONCURRENT_CHUNKS`` at a time. Concurrent imports to one
        // list are safe server-side: ``add_leads`` is ON CONFLICT DO NOTHING,
        // each chunk carries its own server-minted idempotency key, and the
        // credit CAS guard can't go negative.
        const MAX_CONCURRENT_CHUNKS = 4;
        let importedTotal = 0;
        let skippedTotal = 0;
        let failedChunks = 0;
        let pendingChunks = 0;
        let firstChunkError: string | null = null;
        let doneRows = 0;
        const importedLeadIds: string[] = [];
        const alreadyInListIds: string[] = [];
        // Per-person outcomes, when every finished batch sent them.
        const outcomes: PersonImportOutcome[] = [];
        let outcomesMissing = false;

        setImportProgress({ done: 0, total });

        // Async mode: /import returns {task_id, status:'processing'} instantly;
        // poll /import/status until the background job finishes. ~3 min ceiling
        // — imports "shouldn't take long", and the navbar notification covers a
        // slower tail (or a closed tab). A sentinel status:'processing' return
        // marks the chunk pending (not failed) so the summary can say so.
        const pollImportStatus = async (
          taskId: string
        ): Promise<ImportPeopleResponse> => {
          const INTERVAL_MS = 2000;
          const MAX_ATTEMPTS = 90;
          for (let i = 0; i < MAX_ATTEMPTS; i++) {
            await new Promise((r) => setTimeout(r, INTERVAL_MS));
            try {
              const status = await triggerImportStatus(taskId).unwrap();
              if (status.status && status.status !== 'processing')
                return status;
            } catch {
              // 404 before the worker writes / transient blip — keep polling.
            }
          }
          return {
            success: false,
            imported_count: 0,
            skipped_count: 0,
            errors: [],
            lead_ids: [],
            status: 'processing',
          };
        };

        const runChunk = async (chunk: Record<string, unknown>[]) => {
          try {
            let response = await triggerImport({
              lead_list_id: listId,
              people: chunk,
              ...(enrichmentType && enrichmentType !== 'none'
                ? {
                    enrichment_type: enrichmentType,
                    ...(options?.enrichListed ? { enrich_listed: true } : {}),
                  }
                : {}),
              async_import: true,
            }).unwrap();
            // Async dispatch → poll to completion before tallying the chunk.
            if (response.task_id && response.status === 'processing') {
              response = await pollImportStatus(response.task_id);
            }
            if (response.status === 'processing') {
              // Client poll window elapsed; the job finishes server-side and the
              // notification bell reports completion.
              pendingChunks += 1;
            } else if (response.success) {
              importedTotal += response.imported_count ?? chunk.length;
              skippedTotal += response.skipped_count ?? 0;
              importedLeadIds.push(...(response.lead_ids ?? []));
              alreadyInListIds.push(...(response.already_in_list_ids ?? []));
              if (response.outcomes) outcomes.push(...response.outcomes);
              else outcomesMissing = true;
            } else {
              failedChunks += 1;
              // The async worker reports a cap rejection (lead_cap_reached)
              // in the poll's `errors`, not as a thrown 403 — keep it too.
              firstChunkError ??= response.errors?.find(Boolean) ?? null;
            }
          } catch (error) {
            failedChunks += 1;
            // Keep the API's reason (e.g. "Access denied to this lead list",
            // a lead cap) for the all-failed toast instead of a bare retry.
            // A bare code ("FORBIDDEN") is the envelope's placeholder.
            const reason = requestErrorMessage(error, '');
            if (!firstChunkError && reason && !/^[A-Z_]+$/.test(reason)) {
              firstChunkError = reason;
            }
          } finally {
            // Completion-based progress: chunks resolve out of order under
            // parallelism, so bump ``done`` as each one finishes rather than by
            // slice offset. The "Importing N / M…" counter still climbs
            // monotonically to the total.
            doneRows += chunk.length;
            setImportProgress({ done: Math.min(doneRows, total), total });
          }
        };

        // Bounded worker pool: N workers pull chunks off a shared cursor until
        // the queue drains. Single-threaded JS makes ``cursor++`` and the
        // accumulator ``+=`` safe between awaits — no lock needed.
        const importStartedAt = performance.now();
        let cursor = 0;
        const worker = async () => {
          while (cursor < chunks.length) {
            const chunk = chunks[cursor++];
            await runChunk(chunk);
          }
        };
        await Promise.all(
          Array.from(
            { length: Math.min(MAX_CONCURRENT_CHUNKS, chunks.length) },
            worker
          )
        );

        // Emit the import-latency metric for the chunked phase (the part the
        // parallel pool optimized). Fires on success and partial failure so it
        // also serves as an import-volume signal; ``durationMs`` is charted as
        // p50/p95 in PostHog against the old ~30–40s sequential baseline.
        trackPeopleImportCompleted(posthog, {
          tab: 'people',
          requestedCount: total,
          importedCount: importedTotal,
          skippedCount: skippedTotal,
          chunkCount: chunks.length,
          failedChunks,
          maxConcurrency: MAX_CONCURRENT_CHUNKS,
          enrichment: enrichmentType ?? 'none',
          durationMs: Math.round(performance.now() - importStartedAt),
        });

        // Every chunk failed → nothing landed; report an outright failure.
        if (
          failedChunks > 0 &&
          importedTotal === 0 &&
          skippedTotal === 0 &&
          pendingChunks === 0
        ) {
          toast.error(firstChunkError ?? 'Import failed. Please try again.');
          return null;
        }

        invalidateLeadListLeadsCache(dispatch, listId);
        dispatch(
          leadListsApi.util.invalidateTags([
            { type: 'LeadList', id: listId },
            { type: 'LeadList', id: 'LIST' },
          ])
        );
        // A list created just before this import isn't in ``allLists`` yet.
        const listName =
          options?.listName ??
          allLists?.find((l) => l.id === listId)?.name ??
          'list';

        // Some chunks are still running server-side (async import exceeded the
        // client poll window). Let them finish in the background — the
        // notification bell reports completion when they land.
        if (pendingChunks > 0) {
          toast.info(
            `Importing to "${listName}" in the background — we'll notify you when it's done.`
          );
          return {
            imported_count: importedTotal,
            skipped_count: skippedTotal,
            lead_ids: importedLeadIds,
            already_in_list_ids: alreadyInListIds,
            pending: true,
            ...(outcomesMissing ? {} : { outcomes }),
          };
        }

        const summary = importSummary(importedTotal, skippedTotal, listName);
        if (failedChunks > 0) {
          // Partial success — some batches landed, some didn't. Tell the user so
          // they can re-run to add the rest (dedup makes a re-run safe).
          toast.warning(
            `${summary} — some batches failed; re-run to add the rest.`
          );
        } else if (!options?.resultOnScreen?.()) {
          toast.success(summary);
        }
        return {
          imported_count: importedTotal,
          skipped_count: skippedTotal,
          lead_ids: importedLeadIds,
          already_in_list_ids: alreadyInListIds,
          ...(outcomesMissing ? {} : { outcomes }),
        };
      } catch {
        toast.error('Import failed. Please try again.');
        return null;
      } finally {
        // Always consume the pending enrichment so a failed import
        // doesn't bleed into the next attempt.
        pendingImportEnrichmentRef.current = undefined;
        importInProgressRef.current = false;
        setImportProgress(null);
      }
    },
    [
      compositeCompanyFilters.startupPrograms,
      results,
      matchCriteria,
      triggerImport,
      triggerImportStatus,
      filters,
      hasSearched,
      dispatch,
      allLists,
      posthog,
    ]
  );

  const handleImportToList = useCallback(
    async (listId: string) => {
      if (importInProgressRef.current) return;

      const count =
        pendingImportPeopleRef.current.length > 0
          ? pendingImportPeopleRef.current.length
          : results.length;
      if (count > 500) {
        importInProgressRef.current = true;
        setPendingImportListId(listId);
        setImportDialogOpen(false);
        setConfirmImportOpen(true);
        return;
      }

      await executeImport(listId);
    },
    [results, executeImport]
  );

  const cancelPendingImport = useCallback(() => {
    importInProgressRef.current = false;
    setPendingImportListId(null);
    setConfirmImportOpen(false);
  }, []);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortOrder('desc');
      }
    },
    [sortColumn]
  );

  // ─── Inline Enrichment (CLE-247) ────────────────────────────────────
  // The per-row Enrich chip no longer asks the user to pick a list. It
  // fires a single manual enrichment (one credit) against the active
  // linked list if the search is scoped to one, otherwise the protected
  // "Extension Leads" list (guaranteed to exist per-org). The resolved
  // email/phone comes back via the enrichment websocket and is rendered
  // inline by matching on task_id.
  //
  // enrichingPersonIds: keyed by ``${personId}|${type}`` so email and
  //   phone reveals for the same row can run concurrently (shows
  //   spinner for the specific cell in flight).
  // enrichedPersonIds: same composite key, marks a completed reveal.
  // revealedByPersonId: map personId → {email, phone} for inline render.
  const enrichmentKey = useCallback(
    (personId: string, type: 'email' | 'phone') => `${personId}|${type}`,
    []
  );
  const [enrichingPersonIds, setEnrichingPersonIds] = useState<Set<string>>(
    new Set()
  );
  // Parallel ref-tracked mirror of ``enrichingPersonIds`` so the
  // double-click guard inside ``handleEnrichPerson`` can read the
  // latest in-flight set synchronously — React state updates are
  // batched and the closure'd ``enrichingPersonIds`` is one render
  // behind, which let two rapid clicks both pass the guard and pay
  // double credits (round-2 reliability audit S1).
  const enrichingPersonIdsRef = useRef<Set<string>>(new Set());
  // Single reveals this page load, for the one-time bulk hint below.
  const singleRevealCountRef = useRef(0);
  // Stops every reveal's polling loop when the hook unmounts. Made in the
  // effect, so a StrictMode remount gets a live one.
  const revealPollAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    revealPollAbortRef.current = controller;
    return () => controller.abort();
  }, []);
  const [enrichedPersonIds, setEnrichedPersonIds] = useState<Set<string>>(
    new Set()
  );
  const [revealedByPersonId, setRevealedByPersonId] = useState<
    Map<string, { email?: string; phone?: string }>
  >(new Map());
  // Shape mirrors the ``EnrichmentMatch`` type declared further down
  // alongside ``applyEnrichmentUpdate`` — kept in sync manually since
  // the ref is set up before the type can be referenced. If you add a
  // field there, add it here too.
  const taskToPersonRef = useRef<
    Map<
      string,
      {
        personId: string;
        type: 'email' | 'phone';
        personName: string;
        operationId: string;
        listName: string;
      }
    >
  >(new Map());

  const [enrichManually] = useEnrichProspectManuallyMutation();
  // HTTP polling fallback for when the WS terminal frame doesn't reach
  // this browser session (impersonated session whose user_id ≠ the
  // broadcast target, ad-blocker dropping the WS, server-sent-events
  // proxy timeout, etc.). Without this, a successful Temporal workflow
  // would still leave the row stuck in the loading state.
  const [fetchTaskStatus] = useLazyGetEnrichmentTaskStatusQuery();

  // Buffer for enrichment WS messages that arrive before the
  // /enrichment/manual POST has resolved and populated
  // taskToPersonRef. The backend fires a "queued" WS message before
  // the HTTP response returns, and a very fast cached provider hit
  // can beat the response for "completed" too. Drained in
  // handleEnrichPerson once the mapping is registered, keyed by
  // task_id.
  const pendingEnrichmentMessagesRef = useRef<
    Map<
      string,
      {
        status?: string;
        result?: { email?: string; phone?: string };
        message?: string;
      }
    >
  >(new Map());

  // Carries everything we need to (a) update the table cell on
  // completion and (b) close out the matching active-operation entry
  // that drives the progress footer + toasts.
  type EnrichmentMatch = {
    personId: string;
    type: 'email' | 'phone';
    personName: string;
    operationId: string;
    listName: string;
  };

  const applyEnrichmentUpdate = useCallback(
    (
      match: EnrichmentMatch,
      msg: {
        task_id?: string;
        status?: string;
        result?: {
          email?: string;
          phone?: string;
          failure_kind?: string | null;
        };
        message?: string;
        error?: string;
        failure_kind?: string | null;
      }
    ) => {
      const key = enrichmentKey(match.personId, match.type);
      const fieldLabel = match.type === 'email' ? 'email' : 'phone';
      // The Temporal worker for /enrichment/manual emits its terminal
      // event as ``session_completed`` (the wrap-up frame for a one-
      // shot session) more often than the per-task ``completed``
      // frame. Treat both as success — without ``session_completed``
      // here, our completion toast never fires even though the cell
      // does eventually update via a stray ``completed`` frame.
      // Mirrors copilot-tool-uis handleWsMessage line ~1529.
      const isCompletedStatus =
        msg.status === 'completed' || msg.status === 'session_completed';
      if (isCompletedStatus) {
        const value =
          match.type === 'email' ? msg.result?.email : msg.result?.phone;
        setRevealedByPersonId((prev) => {
          const next = new Map(prev);
          const existing = next.get(match.personId) ?? {};
          next.set(match.personId, {
            ...existing,
            [match.type]: value || '—',
          });
          return next;
        });
        // NOTE: we deliberately do NOT clone the underlying ``results``
        // array on reveal. The PeopleRow memo reads its enrichSignal
        // off ``revealedByPersonId`` (see people-search-results
        // ``enrichSignal=`...|${revealed?.email ?? row.original.email}|
        // ${revealed?.phone ?? row.original.phone}`` ``) which already
        // changes identity on every reveal — that alone re-renders the
        // single affected row. Cloning ``results`` here used to
        // re-render every row in the table (~100 rows × ~30 cells each
        // = a noticeable jank pulse on every reveal). The next /search
        // refetch will hydrate the canonical PersonRecord.email/phone
        // values from ``_hydrate_enriched_leads`` on the backend, so
        // we don't lose anything by skipping the in-memory mirror.

        // Bust the dedup cache + last-search snapshot in sessionStorage.
        // Both were captured *before* the enrichment, so a page reload
        // would render the stale row without the new email/phone, and
        // the user would think they lost the reveal. Clearing them
        // forces the next /search call to refetch from the backend,
        // where ``_hydrate_enriched_leads`` will backfill the values
        // for any row this user has ever enriched. Best-effort —
        // failure here doesn't break anything, the in-memory state
        // stays correct either way.
        // Targeted bust: only invalidate cached pages that actually
        // contain THIS person. Without ``{personId}`` the call drops
        // every cached search across every org, which forces a
        // Crustdata refetch the next time any unrelated saved search
        // is re-opened.
        clearAllCachedResults({ personId: match.personId });
        // The last-search snapshot keeps the search and takes the revealed
        // value into this row: clearing it wholesale lost the search on
        // the way back from a list. A miss is kept too, so the cell still
        // says "Not found" when the user comes back.
        patchSearchStateRow(
          'people',
          orgId,
          match.personId,
          value
            ? { [match.type]: value }
            : { [`${match.type}_not_found`]: true }
        );
        enrichingPersonIdsRef.current.delete(key);
        setEnrichingPersonIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setEnrichedPersonIds((prev) => new Set(prev).add(key));
        if (msg.task_id) taskToPersonRef.current.delete(msg.task_id);

        // Close out the corresponding active-operation entry so the
        // sticky progress footer drops this row. Toast surfaces the
        // result regardless of whether the provider actually found a
        // value — "—" means "we tried, nothing found" which the user
        // still needs to see.
        if (match.operationId) {
          dispatch(
            updateOperationProgress({
              id: match.operationId,
              progress: { processed: 1 },
            })
          );
          dispatch(
            setOperationStatus({
              id: match.operationId,
              status: 'completed',
            })
          );
          // Keep the op visible briefly with a checkmark, then drop
          // it from the strip. ~2s is long enough for the user to
          // register the success without lingering noise.
          const settledOpId = match.operationId;
          window.setTimeout(() => {
            dispatch(removeOperation(settledOpId));
          }, 2000);
        }
        // Plain toast.success — no id chaining. Mirrors the pattern
        // used everywhere else in this hook (e.g. line ~988 for the
        // bulk-import success toast).
        if (value && value !== '—') {
          toast.success(`Got ${match.personName}'s ${fieldLabel}`);
        } else {
          // The provider returned no value — still a "completed" run
          // that consumed credits, so call it out explicitly rather
          // than letting the cell silently say "Not found".
          toast.message(`No ${fieldLabel} found for ${match.personName}`);
        }
      } else if (msg.status === 'failed') {
        // Every provider came back empty: a final answer, not an error to
        // retry. Show "Not found" like a completed reveal with no value;
        // the cell used to fall back to "Reveal email" after a ~95s lookup,
        // as if it had never run. A miss costs nothing.
        const miss = msg.result?.failure_kind === 'miss';
        // Refused, unbilled: the person opted out. Final like a miss, so
        // the cell says so instead of offering the reveal again.
        const optedOut = isOptedOutFailure({
          failure_kind: msg.failure_kind ?? msg.result?.failure_kind,
          error: msg.error,
          message: msg.message,
        });
        if (miss || optedOut)
          setRevealedByPersonId((prev) => {
            const next = new Map(prev);
            next.set(match.personId, {
              ...(next.get(match.personId) ?? {}),
              [match.type]: optedOut ? OPTED_OUT_REVEAL : '—',
            });
            return next;
          });
        if (miss)
          patchSearchStateRow('people', orgId, match.personId, {
            [`${match.type}_not_found`]: true,
          });
        enrichingPersonIdsRef.current.delete(key);
        setEnrichingPersonIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        if (msg.task_id) taskToPersonRef.current.delete(msg.task_id);
        if (match.operationId) {
          dispatch(
            updateOperationProgress({
              id: match.operationId,
              progress: { failed: 1 },
            })
          );
          dispatch(
            setOperationStatus({
              id: match.operationId,
              status: 'failed',
            })
          );
          const settledOpId = match.operationId;
          window.setTimeout(() => {
            dispatch(removeOperation(settledOpId));
          }, 2000);
        }
        if (optedOut) toast.info(OPTED_OUT_MESSAGE);
        else if (miss)
          toast.info(
            `No ${fieldLabel} found for ${match.personName}. No credits used.`
          );
        else
          toast.error(
            msg.message || `Couldn't enrich ${match.personName}'s ${fieldLabel}`
          );
      }
    },
    [dispatch, enrichmentKey, orgId]
  );

  // Hydrate row state from enrichment WS updates. Each Reveal call
  // returns a task_id we track until the worker reports completion.
  // Unmatched messages are buffered so the HTTP response → mapping
  // registration can drain them even if the worker races us.
  // A reveal's found value arrives in ``enrichment_results_ready`` the moment
  // it is saved; the terminal frame lands ~45s later, after billing and the
  // CRM export. Settle on the early one when it carries the result (older
  // workers send it without), then drop that task's terminal frame instead of
  // buffering it for a registration that never comes.
  const earlySettledTasksRef = useRef<Set<string>>(new Set());
  // Optional: search tests render without a provider; the app always has one.
  const subscribeWs = useOptionalWebSocketContext()?.subscribe;
  useEffect(
    () =>
      subscribeWs?.('enrichment', (raw) => {
        const msg = raw as {
          type?: string;
          task_id?: string;
          result?: { email?: string; phone?: string };
        };
        if (msg.type !== 'enrichment_results_ready') return;
        if (!msg.task_id || !msg.result) return;
        const match = taskToPersonRef.current.get(msg.task_id);
        if (!match) return;
        earlySettledTasksRef.current.add(msg.task_id);
        applyEnrichmentUpdate(match, {
          task_id: msg.task_id,
          status: 'completed',
          result: msg.result,
        });
      }),
    [subscribeWs, applyEnrichmentUpdate]
  );

  useEnrichmentWebSocket((msg) => {
    if (!isEnrichmentMessage(msg) || !msg.task_id) return;
    const match = taskToPersonRef.current.get(msg.task_id);
    if (!match) {
      if (earlySettledTasksRef.current.delete(msg.task_id)) return;
      // Only buffer terminal statuses — intermediate "queued" /
      // "processing" messages don't change the UI so they can drop.
      // ``session_completed`` is a terminal frame too (Temporal worker
      // emits it as the wrap-up of a one-shot manual enrichment) so
      // it has to be buffered alongside ``completed`` / ``failed``,
      // otherwise a fast worker that beats the HTTP response loses
      // its terminal message entirely.
      if (
        msg.status === 'completed' ||
        msg.status === 'session_completed' ||
        msg.status === 'failed'
      ) {
        pendingEnrichmentMessagesRef.current.set(msg.task_id, msg);
      }
      return;
    }
    applyEnrichmentUpdate(match, msg);
  });

  // Polling hit its 30 min ceiling with no verdict: show "Not found" so the
  // row stops spinning. The task mapping stays, so a late WS result still
  // overwrites it with the real value.
  const giveUpReveal = useCallback(
    (match: EnrichmentMatch) => {
      const key = enrichmentKey(match.personId, match.type);
      enrichingPersonIdsRef.current.delete(key);
      setEnrichingPersonIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setRevealedByPersonId((prev) => {
        const next = new Map(prev);
        next.set(match.personId, {
          ...(next.get(match.personId) ?? {}),
          [match.type]: '—',
        });
        return next;
      });
      // The row carries the state now; the footer strip would sit on
      // "running" with nothing left to report.
      if (match.operationId) dispatch(removeOperation(match.operationId));
      const fieldLabel = match.type === 'email' ? 'email' : 'phone';
      toast.message(
        `${match.personName}'s ${fieldLabel} lookup didn't finish. Check ${match.listName || 'your list'} later.`
      );
    },
    [dispatch, enrichmentKey]
  );

  const handleEnrichPerson = useCallback(
    async (person: PersonRecord, type: 'email' | 'phone') => {
      const personId = person.id || '';
      if (!personId) return;
      const key = enrichmentKey(personId, type);
      const alreadyRevealed = revealedByPersonId.get(personId)?.[type];
      // Composite key so an in-flight email reveal doesn't block the
      // phone reveal (or vice versa) on the same row.
      //
      // Synchronously read+set the ref-backed mirror BEFORE the await.
      // ``enrichingPersonIds.has`` reads React state via closure and is
      // one render behind — two rapid clicks both pass that check and
      // fire two enrich POSTs (double credit charge). The ref guard
      // closes the gap because Set.add is synchronous.
      if (alreadyRevealed || enrichingPersonIdsRef.current.has(key)) return;

      // Crustdata v1 stores the personal LinkedIn URL under
      // linkedin_profile_url on the raw payload; raw.linkedin_url is
      // often the company page. Prefer the profile URL over raw.linkedin_url
      // so the provider actually resolves the person. The normalized
      // top-level person.linkedin_url wins when present (it's already
      // normalized to the personal URL by the backend).
      const linkedin =
        person.linkedin_url ||
        (person.raw?.linkedin_profile_url as string | undefined) ||
        (person.raw?.linkedin_url as string | undefined);
      const companyDomain =
        person.company_domain ||
        person.company_name ||
        (person.raw?.company_domain as string | undefined);
      if (!linkedin && !companyDomain) {
        toast.error('Need a LinkedIn URL or company to resolve this contact.');
        return;
      }

      // Guard and spinner go up together, BEFORE the list lookups below.
      // Those can hit the network (a cold lists cache, or the
      // get-or-create POST for orgs without a protected list), and with
      // the spinner set after them the row showed nothing while the guard
      // silently swallowed a second click.
      enrichingPersonIdsRef.current.add(key);
      setEnrichingPersonIds((prev) => new Set(prev).add(key));
      const release = () => {
        enrichingPersonIdsRef.current.delete(key);
        setEnrichingPersonIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      };

      // Destination resolution — linked context list wins; otherwise
      // fall through to the user's protected "Extension Leads" list
      // (created at onboarding, flagged with `is_protected` on the DB).
      // Backend requires lead_list_id, so we must resolve it client-side.
      //
      // Cold-cache path: useGetLeadListsQuery() is already running at
      // hook scope, but on first paint `allLists` can still be
      // undefined when the user clicks. Await the query imperatively
      // instead of erroring — `initiate` returns cached data when
      // available, otherwise kicks the fetch and resolves with the
      // response. This eliminates the "Lists still loading" regression
      // without sacrificing the destination lookup.
      let resolvedLists = allLists;
      if (!resolvedLists) {
        try {
          resolvedLists = await dispatch(
            leadListsApi.endpoints.getLeadLists.initiate()
          ).unwrap();
        } catch {
          release();
          toast.error('Could not load your lead lists. Try again.');
          return;
        }
      }

      let targetList =
        (linkedListId
          ? resolvedLists.find((l) => l.id === linkedListId)
          : undefined) || resolvedLists.find((l) => l.is_protected);

      // Fail open: orgs onboarded before the protected-list webhook have no
      // is_protected list, which used to dead-end the reveal here. Ask the
      // backend to get-or-create the canonical "Extension Leads" list
      // (idempotent, cap-exempt) and continue.
      if (!targetList) {
        try {
          targetList = await dispatch(
            leadListsApi.endpoints.getOrCreateDefaultLeadList.initiate()
          ).unwrap();
        } catch {
          // fall through to the terminal toast below
        }
      }

      const targetListId = targetList?.id;
      const targetListName = targetList?.name || '';

      if (!targetListId) {
        release();
        toast.error(
          'No destination list found. Contact support if this persists.'
        );
        return;
      }

      // Display labels for the toast + the progress-footer label so
      // the user can tell which row each running operation belongs
      // to. Falls through name → first/last → "contact" so the label
      // is always human-readable even with sparse Crustdata records.
      const personName =
        person.full_name ||
        [person.first_name, person.last_name].filter(Boolean).join(' ') ||
        'contact';
      const fieldLabel = type === 'email' ? 'email' : 'phone';

      // Stable per-attempt id so the WS handler can find the right
      // operation to close out when ``completed`` / ``failed`` arrives.
      const operationId = `enrichment-${personId}-${type}-${Date.now()}`;

      // Track the enrichment as an active operation so the sticky
      // progress footer renders a "1 of 1 done" strip while the worker
      // runs. Status flips to ``completed`` / ``failed`` from the WS
      // handler in applyEnrichmentUpdate, which removes it from the
      // footer.
      dispatch(
        addOperation({
          id: operationId,
          type: 'enrichment',
          label: `Reveal ${fieldLabel} • ${personName}`,
          status: 'running',
          progress: { total: 1, processed: 0, failed: 0 },
          startedAt: new Date().toISOString(),
          leadListId: targetListId,
        })
      );

      try {
        // /enrichment/manual accepts a company name in the
        // company_domain field, so the visible company string falls
        // through here when no domain is available.
        const result = await enrichManually({
          enrichment_type: type === 'email' ? 'partial' : 'phone_only',
          linkedin_url: linkedin || undefined,
          first_name: person.first_name || undefined,
          last_name: person.last_name || undefined,
          company_domain: companyDomain || undefined,
          lead_list_id: targetListId,
        }).unwrap();

        // Start toast only once the server took the reveal: a 402 (no
        // credits) or other refusal adds nothing to the list, so an
        // earlier "Lead added" contradicted the credit gate. The
        // completion toast fires separately from the WS terminal frame.
        toast.success(`Lead added to your list — enriching ${fieldLabel}…`);
        // Replay: users clicking Reveal row by row through dozens of pages,
        // when one Add to list with enrichment does the whole selection. Say
        // so once, on the third single reveal, and never again in this browser.
        singleRevealCountRef.current += 1;
        if (singleRevealCountRef.current === BULK_REVEAL_HINT_AFTER) {
          showBulkRevealHintOnce();
        }

        if (result?.task_id) {
          const taskId = result.task_id;
          const match = {
            personId,
            type,
            personName,
            operationId,
            listName: targetListName,
          } as const;
          taskToPersonRef.current.set(taskId, match);
          // Drain any WS messages that arrived before the mapping was
          // registered — without this, a fast-cached completion or the
          // initial status update that precedes the HTTP response is
          // silently dropped.
          const buffered = pendingEnrichmentMessagesRef.current.get(taskId);
          if (buffered) {
            pendingEnrichmentMessagesRef.current.delete(taskId);
            applyEnrichmentUpdate(match, { ...buffered, task_id: taskId });
          }

          // HTTP polling fallback. The WS handler will usually beat us
          // here, in which case ``taskToPersonRef`` no longer has the
          // entry and the poll exits as a no-op. We keep polling for
          // up to 30 min so even sessions where the WS broadcast targets
          // a different user_id (impersonated logins) still resolve.
          const signal = revealPollAbortRef.current?.signal;
          void pollTaskStatusUntilDone({
            taskId,
            match,
            fetchTaskStatus,
            applyEnrichmentUpdate,
            taskToPersonRef,
            onTimeout: () => giveUpReveal(match),
            signal,
          }).then(() => {
            // Left the page mid-reveal: nothing here settles the footer
            // strip now, and the list page shows it too (same leadListId).
            if (signal?.aborted && taskToPersonRef.current.has(taskId))
              dispatch(removeOperation(operationId));
          });
        } else {
          // No task to follow, so no WS frame or poll would ever clear
          // the spinner (it spun forever). The reveal may still have run,
          // so point at the list rather than invite a second paid reveal.
          release();
          dispatch(removeOperation(operationId));
          toast.error(
            `Couldn't track ${personName}'s ${fieldLabel} reveal. Check ${targetListName || 'your list'} in a minute before retrying.`
          );
        }
      } catch (err) {
        release();
        // Mark the operation as failed so the progress footer drops
        // it. Without this the sticky strip would hang on "running"
        // forever after an HTTP error.
        dispatch(
          updateOperationProgress({
            id: operationId,
            progress: { failed: 1 },
          })
        );
        dispatch(setOperationStatus({ id: operationId, status: 'failed' }));
        // Hold the failure state in the footer briefly with the red
        // x flourish, then drop it.
        const settledOpId = operationId;
        window.setTimeout(() => {
          dispatch(removeOperation(settledOpId));
        }, 2000);
        if (toastIfOptedOut(err)) {
          setRevealedByPersonId((prev) => {
            const next = new Map(prev);
            next.set(personId, {
              ...(next.get(personId) ?? {}),
              [type]: OPTED_OUT_REVEAL,
            });
            return next;
          });
          return;
        }
        const status =
          typeof err === 'object' && err && 'status' in err
            ? (err as { status?: unknown }).status
            : undefined;
        const detail =
          typeof err === 'object' && err && 'data' in err
            ? (err as { data?: { detail?: string } }).data?.detail
            : undefined;
        if (status === 402) {
          openCreditGate({
            needed: type === 'phone' ? 10 : 1,
            action: `enrich-${type}`,
          });
        } else {
          toast.error(
            detail || `Couldn't enrich ${personName}'s ${fieldLabel}`
          );
        }
      }
    },
    [
      applyEnrichmentUpdate,
      dispatch,
      enrichManually,
      openCreditGate,
      // ``enrichingPersonIds`` removed from the dep array — the double-
      // click guard now reads from ``enrichingPersonIdsRef.current``
      // (see line ~1812). Listing the state Set here forces an unstable
      // identity on every reveal toggle, which busted memoization of
      // every row cell that received this handler. The ref is stable
      // and gives us the synchronous read we actually need.
      enrichmentKey,
      fetchTaskStatus,
      revealedByPersonId,
      linkedListId,
      allLists,
      giveUpReveal,
    ]
  );

  // Side-channel fetch for the Add-to-list drawer. The table only
  // needs ~100 people for preview/scanning, but the import drawer
  // offers up to 5,000. When the user picks a bigger count, the
  // drawer calls this to pull the expanded set without disturbing
  // the displayed results. Does NOT touch state — returns directly.
  const fetchPeople = useCallback(
    async (limit: number): Promise<PersonRecord[] | null> => {
      if (mcpHandoffBlocked || mcpBlockedRef.current) return null;
      if (mcpContext && filters.limitPerCompany.trim()) return null;
      if (!hasAnyFilter) return null;
      // "Add all N" adds the search on screen. While the filters differ from
      // it (the results are dimmed), refuse rather than fetch and import up
      // to 5,000 people nobody has seen (review 3 S2-4).
      if (
        searched &&
        (JSON.stringify(searched.filters) !== JSON.stringify(filters) ||
          JSON.stringify(searched.composite) !==
            JSON.stringify(compositeCompanyFilters))
      ) {
        toast.error('Click Show to run these filters, then add them.');
        return null;
      }
      const currentGen = searchGenerationRef.current;
      // Do NOT clear resolvedExclusionFiltersRef here: this side-channel drawer
      // fetch must not invalidate the displayed search's resolved state while it
      // is in flight (that would make executeImport reject a validly-searched
      // set). The success paths below set it; the failure path clears it.
      try {
        validatePeopleExclusions(filters);
        const startupPrograms = startupProgramRequest(
          compositeCompanyFilters.startupPrograms
        );
        const importSession = searchSessionIdRef.current;
        // BOTH compilers run before any resolver, so malformed person OR
        // company state costs zero CRM/Cleanlist round-trips. The route is
        // reused at the splice/dispatch below.
        const apiFilters = adaptMcpPeopleFilter(
          buildCrustdataFilters(filters),
          filters,
          mcpContext
        );
        const route = mcpCompositeRoute(
          filters,
          compositeCompanyFilters,
          mcpContext
        );
        assertMcpPreviewLimit(mcpContext, filters, route.useComposite);
        if (mcpContext && route.useComposite && sortColumn) {
          throw new Error(
            'MCP composite searches cannot preserve provider sort.'
          );
        }
        const resolvedExclusions = await resolveSelectedExclusions(
          false,
          currentGen
        );
        if (
          mcpHandoffBlocked ||
          mcpBlockedRef.current ||
          resolvedExclusions === null ||
          currentGen !== searchGenerationRef.current ||
          currentOrganizationRef.current !== orgId
        )
          return null;
        const sortsParam = sortColumn
          ? [{ column: sortColumn, order: sortOrder }]
          : undefined;

        // ``route`` is compiled in the pre-flight above: a firmographic
        // (composite) search can't be served by plain PersonDB, so the import
        // drawer fetches through the by-companies import-fetch endpoint with
        // the same company_filter + person_filter payload handleSearch builds.
        // Needed BEFORE the exclude splice so an exclude-only composite import
        // still carries its excludes.
        //
        // Same asymmetry as handleSearch (centralised in the helper): extend
        // an existing AND group on either endpoint, but synthesize a fresh
        // exclude-only group only for the composite path. Without it an
        // Add-to-list pull on a firmographic-only search would import up to
        // 5,000 people INCLUDING the excluded CRM / Cleanlist set.
        applyExcludeConditions(
          apiFilters,
          resolvedExclusions.conditions,
          route.useComposite
        );
        const parsedLimitPerCompany = Number.parseInt(
          filters.limitPerCompany,
          10
        );
        const limitPerCompany =
          Number.isFinite(parsedLimitPerCompany) && parsedLimitPerCompany > 0
            ? parsedLimitPerCompany
            : undefined;
        if (route.useComposite) {
          if (startupPrograms) {
            const rows = await collectProgramPages<PersonRecord>(
              limit,
              async (cursor, remaining) => {
                if (
                  importSession !== searchSessionIdRef.current ||
                  currentGen !== searchGenerationRef.current ||
                  currentOrganizationRef.current !== orgId
                )
                  throw new ProgramSearchError(
                    'Search changed during import preparation. Run it again.'
                  );
                const raw = await triggerCompositeImportFetch({
                  startup_programs: startupPrograms,
                  company_filter: route.companyFilter ?? undefined,
                  person_filter: apiFilters as Record<string, unknown>,
                  limit: Math.min(1000, remaining),
                  cursor,
                  sorts: sortsParam,
                  ...(limitPerCompany
                    ? { limit_per_company: limitPerCompany }
                    : {}),
                  identity_exclusions: resolvedExclusions.identityExclusions,
                }).unwrap();
                requireIdentityExclusionsApplied(
                  raw,
                  resolvedExclusions.identityExclusions
                );
                if (!Array.isArray(raw.results)) {
                  throw new Error('Import fetch returned malformed results');
                }
                if (
                  importSession !== searchSessionIdRef.current ||
                  currentGen !== searchGenerationRef.current ||
                  currentOrganizationRef.current !== orgId
                )
                  throw new ProgramSearchError(
                    'Search changed during import preparation. Run it again.'
                  );
                return {
                  rows: raw.results as unknown as PersonRecord[],
                  next_cursor: raw.next_cursor,
                };
              },
              (rows) =>
                capResultsPerCompany(
                  rows,
                  filters.limitPerCompany,
                  matchCriteria
                )
            );
            resolvedExclusionFiltersRef.current = filters;
            return capResultsPerCompany(
              rows,
              filters.limitPerCompany,
              matchCriteria
            );
          }
          const page = await triggerCompositeImportFetch({
            company_filter: route.companyFilter ?? undefined,
            person_filter: apiFilters as Record<string, unknown>,
            limit,
            ...(limitPerCompany ? { limit_per_company: limitPerCompany } : {}),
            sorts: sortsParam,
            identity_exclusions: resolvedExclusions.identityExclusions,
          }).unwrap();
          requireIdentityExclusionsApplied(
            page,
            resolvedExclusions.identityExclusions
          );
          if (
            mcpHandoffBlocked ||
            mcpBlockedRef.current ||
            currentGen !== searchGenerationRef.current ||
            currentOrganizationRef.current !== orgId
          )
            return null;
          if (!Array.isArray(page.results)) {
            throw new Error('Import fetch returned malformed results');
          }
          // Composite returns ``results`` as the same normalized wire
          // shape as plain search — cast to PersonRecord like handleSearch.
          // Apply the SAME per-company cap the on-screen cappedStash uses
          // (limitPerCompany) — otherwise the Add-to-list pull imports the
          // un-capped set and over-charges import + enrichment credits for
          // rows the user explicitly capped out.
          const results = capResultsPerCompany(
            page.results as unknown as PersonRecord[],
            filters.limitPerCompany,
            matchCriteria
          ).slice(0, limit);
          resolvedExclusionFiltersRef.current = filters;
          return results;
        }

        const page = await triggerImportFetch({
          filters: apiFilters as unknown as CrustdataFilterGroup,
          limit,
          ...(limitPerCompany ? { limit_per_company: limitPerCompany } : {}),
          ...(sortsParam ? { sorts: sortsParam } : {}),
          identity_exclusions: resolvedExclusions.identityExclusions,
        }).unwrap();
        requireIdentityExclusionsApplied(
          page,
          resolvedExclusions.identityExclusions
        );
        if (
          mcpHandoffBlocked ||
          mcpBlockedRef.current ||
          currentGen !== searchGenerationRef.current ||
          currentOrganizationRef.current !== orgId
        )
          return null;
        if (!Array.isArray(page.results)) {
          throw new Error('Import fetch returned malformed results');
        }
        // Cap per company to match the on-screen results (see composite
        // branch above) so imports never exceed the user's per-company limit.
        const results = capResultsPerCompany(
          page.results,
          filters.limitPerCompany,
          matchCriteria
        ).slice(0, limit);
        resolvedExclusionFiltersRef.current = filters;
        return results;
      } catch (err) {
        if (
          mcpHandoffBlocked ||
          mcpBlockedRef.current ||
          currentGen !== searchGenerationRef.current ||
          currentOrganizationRef.current !== orgId
        )
          return null;
        resolvedExclusionFiltersRef.current = null;
        setCrmExclusionStatus((prev) => ({ ...prev, isFetching: false }));
        setCrmSectionStatus((prev) =>
          Object.fromEntries(
            Object.entries(prev).map(([crm, status]) => [
              crm,
              { ...status, isFetching: false },
            ])
          )
        );
        // A compile failure names the control to fix; nothing was requested.
        toast.error(
          identityExclusionFailureMessage(err) ??
            startupProgramErrorMessage(err, 'Failed to fetch additional leads.')
        );
        return null;
      }
    },
    [
      matchCriteria,
      triggerImportFetch,
      triggerCompositeImportFetch,
      filters,
      searched,
      compositeCompanyFilters,
      hasAnyFilter,
      resolveSelectedExclusions,
      orgId,
      sortColumn,
      sortOrder,
      mcpContext,
      mcpHandoffBlocked,
    ]
  );

  // Re-run the current search forcing a fresh CRM exclusion-set fetch.
  // Wired to the sidebar "refresh" affordance; no-op when no CRM
  // exclusion is enabled.
  const refreshCrmExclusions = useCallback(() => {
    if (!hasAnyCrmExclusionEnabled) return;
    forceRefreshCrmRef.current = true;
    handleSearch();
  }, [hasAnyCrmExclusionEnabled, handleSearch]);

  /** Chip-strip "X" removal. Generic over every SearchFilters key —
   *  arrays drop ``value``, scalars reset to their INITIAL_FILTERS
   *  default. Replaces a hand-maintained switch that was missing
   *  ~14 of the expanded-filters fields (geoRadius, headlineContains,
   *  summaryContains, twitterHandle, both boolean queries, three date
   *  fields, followersMin/Max, joinedCurrentAfter, pastLeftCompanyAfter,
   *  pastCompaniesRequireAll, companyFilterCompositeMode) and silently
   *  no-op'd those chips — round-2 reliability audit S2. */
  const removeFilter = useCallback((key: string, value: string) => {
    setFilters((prev) => {
      const copy = { ...prev };
      const k = key as keyof SearchFilters;
      const initial = INITIAL_FILTERS[k];
      const current = (copy as Record<string, unknown>)[k];
      if (Array.isArray(current)) {
        // Multi-value field — strip just the chip the user clicked.
        (copy as Record<string, unknown>)[k] = current.filter(
          (v) => v !== value
        );
      } else if (initial === undefined && current !== undefined) {
        // Defensive: unknown key with a value — leave it alone rather
        // than nuking unrelated state.
      } else {
        // Scalar (string, boolean, number, nullable object) — restore
        // INITIAL_FILTERS' default so the chip disappears and the
        // search round-trip stops referencing the value.
        (copy as Record<string, unknown>)[k] = initial as unknown;
      }
      return copy;
    });
  }, []);

  return {
    filters,
    setFilters,
    // Composite firmographic slice (revenue, funding, growth %, IPO,
    // etc.). When non-empty, handleSearch routes through
    // /people-search/by-companies. Exposed so the People sidebar can
    // render CompositeCompanyFilterSection.
    compositeCompanyFilters,
    setCompositeCompanyFilters,
    results,
    // Full accumulated stash across every fetched block — the results
    // table resolves its cross-page row selection against this (you can
    // only select rows you've actually fetched).
    // With a cap applied, everything downstream -- selection, "Select N" and
    // "Export all" -- must see the CAPPED set. Selection resolves rows by
    // index, so handing out the raw stash made row i in the table and row i
    // here different people, and "Export all" included rows the cap removed.
    stash: appliedLimitPerCompany ? cappedStash : stash,
    // The header count: a plain PersonDB or general composite total_count is
    // the provider's UNCAPPED count, so with a cap it overstates what was
    // found -- report what was actually loaded instead. Startup programs are
    // the exception: that search is
    // company-scoped and capped server-side, so its total_count already
    // reflects the cap and is trusted as-is (null there still means unknown).
    total:
      appliedLimitPerCompany && !programCoverage ? cappedStash.length : total,
    totalIsUpperBound,
    totalKnown: appliedLimitPerCompany && !programCoverage ? false : totalKnown,
    programCoverage,
    programWarning,
    companyCoverage,
    instantlyUnchecked,
    closestMatches,
    hasSearched,
    isSearching,
    searchingEachCompany: isSearching && searchingEachCompany,
    // Bumped on every true new search — the results table clears its
    // cross-page selection when this changes.
    searchSessionId,
    // ── Client-side paging ──
    /** 1-based page the table is showing. */
    currentPage,
    /** True while a next-block fetch is in flight (drives the Next spinner). */
    isPaging,
    /** Whether a Next is possible — either more rows are buffered or a
     *  cursor exists to fetch the next block. */
    hasNextPage: currentPage * PAGE_SIZE < cappedStash.length || !!nextCursor,
    // Only while the next page needs a fetch: buffered pages stay reachable,
    // and an MCP page fill that still needs rows counts as a fetch.
    pagingLockedUntil: nextPageNeedsFetch ? pagingLockedUntil : null,
    goNextPage,
    goPrevPage,
    goToPage,
    bufferedPageCount,
    isError: errorState.isError,
    errorMessage: errorState.message,
    /** Drives SearchErrorState's variant — 'feature_gated' renders
     *  the upgrade card, anything else renders the generic retry. */
    errorKind: errorState.kind,
    retrySearch,
    isImporting,
    importProgress,
    hasAnyFilter,
    activeFilters,
    matchCriteria: appliedMatchCriteria,
    searched,
    linkedList: linkedList
      ? { id: linkedList.id, name: linkedList.name }
      : undefined,
    handleSearch,
    restoreMcpSearch,
    clearMcpContext,
    scheduleSearch,
    exclusionsUnresolved:
      hasSearched &&
      hasPeopleExclusions(filters) &&
      resolvedExclusionFiltersRef.current !== filters,
    setPendingImportPeople,
    setPendingImportEnrichment,
    handleImportToList,
    sortColumn,
    sortOrder,
    handleSort,
    clearSort: () => setSortColumn(undefined),
    handleEnrichPerson,
    enrichingPersonIds,
    enrichedPersonIds,
    revealedByPersonId,
    fetchPeople,
    clearAll,
    removeFilter,
    // CRM-exclusion surface (FE-E).
    crmExclusionStatus,
    crmSectionStatus,
    // Cleanlist-level dedup surface (CP-8).
    cleanlistExclusionStatus,
    hasConnectedCrm,
    refreshCrmExclusions,
    // Preview-quota state — surfaced so the search container can
    // render the onboarding card + sidebar badge alongside the
    // search button. ``isLimited === false`` for paid/Pro tiers, in
    // which case both UI surfaces render nothing.
    searchQuota: {
      isLimited: searchQuota.isLimited,
      remaining: searchQuota.remaining,
      max: searchQuota.max,
      isExhausted: searchQuota.isExhausted,
      isAppSumo: searchQuota.isAppSumo,
    },
    importDialogOpen,
    setImportDialogOpen,
    confirmImportOpen,
    setConfirmImportOpen,
    pendingImportListId,
    executeImport,
    cancelPendingImport,
  };
}
